import React, { useEffect, useState, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { computeVisualBounds as sharedComputeVisualBounds } from '../utils/imageUtils';
import { canvasLoadStart as _canvasLoadStart, canvasLoadDone as _canvasLoadDone } from '../utils/canvasLoadingCounter';
import styled, { keyframes } from 'styled-components';
import { Stage, Layer, Image, Text, Transformer, Group, Rect, Line, Circle } from 'react-konva';
import Konva from 'konva';
import { useDesignContext } from '../hooks/useDesignContext';
import { getGraniteForColor } from '../utils/colorMappings';
import AlignmentToolbar from './AlignmentToolbar';
import { useCanvasScale } from '../hooks/useCanvasScale';
import ImageAdjuster from './ImageAdjuster';
import { buildApiUrl, authHeaders } from '../config/api';
import VaseElement from './VaseElement';
import { DesignElement } from '../types/index';
import { PremiumDatePicker } from './LeftPanel/Text';
import { adjustLastNameCasing } from '../utils/nameCasing';

function rectsOverlap(a: { x: number, y: number, width: number, height: number }, b: { x: number, y: number, width: number, height: number }) {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}
function overlapsAny(rect: { x: number, y: number, width: number, height: number, id?: string, properties?: any }, elements: DesignElement[]) {
  const color = rect.properties?.color || rect.properties?.fill || rect.properties?.graniteColor || (rect.properties ? rect.properties.colorId : undefined);
  const filtered = elements.filter(el => {
    if (el.id === rect.id) return false;
    const props = el.properties || {};
    const role = String(props.role || props.kind || '').toLowerCase();
    if (role === 'template' || props.isTemplate === true) return false;
    if (role === 'vase' || role === 'work') return false;
    if (el.type === 'artwork' && role !== 'template') return false;
    const elColor = el.properties?.color || el.properties?.fill || el.properties?.graniteColor || (el.properties ? el.properties.colorId : undefined);
    return color === elColor;
  });
  const result = filtered.some(el => rectsOverlap(rect, el));
  return result;
}

const isVase = (el: any) => {
  try {
    return el && el.type === 'artwork' && String(el.properties?.role || el.properties?.kind || '').toLowerCase() === 'vase';
  } catch {
    return false;
  }
};

const mapEsqDbFontToLoadedFamily = (rawFont: unknown, fieldName?: string, isPjb: boolean = false): string => {
  const fnLc = String(fieldName || '').toLowerCase();
  if (fnLc === 'verse' || fnLc.startsWith('verse')) {
    return isPjb ? 'MonotypeCorsiva' : 'Vermarco';
  }
  const value = String(rawFont || '').trim().toLowerCase();
  if (!value || value === 'custom') return 'MLCModifiedRomanSKS';
  if (value.includes('monument mod. roman')) return 'MLCModifiedRomanSKS';
  if (value.includes('pamega')) return isPjb ? 'PamegaScript' : 'Vermarco';
  if (value.includes('vermarco')) return 'Vermarco';
  if (value.includes('monotype') || value.includes('corsiva')) return isPjb ? 'MonotypeCorsiva' : 'Vermarco';
  return String(rawFont || 'MLCModifiedRomanSKS');
};

const clampTextByCharacterLimit = (raw: string, limit?: number | null): string => {
  return String(raw ?? '');
};

const clampNumber = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const MONTHS = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
];

function computeEsqBoundsPixelFrame(opts: {
  bounds: { v1: number; v2: number; h1: number; h2: number };
  compositeProps: any;
  graniteTemplate: any;
  graniteConfig: any;
  pixelsPerInch: number;
}) {
  const { bounds, compositeProps, graniteTemplate, graniteConfig, pixelsPerInch } = opts;
  const cp = compositeProps as any;
  const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0, width: 0, height: 0 });
  const ox = Number(ref.x || 0);
  const oy = Number(ref.y || 0);
  const w = Number(ref.width || 0);
  const h = Number(ref.height || 0);
  const gcSize = (graniteConfig as any)?.granite_details?.size as number[] | undefined;
  const physW = (Array.isArray(gcSize) && gcSize.length >= 1 && Number(gcSize[0]) > 0) ? Number(gcSize[0]) : 0;
  const physH = (Array.isArray(gcSize) && gcSize.length >= 2 && Number(gcSize[1]) > 0) ? Number(gcSize[1]) : 0;
  const fallback = Number(pixelsPerInch) || 96;
  const ppiX = (physW > 0 && w > 0) ? w / physW : fallback;
  const ppiY = (physH > 0 && h > 0) ? h / physH : fallback;
  const left = ox + Number(bounds.v1) * ppiX;
  const right = ox + Number(bounds.v2) * ppiX;
  const top = oy + Number(bounds.h1) * ppiY;
  const bottom = oy + Number(bounds.h2) * ppiY;
  const insetBase = Math.max(ppiX, ppiY);
  const insetX = Math.max(2, Math.round(insetBase * 0.04));
  const insetTop = Math.max(3, Math.round(insetBase * 0.08));
  const insetBottom = Math.max(2, Math.round(insetBase * 0.04));
  return { left, right, top, bottom, ppiX, ppiY, insetX, insetTop, insetBottom };
}
interface DesignerProps { }

const Root = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden; /* Hide overflow on desktop */
  @media (max-width: 768px) {
    overflow: hidden; /* Ensure clipped on mobile too */
  }
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;


const useImage = (src: string, priority: 'high' | 'low' = 'low') => {
  const [image, setImage] = React.useState<HTMLImageElement | null>(null);

  React.useEffect(() => {
    if (!src) { setImage(null); return; }
    setImage(null);
    let loadCompleted = false;
    const markDone = () => {
      if (!loadCompleted) {
        loadCompleted = true;
        _canvasLoadDone();
      }
    };
    _canvasLoadStart();

    const img = new window.Image();
    img.crossOrigin = 'anonymous';

    if (priority === 'high' && 'fetchPriority' in img) {
      (img as any).fetchPriority = 'high';
    }

    img.onload = () => {
      setImage(img);
      markDone();
    };
    img.onerror = () => {
      setImage(null);
      markDone();
    };
    img.src = src;
    return () => {
      try { img.onload = null as any; img.onerror = null as any; } catch { }
      markDone(); // balance counter on cleanup/cancel
    };
  }, [src, priority]);
  return image;
};

const DESIGN_WIDTH: number = 1400;
const DESIGN_HEIGHT: number = 700;

let lastTextGroupClick = { id: '', time: 0 };
const moduleImageCache = new Map<string, HTMLImageElement>();
const moduleImageFetching = new Map<string, Promise<HTMLImageElement | null>>();

const Designer: React.FC<DesignerProps> = () => {
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if ((window as any).__internalReload) return;
      if ((window as any).__mailtoOpening) return;
      e.preventDefault();
      e.returnValue = 'If you continue, all design components will be removed from the design.';
      return 'If you continue, all design components will be removed from the design.';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  const {
    currentDesign,
    selectedGraniteImage,
    setSelectedGraniteImage,
    updateDesign,
    isTemplateSelected,
    setIsTemplateSelected,
    setStageRef,
    selectedBackground,
    templateResetKey,
    selectedGraniteMeta,
    setSelectedGraniteMeta,
    layoutGuides,
    setLayoutGuides,
    selectedGraniteBaseImage,
    setSelectedGraniteBaseImage,
    isFlatMarkerCurrent,
    isHistoryNavigating,
    setSelectedBackground,
    setCurrentTool,
    isApplyingDesign,
    setIsApplyingDesign,
    setSelectedElement,
    selectedElement,

    placementSlots,
    setPlacementSlots,
    activePlacementSlot,
    setActivePlacementSlot,
    pixelsPerInch: ctxPixelsPerInch,
    setPixelsPerInch: setCtxPixelsPerInch,
    renderedTemplateOrigin,
    setRenderedTemplateOrigin,
    currentTool,
    sharedColors,
    sharedFonts,
    openModal,
    setOpenModal,
    showPersonalTextOnCanvas,
    authedUser,
    isGlobalLoading: isGlobalLoadingFromCtx,
    loadingMessage,
    areTextAssetsLoaded,
    fetchModelDetails,
    fetchDesignPreview,
    sampleGuideLines,
    setSampleGuideLines,
    designMode
  } = useDesignContext();


  const isAdminEmail = useMemo(() => {
    return authedUser?.is_editor === 'yes';
  }, [authedUser]);

  const visibleBoundsCache = useRef<Map<string, { left: number, top: number, right: number, bottom: number }>>(new Map());

  const computeVisualBounds = useCallback(async (img: HTMLImageElement, src: string) => {
    if (visibleBoundsCache.current.has(src)) return visibleBoundsCache.current.get(src)!;

    const res = await sharedComputeVisualBounds(img);

    visibleBoundsCache.current.set(src, res);
    return res;
  }, []);


  const granitePadding = (typeof isFlatMarkerCurrent === 'function' && isFlatMarkerCurrent()) ? 100 : 40;
  const lastProcessedParamsRef = useRef<{ src: string, gw: number, gh: number, inset: number } | null>(null);
  const cachedWorkerResultRef = useRef<{ outline: number[], safeTopCurve: any, safeScan: any } | null>(null);

  const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const stageRef = useRef<any>(null);
  const staticLayerRef = useRef<any>(null);
  const textLayerRef = useRef<any>(null);
  const vaseLayerRef = useRef<any>(null);
  const activeLayerRef = useRef<any>(null);
  const trRef = useRef<any>(null);
  const templateLayerRef = useRef<any>(null);
  const backgroundLayerRef = useRef<any>(null);
  const guidesLayerRef = useRef<any>(null);
  const isTransformingRef = useRef<boolean>(false);
  const transformCacheStateRef = useRef<{
    tplWasCached: boolean;
    stWasCached: boolean;
    tplPrevListening?: boolean;
    stPrevListening?: boolean;
    nodeWasCached?: boolean;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const graniteCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingPhotoIdRef = useRef<string | null>(null);
  const deselectAllRef = useRef<(() => void) | null>(null);
  const [contractOutlinePts, setContractOutlinePts] = useState<number[] | null>(null);
  const [safeTopCurve, setSafeTopCurve] = useState<Array<{ x: number; y: number }> | null>(null);
  const [stageSize, setStageSize] = useState({ width: DESIGN_WIDTH, height: DESIGN_HEIGHT });
  const [visibilityTick, setVisibilityTick] = useState<number>(0);
  const [fontsLoaded, setFontsLoaded] = useState<boolean>(false);
  const [guides, setGuides] = useState<{ x1: number; y1: number; x2: number; y2: number; type: 'v' | 'h' }[]>([]);
  const [measureOverlays, setMeasureOverlays] = useState<Array<{
    x1: number; y1: number; x2: number; y2: number; label: string;
  }>>([]);
  const [areaSelectActive, setAreaSelectActive] = useState<boolean>(false);
  const [areaRect, setAreaRect] = useState<{ active: boolean; x: number; y: number; w: number; h: number }>({ active: false, x: 0, y: 0, w: 0, h: 0 });
  const [showSafeZoneWarning, setShowSafeZoneWarning] = useState<boolean>(false);
  const [showPlacementOutlines, setShowPlacementOutlines] = useState<boolean>(false);
  const [showGuidelineSettings, setShowGuidelineSettings] = useState<boolean>(false);
  const [hideDesignerUI, setHideDesignerUI] = useState<boolean>(false);
  const [showEsqPositionModal, setShowEsqPositionModal] = useState<boolean>(false);
  const [selectedEsqField, setSelectedEsqField] = useState<string>('FirstName');
  const esqFieldOptions = ['FirstName', 'Separator', 'LastName', 'DOB', 'DOD', 'Verse', 'Artwork'] as const;
  const [esqDatePicker, setEsqDatePicker] = useState<{ elementId: string; value: string } | null>(null);
  const safeWarnVisibleRef = useRef<boolean>(false);
  const setSafeWarnVisible = useCallback((visible: boolean) => {
    if (safeWarnVisibleRef.current !== visible) {
      safeWarnVisibleRef.current = visible;
      setShowSafeZoneWarning(visible);
    }
  }, []);
  const [safeZoneHit, setSafeZoneHit] = useState<boolean>(false);
  const [adjustingImageEl, setAdjustingImageEl] = useState<DesignElement | null>(null);
  const [graniteConfig, setGraniteConfig] = useState<{
    granite_details?: { size: number[] };
    vases?: Array<{ pos: string; coords: any }>;
    guides?: Array<{ margins: { top: number; left: number; right: number; bottom: number } }>;
  } | null>(null);
  const [graniteConfigTimestamp, setGraniteConfigTimestamp] = useState<number>(Date.now());
  const [vasePositioningKey, setVasePositioningKey] = useState<number>(Date.now());
  const prevGraniteTemplateRef = useRef<any>(null);
  const prevGraniteConfigRef = useRef<any>(null);
  const skipNextVaseAutoPositionRef = useRef<number>(0);
  const lastVaseRepositionKeyRef = useRef<string | null>(null);
  const savedVaseRepositionDoneRef = useRef<boolean>(false);
  const [showPositionUploadModal, setShowPositionUploadModal] = useState<boolean>(false);
  const [positionUploadBusy, setPositionUploadBusy] = useState<boolean>(false);
  const [artCodeOptions, setArtCodeOptions] = useState<string[]>([]);
  const [placementCodeOptions, setPlacementCodeOptions] = useState<string[]>([]);
  const [selectedArtCode, setSelectedArtCode] = useState<string>('');
  const [selectedPlacementCode, setSelectedPlacementCode] = useState<string>('');
  const [isEnteringNewPlacement, setIsEnteringNewPlacement] = useState<boolean>(false);
  const [codeMappings, setCodeMappings] = useState<Array<{ art_code: string, placement_code: string }>>([]);

  const [selectedPosType, setSelectedPosType] = useState<string>('');
  const [selectedCornerSide, setSelectedCornerSide] = useState<string>('corner_left');
  const [innerArtTitleOptions, setInnerArtTitleOptions] = useState<string[]>([]);
  const [selectedInnerArtTitle, setSelectedInnerArtTitle] = useState<string>('');

  const getTodayDateInputValue = useCallback(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const formatEsqDateForDisplay = useCallback((dateInput: string) => {
    if (!dateInput) return '';
    const parsed = new Date(`${dateInput}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return '';
    let abbreviateMonth = true;
    try {
      const raw = localStorage.getItem('design_tool.esq_text_form');
      if (raw) {
        const parsedPrefs = JSON.parse(raw);
        abbreviateMonth = parsedPrefs?.abbreviateMonth !== false;
      }
    } catch { }
    const monthLabels = abbreviateMonth
      ? ['JAN.', 'FEB.', 'MAR.', 'APR.', 'MAY', 'JUNE', 'JULY', 'AUG.', 'SEPT.', 'OCT.', 'NOV.', 'DEC.']
      : ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    const month = monthLabels[parsed.getMonth()] || '';
    const day = parsed.getDate();
    const year = parsed.getFullYear();
    return `${month} ${day}, ${year}`;
  }, []);

  const parseEsqDateContentToInput = useCallback((content: string, fieldName?: string) => {
    const raw = String(content || '').trim();
    const fallbackValue = String(fieldName || '').toLowerCase() === 'dob'
      ? '1940-01-01'
      : getTodayDateInputValue();
    if (!raw) return fallbackValue;
    const normalized = raw.replace(/\s+/g, ' ').toUpperCase();
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) {
      const yyyy = parsed.getFullYear();
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      const dd = String(parsed.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    return fallbackValue;
  }, [getTodayDateInputValue]);

  const applyEsqDateToElement = useCallback((elementId: string, dateInput: string) => {
    const nextContent = formatEsqDateForDisplay(dateInput);
    if (!nextContent) return;
    updateDesign(prev => {
      const idx = prev.elements.findIndex((item: any) => item.id === elementId);
      if (idx === -1) return prev;
      const current = prev.elements[idx] as any;
      if (String(current?.properties?.content || '') === nextContent) return prev;
      return {
        ...prev,
        elements: prev.elements.map((item: any, i: number) => (
          i === idx
            ? { ...item, properties: { ...item.properties, content: nextContent } }
            : item
        ))
      };
    });
  }, [formatEsqDateForDisplay, updateDesign]);

  const parseEsqDateInputValue = useCallback((dateInput: string) => {
    if (!dateInput) return null;
    const parsed = new Date(`${dateInput}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const esqDatePickerParts = useMemo(() => {
    const parsed = parseEsqDateInputValue(esqDatePicker?.value || '');
    const base = parsed || new Date();
    return {
      month: MONTHS[base.getMonth()],
      day: String(base.getDate()),
      year: String(base.getFullYear())
    };
  }, [esqDatePicker?.value, parseEsqDateInputValue]);

  useEffect(() => {
    if (showPositionUploadModal && isAdminEmail) {
      if (activePlacementSlot) {
        const pType = activePlacementSlot.slot_type;
        if (pType.startsWith('corner_')) {
          setSelectedPosType('corner');
          setSelectedCornerSide(pType);
        } else {
          setSelectedPosType(pType);
        }
      }

      const modelId = currentDesign.selectedModelId;
      const sizeId = currentDesign.selectedSizeId;
      const tpl = currentDesign.elements.find(el => (el.properties?.role === 'template' || el.properties?.isTemplate === true));
      const colorId = (tpl?.properties?.colorId || selectedGraniteMeta?.colorId);

      const params = new URLSearchParams();
      if (modelId) params.append('model_id', String(modelId));
      if (sizeId) params.append('size_id', String(sizeId));
      if (colorId) params.append('color_id', String(colorId));
      if (selectedPosType) params.append('placement', selectedPosType);

      if (selectedPosType === 'inner_artwork' && selectedArtCode) {
        fetch(buildApiUrl(`/api/art/inner?art_code=${encodeURIComponent(selectedArtCode)}`), { headers: authHeaders() })
          .then(res => res.json())
          .then(data => {
            if (data.status && data.data) {
              setInnerArtTitleOptions(data.data.map((item: any) => item.title));
            }
          })
          .catch(() => { });
      }

      fetch(buildApiUrl(`/api/designs/get-unique-codes?${params.toString()}`), { headers: authHeaders() })
        .then(res => res.json())
        .then(data => {
          if (data.ok) {
            setArtCodeOptions(data.art_codes || []);
            setPlacementCodeOptions(data.placement_codes || []);
            setCodeMappings(data.mappings || []);
          }
        })
        .catch(() => { });
    }
  }, [showPositionUploadModal, isAdminEmail, activePlacementSlot, currentDesign.selectedModelId, currentDesign.selectedSizeId, currentDesign.elements, selectedGraniteMeta, selectedPosType]);

  const handleArtCodeChange = (code: string) => {
    setSelectedArtCode(code);
    setSelectedInnerArtTitle('');
    if (code) {
      const match = codeMappings.find(m => m.art_code === code);
      if (match && match.placement_code) {
        setSelectedPlacementCode(match.placement_code);
        setIsEnteringNewPlacement(false);
      }
    }
  };

  const handlePlacementCodeChange = (code: string) => {
    setSelectedPlacementCode(code);
    if (code && code !== 'NEW') {
      const match = codeMappings.find(m => m.placement_code === code);
      if (match && match.art_code) {
        setSelectedArtCode(match.art_code);
      }
    }
  };

  useEffect(() => {
    if (showPositionUploadModal && selectedElement) {
      const el = currentDesign.elements.find(e => e.id === selectedElement);
      if (el) {
        if (el.properties?.isInnerArtwork) {
          setSelectedPosType('inner_artwork');
          setSelectedArtCode(el.properties?.parentArtCode || '');
          setSelectedInnerArtTitle(el.properties?.alt || '');
          setSelectedPlacementCode('');
          return;
        }

        if (el.properties?.artCode) {
          setSelectedArtCode(el.properties.artCode);
        } else if (el.properties?.alt) {
          setSelectedArtCode(el.properties.alt);
        }

        if (placementSlots && placementSlots.length > 0) {
          const slot = placementSlots.find(s =>
            rectsOverlap(el, { x: Number(s.x), y: Number(s.y), width: Number(s.width), height: Number(s.height) })
          );
          if (slot) {
            if (slot.placement_code) {
              setSelectedPlacementCode(slot.placement_code);
            }
            const pType = slot.slot_type || slot.type;
            if (pType) {
              if (pType.startsWith('corner_')) {
                setSelectedPosType('corner');
                setSelectedCornerSide(pType);
              } else {
                setSelectedPosType(pType);
              }
            }
          }
        }
      }
    }
  }, [showPositionUploadModal, selectedElement, currentDesign.elements, placementSlots]);

  const SNAP_TOLERANCE = 0; // px
  const computeStageWidth = useCallback((): number => DESIGN_WIDTH, []);
  const computeStageHeight = useCallback((): number => DESIGN_HEIGHT, []);

  const previewSrcForComposite = (selectedGraniteImage || (selectedGraniteMeta && selectedGraniteMeta.src) || '') as string;
  const previewImageEl = useImage(previewSrcForComposite, 'high');
  const baseImageEl = useImage(selectedGraniteBaseImage || '', 'high');
  const bgUrl = useMemo(() => selectedBackground?.image_url || null, [selectedBackground?.image_url]);
  const bgColor = useMemo(() => (selectedBackground as any)?.background_color || '#e0e0e0', [(selectedBackground as any)?.background_color]);

  const baseWaitStartTsRef = useRef<number>(0);
  const ignoreBaseUntilChangeRef = useRef<boolean>(false);
  const colorFetchReqIdRef = useRef<number>(0);
  const colorFetchAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    try {
      const bg = (currentDesign as any)?.background;
      if (!isApplyingDesign && bg && !selectedBackground) {
        setSelectedBackground(bg as any);
      }
    } catch { }
  }, [(currentDesign as any)?.background, isApplyingDesign, selectedBackground, setSelectedBackground]);

  useEffect(() => {
    if (isApplyingDesign) return;
    try {
      if (selectedGraniteMeta) return;
      const els: any[] = Array.isArray((currentDesign as any)?.elements) ? (currentDesign as any).elements : [];
      const tpl: any = els.find((el: any) =>
        el?.type === 'artwork' && (el?.properties?.role === 'template' || el?.properties?.isTemplate === true)
      );
      if (!tpl?.properties) return;
      const savedMeta: any = (currentDesign as any)?.selectedGraniteMeta || (currentDesign as any)?.graniteMeta || null;
      const modelId = Number(tpl.properties.modelId || savedMeta?.modelId || 0);
      const sizeId = Number(tpl.properties.sizeId || savedMeta?.sizeId || 0);
      const colorId = Number(tpl.properties.colorId || savedMeta?.colorId || 0);
      const src = (tpl.properties.src ? String(tpl.properties.src) : (savedMeta?.src ? String(savedMeta.src) : ''));
      if (modelId > 0 && sizeId >= 0 && colorId > 0 && src) {
        setSelectedGraniteMeta({ modelId, sizeId, colorId, src });
        try {
          if (!selectedGraniteImage || selectedGraniteImage !== src) {
            setSelectedGraniteImage?.(src);
          }
        } catch { }
      }
    } catch { }
  }, [currentDesign, isApplyingDesign, selectedGraniteMeta, setSelectedGraniteMeta, setSelectedGraniteImage, selectedGraniteImage]);
  useEffect(() => {
    ignoreBaseUntilChangeRef.current = false;
    baseWaitStartTsRef.current = Date.now();
    if (!selectedGraniteBaseImage) return;
    const t = window.setTimeout(() => {
      if (!baseImageEl) {
        ignoreBaseUntilChangeRef.current = true;
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [selectedGraniteBaseImage, baseImageEl]);

  const compositePending = useMemo(() => {
    try {
      let c = 0;
      if (previewSrcForComposite) { if (!previewImageEl) c += 1; }
      if (selectedGraniteBaseImage && !ignoreBaseUntilChangeRef.current) { if (!baseImageEl) c += 1; }
      return c;
    } catch { return 0; }
  }, [previewSrcForComposite, previewImageEl, selectedGraniteBaseImage, baseImageEl]);

  const designerApiCacheRef = useRef<Record<string, any>>({});

  const loadGraniteConfig = useCallback(async (modelId: number, sizeId: number, colorId: number) => {
    const cacheKey = `granite_config_${modelId}_${sizeId}_${colorId}`;
    if (designerApiCacheRef.current[cacheKey]) {
      const cachedData = designerApiCacheRef.current[cacheKey];
      setGraniteConfig(cachedData.config);
      if (setSelectedGraniteBaseImage && cachedData.baseUrl !== undefined) {
        setSelectedGraniteBaseImage(cachedData.baseUrl);
      }
      setGraniteConfigTimestamp(Date.now());
      setVasePositioningKey(Date.now());
      return;
    }

    try {
      const data = await fetchDesignPreview(modelId, sizeId, colorId);
      if (data?.data?.config_json) {
        const config = typeof data.data.config_json === 'string'
          ? JSON.parse(data.data.config_json)
          : data.data.config_json;

        let baseUrl = null;
        try {
          const baseApi = data?.data?.preview_img_api_url;
          const baseRaw = data?.data?.preview_img;
          baseUrl = baseApi && baseApi !== buildApiUrl('/api/uploads/')
            ? baseApi
            : (baseRaw ? buildApiUrl(`/api/uploads/${String(baseRaw).replace(/^\/*uploads\//, '')}`) : null);
        } catch { }

        setGraniteConfig(config);
        if (setSelectedGraniteBaseImage) setSelectedGraniteBaseImage(baseUrl || null);

        designerApiCacheRef.current[cacheKey] = { config, baseUrl };

        setGraniteConfigTimestamp(Date.now());
        setVasePositioningKey(Date.now());
      } else {
        setGraniteConfig(null);
      }
    } catch (error) {
      setGraniteConfig(null);
    }
  }, [fetchDesignPreview, setSelectedGraniteBaseImage]);


  const getElementBounds = useCallback((el: DesignElement) => {
    return {
      left: el.x,
      top: el.y,
      right: el.x + el.width,
      bottom: el.y + el.height,
      cx: el.x + el.width / 2,
      cy: el.y + el.height / 2
    };
  }, []);

  const computeSnap = useCallback((
    draggingId: string,
    desiredCenterX: number,
    desiredCenterY: number,
    width: number,
    height: number
  ) => {
    const vLines: number[] = [];
    const hLines: number[] = [];
    try {
      if (layoutGuides) {
        const lg = layoutGuides;
        vLines.push(lg.left, lg.right);
        hLines.push(lg.top, lg.bottom);
      }
    } catch (_) { }
    try {
      const bounds: Array<{ id: string; left: number; right: number; top: number; bottom: number; cx: number; cy: number }> = [];
      for (const el of currentDesign.elements) {
        if (!el || el.id === draggingId) continue;
        const role = String(el.properties?.role || el.properties?.kind || '').toLowerCase();
        if (role === 'template' || el.properties?.isTemplate === true) continue;
        const b = getElementBounds(el);
        bounds.push({ id: el.id, left: b.left, right: b.right, top: b.top, bottom: b.bottom, cx: b.cx, cy: b.cy });
      }
      for (let i = 0; i < bounds.length; i++) {
        for (let j = 0; j < bounds.length; j++) {
          if (i === j) continue;
          const a = bounds[i];
          const b = bounds[j];
          const yOverlap = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
          if (yOverlap <= 0) continue;
          const leftB = a.left <= b.left ? a : b;
          const rightB = a.left <= b.left ? b : a;
          const gap = Math.max(0, rightB.left - leftB.right);
          if (gap <= 0) continue;
          vLines.push(rightB.right + gap);               // right edge alignment target
          vLines.push(rightB.right + gap + width / 2);   // center alignment to match gap
          vLines.push(leftB.left - gap);                 // left edge alignment target
          vLines.push(leftB.left - gap - width / 2);     // center alignment to match gap
        }
      }
      // Vertical equal gaps (column-like): horizontal overlap required
      for (let i = 0; i < bounds.length; i++) {
        for (let j = 0; j < bounds.length; j++) {
          if (i === j) continue;
          const a = bounds[i];
          const b = bounds[j];
          const xOverlap = Math.min(a.right, b.right) - Math.max(a.left, b.left);
          if (xOverlap <= 0) continue;
          const topB = a.top <= b.top ? a : b;
          const bottomB = a.top <= b.top ? b : a;
          const gapY = Math.max(0, bottomB.top - topB.bottom);
          if (gapY <= 0) continue;
          hLines.push(bottomB.bottom + gapY);
          hLines.push(bottomB.bottom + gapY + height / 2);
          hLines.push(topB.top - gapY);
          hLines.push(topB.top - gapY - height / 2);
        }
      }
    } catch (_) { }

    // Other elements (exclude templates and the dragging element)
    try {
      for (const el of currentDesign.elements) {
        if (!el || el.id === draggingId) continue;
        const role = String(el.properties?.role || el.properties?.kind || '').toLowerCase();
        if (role === 'template' || el.properties?.isTemplate === true) continue;
        const b = getElementBounds(el);
        vLines.push(b.left, b.cx, b.right);
        hLines.push(b.top, b.cy, b.bottom);
      }
    } catch (_) { }

    try {
      if (layoutGuides) {
        const lg = layoutGuides;
        const gCenterX = Math.floor((lg.left + lg.right) / 2);
        const gCenterY = Math.floor((lg.top + lg.bottom) / 2);
        for (const el of currentDesign.elements) {
          if (!el || el.id === draggingId) continue;
          const role = String(el.properties?.role || el.properties?.kind || '').toLowerCase();
          if (role === 'template' || el.properties?.isTemplate === true) continue;
          const b = getElementBounds(el);
          // Mirror horizontal positions across center X
          const mirrorLeft = gCenterX - (b.right - gCenterX);
          const mirrorCenter = gCenterX - (b.cx - gCenterX);
          const mirrorRight = gCenterX - (b.left - gCenterX);
          vLines.push(mirrorLeft, mirrorCenter, mirrorRight);
          // Mirror vertical positions across center Y
          const mirrorTop = gCenterY - (b.bottom - gCenterY);
          const mirrorMiddle = gCenterY - (b.cy - gCenterY);
          const mirrorBottom = gCenterY - (b.top - gCenterY);
          hLines.push(mirrorTop, mirrorMiddle, mirrorBottom);
        }
      }
    } catch (_) { }

    const candidateCentersX: Array<{ center: number; guide: { x1: number; y1: number; x2: number; y2: number; type: 'v' } }> = [];
    const candidateCentersY: Array<{ center: number; guide: { x1: number; y1: number; x2: number; y2: number; type: 'h' } }> = [];

    // Limit guide rendering to layout bounds when available
    const guideY1 = layoutGuides ? layoutGuides.top : 0;
    const guideY2 = layoutGuides ? layoutGuides.bottom : stageSize.height;
    const guideX1 = layoutGuides ? layoutGuides.left : 0;
    const guideX2 = layoutGuides ? layoutGuides.right : stageSize.width;

    // For each vertical snap line, compute which centerX would align left/center/right
    for (const x of vLines) {
      candidateCentersX.push({ center: x, guide: { x1: x + 0.5, y1: guideY1, x2: x + 0.5, y2: guideY2, type: 'v' } }); // center-center
      candidateCentersX.push({ center: x + width / 2, guide: { x1: x + 0.5, y1: guideY1, x2: x + 0.5, y2: guideY2, type: 'v' } }); // left-left
      candidateCentersX.push({ center: x - width / 2, guide: { x1: x + 0.5, y1: guideY1, x2: x + 0.5, y2: guideY2, type: 'v' } }); // right-right
    }

    // For each horizontal snap line, compute which centerY would align top/center/bottom
    for (const y of hLines) {
      candidateCentersY.push({ center: y, guide: { x1: guideX1, y1: y + 0.5, x2: guideX2, y2: y + 0.5, type: 'h' } }); // center-center
      candidateCentersY.push({ center: y + height / 2, guide: { x1: guideX1, y1: y + 0.5, x2: guideX2, y2: y + 0.5, type: 'h' } }); // top-top
      candidateCentersY.push({ center: y - height / 2, guide: { x1: guideX1, y1: y + 0.5, x2: guideX2, y2: y + 0.5, type: 'h' } }); // bottom-bottom
    }

    let snappedX = desiredCenterX;
    let snappedY = desiredCenterY;
    const nextGuides: { x1: number; y1: number; x2: number; y2: number; type: 'v' | 'h' }[] = [];

    // Find closest X within tolerance
    let bestDx = SNAP_TOLERANCE + 1;
    for (const c of candidateCentersX) {
      const dx = Math.abs(desiredCenterX - c.center);
      if (dx < bestDx && dx <= SNAP_TOLERANCE) {
        bestDx = dx;
        snappedX = c.center;
        // Replace any existing vertical guide
        for (let i = nextGuides.length - 1; i >= 0; i--) { if (nextGuides[i].type === 'v') nextGuides.splice(i, 1); }
        nextGuides.push(c.guide);
      }
    }

    // Find closest Y within tolerance
    let bestDy = SNAP_TOLERANCE + 1;
    for (const c of candidateCentersY) {
      const dy = Math.abs(desiredCenterY - c.center);
      if (dy < bestDy && dy <= SNAP_TOLERANCE) {
        bestDy = dy;
        snappedY = c.center;
        // Replace any existing horizontal guide
        for (let i = nextGuides.length - 1; i >= 0; i--) { if (nextGuides[i].type === 'h') nextGuides.splice(i, 1); }
        nextGuides.push(c.guide);
      }

    }

    return { snappedX, snappedY, guides: nextGuides };
  }, [currentDesign.elements, getElementBounds, stageSize.width, stageSize.height, layoutGuides]);



  const graniteTemplate = useMemo(() => {
    return currentDesign.elements.find(el =>
      el.type === 'artwork' &&
      (el.properties?.role === 'template' || el.properties?.isTemplate === true)
    );
  }, [currentDesign.elements]);

  // Special-case: Serptop sizes 16x4x18 and 18x4x24 should have right-side vase only
  // (Updated per user: "single vase only apply for Serptop's 16x4x18 18x4x24 only")
  const isSerptopRightOnly = useMemo(() => {
    try {
      const sizeArr = graniteConfig?.granite_details?.size as any;
      if (Array.isArray(sizeArr)) {
        const nums = sizeArr.map((n: any) => Number(n)).filter((n: number) => isFinite(n));
        // Check for 16x4x18 (or order variants)
        const has16_4_18 = nums.length === 3 && [16, 4, 18].every(v => nums.includes(v));
        // Check for 18x4x24 (or order variants)
        const has18_4_24 = nums.length === 3 && [18, 4, 24].every(v => nums.includes(v));

        if (has16_4_18 || has18_4_24) return true;
      }
    } catch { }
    return false;
  }, [graniteConfig]);

  const compositeProps = useMemo(() => {
    if (!baseImageEl) return null;

    const stageWidth = Math.max(1, computeStageWidth());
    const stageHeight = Math.max(1, computeStageHeight());
    const availWidth = Math.max(1, stageWidth - granitePadding * 2);
    const availHeight = Math.max(1, stageHeight - granitePadding * 2);

    const pW = Math.max(1, Math.floor(previewImageEl?.naturalWidth || previewImageEl?.width || 1));
    const pH = Math.max(1, Math.floor(previewImageEl?.naturalHeight || previewImageEl?.height || 1));
    const bW = Math.max(1, Math.floor(baseImageEl.naturalWidth || baseImageEl.width || 1));
    const bH = Math.max(1, Math.floor(baseImageEl.naturalHeight || baseImageEl.height || 1));

    // If a template exists, treat template (preview) and base as a single stacked composite
    const tpl = (currentDesign.elements || []).find((el: any) => String(el?.properties?.role || el?.properties?.kind || '').toLowerCase() === 'template');

    const physicalSize = graniteConfig?.granite_details?.size as number[] | undefined;
    const physicalW = physicalSize ? Number(physicalSize[0]) : 0;
    const physicalH = physicalSize ? Number(physicalSize[1]) : 0;
    const gap = 0;

    let scale: number;
    const isProportionalSize = physicalW === 48 || physicalW === 54;

    if (isProportionalSize && physicalW > 0 && pW > 0) {
      const refInches = 72;
      const targetPPI = availWidth / refInches;

      // Proportional scale
      scale = (physicalW * targetPPI) / pW;

      // Ensure it doesn't overflow the stage height/width
      const maxFitScale = Math.min(
        availWidth / Math.max(pW, bW),
        availHeight / (pH + gap + bH)
      );
      if (scale > maxFitScale) scale = maxFitScale;
    } else {
      // DEFAULT: All other sizes (or if dimensions unknown) use max-fit to fill the screen
      scale = Math.min(
        availWidth / Math.max(pW, bW),
        availHeight / (pH + gap + bH)
      );
    }

    if (tpl) {
      if (!previewImageEl) return null;

      const dispPW = Math.floor(pW * scale);
      const dispPH = Math.floor(pH * scale);
      const dispBW = Math.floor(bW * scale);
      const dispBH = Math.floor(bH * scale);

      const totalH = dispPH + gap + dispBH;
      const previewX = Math.floor((stageWidth - dispPW) / 2);
      let baseX = Math.floor((stageWidth - dispBW) / 2);
      if (isSerptopRightOnly) {
        const shift = Math.floor(dispPW * 0.12);
        baseX = baseX + shift;
      }
      const topY = Math.floor((stageHeight - totalH) / 2);

      return {
        preview: { image: previewImageEl, x: previewX, y: topY, width: dispPW, height: dispPH },
        base: { image: baseImageEl, x: baseX, y: topY + dispPH + gap, width: dispBW, height: dispBH }
      } as any;
    }

    // No template: render both images stacked with zero gap
    if (!previewImageEl) return null;

    const dispPW = Math.floor(pW * scale);
    const dispPH = Math.floor(pH * scale);
    const dispBW = Math.floor(bW * scale);
    const dispBH = Math.floor(bH * scale);

    const totalH = dispPH + gap + dispBH;
    const previewX = Math.floor((stageWidth - dispPW) / 2);
    let baseX = Math.floor((stageWidth - dispBW) / 2);
    if (isSerptopRightOnly) {
      const shift = Math.floor(dispPW * 0.12);
      baseX = baseX + shift;
    }
    const topY = Math.floor((stageHeight - totalH) / 2);

    return {
      preview: { image: previewImageEl, x: previewX, y: topY, width: dispPW, height: dispPH },
      base: { image: baseImageEl, x: baseX, y: topY + dispPH + gap, width: dispBW, height: dispBH }
    };
  }, [previewImageEl, baseImageEl, computeStageWidth, computeStageHeight, currentDesign.elements, granitePadding, isSerptopRightOnly, graniteConfig]);

  // Compute pixels-per-inch strictly from DB config and current template size
  const pixelsPerInch = useMemo(() => {
    try {
      const tpl = (currentDesign.elements || []).find((el: any) => String(el?.properties?.role || el?.properties?.kind || '').toLowerCase() === 'template');
      let size = graniteConfig?.granite_details?.size as number[] | undefined;

      // ESQ: prefer explicit dimensions from modal data; fall back to parsing modal name; then final hard fallback.
      if (tpl?.properties?.designMode === 'ESQ') {
        const wIn = Number(tpl?.properties?.esqModalWidthIn);
        const hIn = Number(tpl?.properties?.esqModalHeightIn);
        if (Number.isFinite(wIn) && wIn > 0 && Number.isFinite(hIn) && hIn > 0) {
          size = [wIn, hIn];
        } else if (tpl?.properties?.esqModalName) {
          const match = String(tpl.properties.esqModalName).match(/(\d+)\s*[xX]\s*(\d+)/);
          if (match) {
            size = [Number(match[1]), Number(match[2])];
          } else {
            size = [24, 24]; // Standard fallback if name doesn't contain size
          }
        }
      }

      if (!tpl || !Array.isArray(size) || size.length < 2) return 35;

      const logicalW = Number(size[0]);
      const logicalH = Number(size[1]);
      if (logicalW <= 0 || logicalH <= 0) return 35;

      // Use the visual width from compositeProps for high-accuracy stage PPI
      const cp = compositeProps as any;
      if (cp && cp.preview && cp.preview.width > 0) {
        return cp.preview.width / logicalW;
      }

      // Fallback to natural PPI if composite is not ready
      if (!tpl.width || !tpl.height) return 35;
      const ppiX = tpl.width / logicalW;
      const ppiY = tpl.height / logicalH;
      const avg = (isFinite(ppiX) && isFinite(ppiY)) ? (ppiX + ppiY) / 2 : (isFinite(ppiX) ? ppiX : (isFinite(ppiY) ? ppiY : 0));
      return (isFinite(avg) && avg > 0) ? avg : 35; // Fallback to 35 PPI if not calculable
    } catch (_) { return 35; }
  }, [currentDesign.elements, graniteConfig, compositeProps]);

  // Sync PPI to context for other components 
  useEffect(() => {
    setCtxPixelsPerInch(pixelsPerInch);
    // Also sync to window for legacy lookups
    (window as any).__appPixelsPerInch = pixelsPerInch;
  }, [pixelsPerInch, setCtxPixelsPerInch]);

  // Reset Sample Guide Lines whenever the model / size / color changes so that
  // stale positions from the previous stone don't carry over.
  useEffect(() => {
    // LSP/QD: use graniteConfig inches.
    // ESQ: use modal inches (width_in/height_in) if provided; otherwise fall back to parsing modal name.
    const tpl = (currentDesign.elements || []).find((el: any) =>
      String(el?.properties?.role || el?.properties?.kind || '').toLowerCase() === 'template'
    );

    let physW = 0;
    let physH = 0;

    const size = graniteConfig?.granite_details?.size as number[] | undefined;
    if (Array.isArray(size) && size.length >= 2 && Number(size[0]) > 0 && Number(size[1]) > 0) {
      physW = Number(size[0]);
      physH = Number(size[1]);
    } else if (designMode === 'ESQ') {
      const wIn = Number(tpl?.properties?.esqModalWidthIn);
      const hIn = Number(tpl?.properties?.esqModalHeightIn);
      if (Number.isFinite(wIn) && wIn > 0 && Number.isFinite(hIn) && hIn > 0) {
        physW = wIn;
        physH = hIn;
      } else {
        const modalName = String(tpl?.properties?.esqModalName || '');
        const match = modalName.match(/(\d+)\s*[xX]\s*(\d+)/);
        if (match) {
          physW = Number(match[1]) || 0;
          physH = Number(match[2]) || 0;
        }
      }
    }
    setSampleGuideLines(prev => ({
      ...prev,          // keep visible state
      h1: 0,
      h2: physH || 0,  // full stone height (e.g. 24")
      v1: 0,
      v2: physW || 0,  // full stone width  (e.g. 36")
      visible: false
    }));
  }, [
    graniteConfigTimestamp,
    graniteConfig,
    designMode,
    currentDesign.elements,
  ]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    try {
      (window as any).__appSelectedGraniteMeta = selectedGraniteMeta;
      (window as any).__appGraniteConfig = graniteConfig;
      (window as any).__appGraniteConfigTimestamp = graniteConfigTimestamp;
      (window as any).__appPixelsPerInch = pixelsPerInch;
      (window as any).__appIsSerptopRightOnly = isSerptopRightOnly;
    } catch { }
  }, [selectedGraniteMeta, graniteConfig, graniteConfigTimestamp, pixelsPerInch, isSerptopRightOnly]);


  // (Removed effect that cleared activePlacementSlot when tool changed,
  // to ensure visibility persists into Text tool as requested)

  useEffect(() => {
    if (!graniteConfig) return;
    updateDesign(prev => {
      const isVase = (el: any) => {
        try {
          return el && el.type === 'artwork' && String(el.properties?.role || el.properties?.kind || '').toLowerCase() === 'vase';
        } catch {
          return false;
        }
      };
      const sideOf = (el: any) => {
        try { return String((el.properties as any)?.side || 'left').toLowerCase(); } catch { return 'left'; }
      };

      const vases = (prev.elements || []).filter(isVase);
      if (vases.length === 0) return prev;

      const leftVase = vases.find(v => sideOf(v) === 'left');
      const rightVase = vases.find(v => sideOf(v) === 'right');

      if (isSerptopRightOnly) {
        // Single-vase model: keep only the right vase, remove the left one.
        const keeper = rightVase || vases[0];
        const keeperId = keeper.id;
        const keeperSide = sideOf(keeper);
        const keeperUpdated = keeperSide === 'right'
          ? keeper
          : { ...keeper, properties: { ...keeper.properties, side: 'right', autosnap: true, fromSaved: true, draggable: false } };

        const filtered = (prev.elements || []).filter(el => !isVase(el) || el.id === keeperId);
        const nextElements = filtered.map(el => (el && el.id === keeperId ? keeperUpdated : el));
        const changedLocal = nextElements.length !== (prev.elements || []).length || keeperUpdated !== keeper;
        return changedLocal ? { ...prev, elements: nextElements } : prev;
      }

      if (leftVase && rightVase) return prev; // Already complete — no change needed.

      if (rightVase && !leftVase) {
        // Right vase exists but left is missing — add a left copy.
        const newLeft = {
          ...rightVase,
          id: rightVase.id.replace('right', 'left').replace('vase-right-', 'vase-left-') + (rightVase.id.includes('left') ? '' : ''),
          properties: { ...rightVase.properties, side: 'left' }
        };
        // Ensure the id is actually different from the right vase id
        const safeId = newLeft.id === rightVase.id
          ? `vase-left-${Date.now()}`
          : newLeft.id;
        return { ...prev, elements: [...(prev.elements || []), { ...newLeft, id: safeId }] };
      }

      if (leftVase && !rightVase) {
        // Left vase exists but right is missing — add a right copy.
        const newRight = {
          ...leftVase,
          id: leftVase.id.replace('left', 'right').replace('vase-left-', 'vase-right-'),
          properties: { ...leftVase.properties, side: 'right' }
        };
        const safeId = newRight.id === leftVase.id
          ? `vase-right-${Date.now()}`
          : newRight.id;
        return { ...prev, elements: [...(prev.elements || []), { ...newRight, id: safeId }] };
      }

      return prev;
    });
  }, [graniteConfig, graniteConfigTimestamp, isSerptopRightOnly]);

  // Sync existing vases to current monument color and size variant (AV01 vs AV02)
  useEffect(() => {
    const colorId = selectedGraniteMeta?.colorId || (currentDesign as any)?.selectedColorId;
    if (!colorId) return;

    const sizeName = (currentDesign as any)?.selectedSizeName || '';
    const digits = sizeName.match(/\d+/g) || [];
    const nums = digits.map(Number);
    const preferredVariant = (nums.length >= 3 && [48, 6, 24].every(n => nums.includes(n))) ? 'AV02' : 'AV01';

    updateDesign(prev => {
      let changed = false;
      const nextElements = prev.elements.map(el => {
        if (el.type !== 'artwork' || String(el.properties?.role || el.properties?.kind || '').toLowerCase() !== 'vase') {
          return el;
        }

        const currentVariant = el.properties?.vaseVariantId || 'AV01';
        const currentColorId = el.properties?.colorId;

        if (currentVariant !== preferredVariant || Number(currentColorId) !== Number(colorId)) {
          changed = true;
          const isAV02 = preferredVariant === 'AV02';
          const heightFactor = isAV02 ? 0.39 : 0.42;
          const ratio = isAV02 ? (2.6 / 4) : (1.8 / 4);
          const overlap = isAV02 ? 1 : 5;

          const tpl = prev.elements.find(e => e.properties?.role === 'template' || e.properties?.isTemplate);
          if (tpl) {
            const newH = Math.round(tpl.height * heightFactor);
            const newW = Math.round(newH * ratio);
            const newY = tpl.y + tpl.height - newH + overlap;

            return {
              ...el,
              width: newW,
              height: newH,
              y: newY,
              properties: {
                ...el.properties,
                colorId: colorId,
                vaseVariantId: preferredVariant,
              }
            };
          }
        }
        return el;
      });

      return changed ? { ...prev, elements: nextElements } : prev;
    });
  }, [selectedGraniteMeta?.colorId, (currentDesign as any)?.selectedSizeName, updateDesign]);

  // Sync safe-zone to layoutGuides: use a 1.5-inch inset as the safe zone boundary
  useEffect(() => {
    try {
      const tpl = (currentDesign.elements || []).find((el: any) => String(el?.properties?.role || el?.properties?.kind || '').toLowerCase() === 'template');
      const ppi = Number(pixelsPerInch || 0);
      if (!tpl || !(ppi > 0)) { setLayoutGuides(null as any); return; }
      const inset = Math.max(0, Math.floor(1.5 * ppi)); // 1.5 inch safe zone margin
      const left = Math.floor(Number(tpl.x || 0) + inset);
      const top = Math.floor(Number(tpl.y || 0) + inset);
      const right = Math.floor(Number(tpl.x || 0) + Math.max(0, Number(tpl.width || 0) - inset));
      const bottom = Math.floor(Number(tpl.y || 0) + Math.max(0, Number(tpl.height || 0) - inset));
      if (right > left && bottom > top) {
        setLayoutGuides({ left, top, right, bottom });
      } else {
        setLayoutGuides(null as any);
      }
    } catch {
      try { setLayoutGuides(null as any); } catch { }
    }
  }, [currentDesign.elements, pixelsPerInch]);
  const outlineWorkerRef = useRef<Worker | null>(null);

  useEffect(() => {
    // Initialize worker
    const worker = new Worker(new URL('../workers/outline.worker.ts', import.meta.url));
    outlineWorkerRef.current = worker;

    worker.onmessage = (e) => {
      const { outline, safeTopCurve, safeScan, error, gx, gy } = e.data;
      if (error) {
        console.error('[Designer] outline.worker returned error:', error);
      }
      if (!error && outline) {
        // Cache the LOCAL results (before adding gx/gy)
        cachedWorkerResultRef.current = { outline, safeTopCurve, safeScan };

        // 1. Map outline to global coords
        const mappedOutline: number[] = [];
        for (let i = 0; i < outline.length; i += 2) {
          mappedOutline.push(outline[i] + gx, outline[i + 1] + gy);
        }

        // 2. Map safeTopCurve
        const mappedSafeTop = safeTopCurve ? safeTopCurve.map((p: any) => ({ x: p.x + gx, y: p.y + gy })) : null;

        setContractOutlinePts(mappedOutline);
        setSafeTopCurve(mappedSafeTop);

        if (safeScan) {
          safeScanRef.current = {
            ...safeScan,
            gx, gy
          };
        }
      } else {
        // Error or empty result
        setContractOutlinePts(null);
        setSafeTopCurve(null);
        cachedWorkerResultRef.current = null;
      }
    };

    return () => {
      worker.terminate();
    };
  }, []);

  useEffect(() => {
    if ((document as any).fonts) {
      (document as any).fonts.ready.then(() => {
        setFontsLoaded(true);
        // Force batch draw on layers that contain text
        try { if (textLayerRef.current) textLayerRef.current.batchDraw(); } catch (_) { }
        try { if (staticLayerRef.current) staticLayerRef.current.batchDraw(); } catch (_) { }
      });
    }
  }, []);

  useEffect(() => {
    try {
      const tpl = (currentDesign.elements || []).find((el: any) => String(el?.properties?.role || el?.properties?.kind || '').toLowerCase() === 'template');
      const ppi = Number(pixelsPerInch || 0);

      if (!tpl || !tpl.properties?.src || !(ppi > 0)) {
        setContractOutlinePts(null);
        // Clear cache if template is gone
        lastProcessedParamsRef.current = null;
        cachedWorkerResultRef.current = null;
        return;
      }

      const gx = Number(tpl.x || 0);
      const gy = Number(tpl.y || 0);
      const gw = Math.max(1, Number(tpl.width || 0));
      const gh = Math.max(1, Number(tpl.height || 0));
      const inset = Math.max(0, Math.floor(0 * ppi));
      const src = String(tpl.properties.src);

      // check if key params changed
      const prev = lastProcessedParamsRef.current;
      const paramsChanged = !prev || prev.src !== src || prev.gw !== gw || prev.gh !== gh || prev.inset !== inset;

      if (paramsChanged) {
        // Clear old stale cache
        setContractOutlinePts(null);
        setSafeTopCurve(null);
        cachedWorkerResultRef.current = null;

        // Must re-process in worker
        lastProcessedParamsRef.current = { src, gw, gh, inset };

        if (outlineWorkerRef.current) {
          outlineWorkerRef.current.postMessage({ imageUrl: src, gw, gh, inset, gx, gy });
        }
      } else {
        // Params matched, use cache if available
        const cached = cachedWorkerResultRef.current;
        if (cached && cached.outline) {
          // Reuse cache, just apply new gx/gy
          const { outline, safeTopCurve, safeScan } = cached;

          const mappedOutline: number[] = [];
          for (let i = 0; i < outline.length; i += 2) {
            mappedOutline.push(outline[i] + gx, outline[i + 1] + gy);
          }

          const mappedSafeTop = safeTopCurve ? safeTopCurve.map((p: any) => ({ x: p.x + gx, y: p.y + gy })) : null;

          setContractOutlinePts(mappedOutline);
          setSafeTopCurve(mappedSafeTop);

          if (safeScan) {
            safeScanRef.current = {
              ...safeScan,
              gx, gy
            };
          }
        }
      }

    } catch {
      setContractOutlinePts(null);
    }
  }, [currentDesign.elements, pixelsPerInch, graniteConfigTimestamp, previewImageEl]);


  const formatInches = useCallback((pixels: number) => {
    // Always show inches. If PPI is unknown, fall back to 96 px/in.
    const ppi = pixelsPerInch > 0.0001 ? pixelsPerInch : 96;
    const inches = pixels / ppi;
    return `${inches.toFixed(2)} in`;
  }, [pixelsPerInch]);

  // Convert inches to pixels using detected PPI or 96 px/in as fallback
  const inchesToPixels = useCallback((inches: number) => {
    const ppi = pixelsPerInch > 0.0001 ? pixelsPerInch : 96;
    return inches * ppi;
  }, [pixelsPerInch]);



  // Measure minimal gaps to nearby elements for an element's rect
  const computeNearestGaps = useCallback((self: { x: number; y: number; width: number; height: number; id?: string }) => {
    const selfBounds = { left: self.x, right: self.x + self.width, top: self.y, bottom: self.y + self.height, cx: self.x + self.width / 2, cy: self.y + self.height / 2 };
    let bestHX: { gap: number; y: number; x1: number; x2: number } | null = null; // horizontal line between vertical edges (left/right gap)
    let bestVY: { gap: number; x: number; y1: number; y2: number } | null = null; // vertical line between top/bottom edges (vertical gap)
    try {
      for (const el of currentDesign.elements) {
        if (!el || el.id === self.id) continue;
        const role = String(el.properties?.role || el.properties?.kind || '').toLowerCase();
        if (role === 'template' || el.properties?.isTemplate === true) continue;
        const b = getElementBounds(el as any);
        // Horizontal gap (left/right)
        const yOverlap = Math.min(selfBounds.bottom, b.bottom) - Math.max(selfBounds.top, b.top);
        if (yOverlap > 0) {
          // gaps: el.right -> self.left, self.right -> el.left
          const gaps = [
            { gap: Math.max(0, selfBounds.left - b.right), x1: b.right, x2: selfBounds.left, y: Math.max(selfBounds.top, b.top) + yOverlap / 2 },
            { gap: Math.max(0, b.left - selfBounds.right), x1: selfBounds.right, x2: b.left, y: Math.max(selfBounds.top, b.top) + yOverlap / 2 }
          ];
          for (const g of gaps) {
            if (g.gap > 0 && (!bestHX || g.gap < bestHX.gap)) bestHX = { gap: g.gap, y: g.y, x1: g.x1, x2: g.x2 };
          }
        }
        // Vertical gap (top/bottom)
        const xOverlap = Math.min(selfBounds.right, b.right) - Math.max(selfBounds.left, b.left);
        if (xOverlap > 0) {
          const gaps = [
            { gap: Math.max(0, selfBounds.top - b.bottom), y1: b.bottom, y2: selfBounds.top, x: Math.max(selfBounds.left, b.left) + xOverlap / 2 },
            { gap: Math.max(0, b.top - selfBounds.bottom), y1: selfBounds.bottom, y2: b.top, x: Math.max(selfBounds.left, b.left) + xOverlap / 2 }
          ];
          for (const g of gaps) {
            if (g.gap > 0 && (!bestVY || g.gap < bestVY.gap)) bestVY = { gap: g.gap, x: g.x, y1: g.y1, y2: g.y2 };
          }
        }
      }
    } catch (_) { }
    const overlays: Array<{ x1: number; y1: number; x2: number; y2: number; label: string }> = [];
    if (bestHX) overlays.push({ x1: bestHX.x1, y1: bestHX.y, x2: bestHX.x2, y2: bestHX.y, label: formatInches(bestHX.gap) });
    if (bestVY) overlays.push({ x1: bestVY.x, y1: bestVY.y1, x2: bestVY.x, y2: bestVY.y2, label: formatInches(bestVY.gap) });
    return overlays;
  }, [currentDesign.elements, getElementBounds, formatInches]);

  // Keyboard shortcuts for center alignment; removed Alt-based area-select activation
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Don't intercept shortcuts if the user is typing in an input or textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      try {
        if (e.key === 'Escape') {
          setPersistentAreaSelection(null);
          setSelectedItemIds([]);
          setSelectedArtworkId(null);
          setAreaSelectActive(false);
          // Hide guide lines on Escape
          setSampleGuideLines(prev => ({ ...prev, visible: false }));
        }

        // Center align selected element to stage
        const selIdH = selectedArtworkIdRef.current;
        if ((e.key === 'h' || e.key === 'H') && selIdH) {
          const cx = Math.floor(stageSize.width / 2);
          updateDesign(prev => {
            const idx = prev.elements.findIndex(x => x.id === selIdH);
            if (idx === -1) return prev;
            const el = prev.elements[idx];
            const newX = cx - el.width / 2;
            const next = prev.elements.slice();
            next[idx] = { ...el, x: newX };
            return { ...prev, elements: next };
          });
        }
        const selIdV = selectedArtworkIdRef.current;
        if ((e.key === 'v' || e.key === 'V') && selIdV) {
          const cy = Math.floor(stageSize.height / 2);
          updateDesign(prev => {
            const idx = prev.elements.findIndex(x => x.id === selIdV);
            if (idx === -1) return prev;
            const el = prev.elements[idx];
            const newY = cy - el.height / 2;
            const next = prev.elements.slice();
            next[idx] = { ...el, y: newY };
            return { ...prev, elements: next };
          });
        }
      } catch (_) { }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      try {
        // Alt-based area selection disabled; ensure mode is not left active
        if (!persistentAreaSelectionRef.current?.active) setAreaSelectActive(false);
      } catch (_) { }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [stageSize.width, stageSize.height, updateDesign]);

  const [isResizing, setIsResizing] = useState(false);
  const [selectedArtworkId, setSelectedArtworkId] = useState<string | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [persistentAreaSelection, setPersistentAreaSelection] = useState<{
    active: boolean;
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const persistentAreaSelectionRef = useRef(persistentAreaSelection);
  useEffect(() => { persistentAreaSelectionRef.current = persistentAreaSelection; }, [persistentAreaSelection]);

  // Sync local selection to global context for UI visibility control
  useEffect(() => {
    try {
      if (setSelectedElement) {
        setSelectedElement(selectedArtworkId || (selectedItemIds.length > 0 ? selectedItemIds[0] : null));
      }
    } catch { }
  }, [selectedArtworkId, selectedItemIds, setSelectedElement]);

  // Alignment functions
  const measureTextWidth = useCallback((el: any) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return Math.max(1, Number(el.width || 0) || 800);
      const props = el?.properties || {};
      const size = Math.max(1, Number(props.fontSize || 48));
      const family = String(props.fontFamily || 'Times New Roman');
      ctx.font = `${size}px ${family}`;
      const lines = String(props.content || '').split('\n');
      let maxWidth = 1;
      for (const line of lines) {
        const w = ctx.measureText(line).width;
        if (w > maxWidth) maxWidth = w;
      }
      return Math.max(1, Math.floor(maxWidth));
    } catch {
      return Math.max(1, Number(el.width || 0) || 800);
    }
  }, []);

  const handleAlignment = useCallback((alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    const itemsToAlign = selectedItemIds.length > 0 ? selectedItemIds : (selectedArtworkId ? [selectedArtworkId] : []);
    if (itemsToAlign.length === 0) return;

    const elements = currentDesign.elements.filter(el => itemsToAlign.includes(el.id) && !el.properties?.isFixedPosition && !el.properties?.isInnerArtwork);
    if (elements.length === 0) return;

    // Batch alignment updates into the next frame and force a layer redraw to avoid flicker
    requestAnimationFrame(() => {
      updateDesign(prev => {
        const next = prev.elements.slice();
        const cp = compositeProps as any;
        const preview = cp?.preview;
        const tplRef = graniteTemplate as any;
        const stoneLeft = preview ? preview.x : (tplRef ? (tplRef.x || 0) : null);
        const stoneTop = preview ? preview.y : (tplRef ? (tplRef.y || 0) : null);
        const stoneRight = preview ? preview.x + preview.width : (tplRef ? (tplRef.x || 0) + (tplRef.width || 0) : null);
        const stoneBottom = preview ? preview.y + preview.height : (tplRef ? (tplRef.y || 0) + (tplRef.height || 0) : null);
        if (stoneLeft === null || stoneTop === null || stoneRight === null || stoneBottom === null) return prev;
        const boundsLeft = stoneLeft;
        const boundsTop = stoneTop;
        const boundsRight = stoneRight;
        const boundsBottom = stoneBottom;
        const boundsWidth = Math.max(0, boundsRight - boundsLeft);
        const boundsHeight = Math.max(0, boundsBottom - boundsTop);

        // Calculate bounds of all selected items
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        let totalWidth = 0, totalHeight = 0;

        elements.forEach(el => {
          minX = Math.min(minX, el.x);
          maxX = Math.max(maxX, el.x + el.width);
          minY = Math.min(minY, el.y);
          maxY = Math.max(maxY, el.y + el.height);
          totalWidth += el.width;
          totalHeight += el.height;
        });

        const selectionWidth = maxX - minX;
        const selectionHeight = maxY - minY;
        const selectionCenterX = minX + selectionWidth / 2;
        const selectionCenterY = minY + selectionHeight / 2;

        // Helpers to respect curved safe-top
        const interpSafeTop = (x: number) => {
          try {
            const pts = (safeTopCurve as Array<{ x: number; y: number }>) || [];
            if (pts.length < 2) return boundsTop;
            if (x <= pts[0].x) return Math.max(boundsTop, pts[0].y);
            if (x >= pts[pts.length - 1].x) return Math.max(boundsTop, pts[pts.length - 1].y);
            let lo = 0, hi = pts.length - 1;
            while (hi - lo > 1) {
              const mid = (lo + hi) >> 1;
              if (pts[mid].x <= x) lo = mid; else hi = mid;
            }
            const a = pts[lo], b = pts[hi];
            const t = (x - a.x) / Math.max(1e-6, (b.x - a.x));
            return Math.max(boundsTop, a.y + (b.y - a.y) * t);
          } catch { return boundsTop; }
        };
        const curveTopForSpan = (left: number, width: number) => {
          const w = Math.max(0, width);
          const right = left + w;
          const yL = interpSafeTop(left);
          const yR = interpSafeTop(right);
          return Math.max(boundsTop, yL, yR);
        };

        // Calculate new positions for all items
        elements.forEach(el => {
          const idx = next.findIndex(x => x.id === el.id);
          if (idx === -1) return;

          let newX = el.x;
          let newY = el.y;
          let changedX = false;
          let changedY = false;
          const isText = el.type === 'text';
          if (isText && (alignment === 'left' || alignment === 'center' || alignment === 'right')) {
            const props = (el as any).properties || {};
            const configuredBoxWidth = (el as any).boxWidth && (el as any).boxWidth > 0 ? (el as any).boxWidth : (el.width || 800);
            const contentWidth = Math.min(configuredBoxWidth, measureTextWidth(el));
            const deltaForAlign = (function () {
              const a = String(props.align || 'left');
              if (a === 'left') return 0;
              if (a === 'right') return configuredBoxWidth - contentWidth;
              return Math.floor((configuredBoxWidth - contentWidth) / 2);
            })();
            if (alignment === 'left') {
              newX = boundsLeft - deltaForAlign;
              changedX = true;
            } else if (alignment === 'center') {
              const targetCenterX = boundsLeft + Math.floor(boundsWidth / 2);
              newX = targetCenterX - Math.floor(contentWidth / 2) - deltaForAlign;
              changedX = true;
            } else if (alignment === 'right') {
              newX = boundsRight - contentWidth - deltaForAlign;
              changedX = true;
            }
          } else if (alignment === 'left' || alignment === 'center' || alignment === 'right') {
            switch (alignment) {
              case 'left':
                newX = boundsLeft;
                changedX = true;
                break;
              case 'center':
                newX = boundsLeft + Math.max(0, Math.floor((boundsWidth - el.width) / 2));
                changedX = true;
                break;
              case 'right':
                newX = boundsRight - el.width;
                changedX = true;
                break;
            }
          } else {
            // vertical alignments
            if (isText && (alignment === 'top' || alignment === 'middle' || alignment === 'bottom')) {
              const props = (el as any).properties || {};
              const size = Math.max(1, Number(props.fontSize || 48));
              const lh = (typeof props.lineHeight === 'number' ? props.lineHeight : 1);
              const lines = String(props.content || '').split('\n');
              const contentHeight = size * lh * Math.max(1, lines.length);
              switch (alignment) {
                case 'top':
                  {
                    const configuredBoxWidth = (el as any).boxWidth && (el as any).boxWidth > 0 ? (el as any).boxWidth : (el.width || 800);
                    const contentWidth = Math.min(configuredBoxWidth, measureTextWidth(el));
                    const props2 = (el as any).properties || {};
                    const deltaForAlign = (function () {
                      const a = String(props2.align || 'left');
                      if (a === 'left') return 0;
                      if (a === 'right') return configuredBoxWidth - contentWidth;
                      return Math.floor((configuredBoxWidth - contentWidth) / 2);
                    })();
                    const visualLeft = el.x + deltaForAlign;
                    newY = curveTopForSpan(visualLeft, contentWidth);
                  }
                  changedY = true;
                  break;
                case 'middle':
                  newY = boundsTop + Math.max(0, Math.floor((boundsHeight - contentHeight) / 2));
                  changedY = true;
                  break;
                case 'bottom':
                  newY = boundsBottom - contentHeight;
                  changedY = true;
                  break;
              }
            } else {
              const vBounds = el.properties?.visibleBounds;
              const vTop = vBounds ? vBounds.top * el.height : 0;
              const vLeft = vBounds ? vBounds.left * el.width : 0;
              const vWidth = vBounds ? (vBounds.right - vBounds.left) * el.width : el.width;
              const vHeight = vBounds ? (vBounds.bottom - vBounds.top) * el.height : el.height;

              switch (alignment) {
                case 'top':
                  // Seat the VISIBLE top of the artwork against the curved edge
                  const targetVTop = curveTopForSpan(el.x + vLeft, vWidth);
                  newY = targetVTop - vTop;
                  changedY = true;
                  break;
                case 'middle':
                  newY = boundsTop + Math.max(0, Math.floor((boundsHeight - vHeight) / 2)) - vTop;
                  changedY = true;
                  break;
                case 'bottom':
                  newY = boundsBottom - vHeight - vTop;
                  changedY = true;
                  break;
              }
            }
          }
          // Clamp to bounds only for the axis being changed in this operation
          if (changedX && isText && (alignment === 'left' || alignment === 'center' || alignment === 'right')) {
            const props = (el as any).properties || {};
            const configuredBoxWidth = (el as any).boxWidth && (el as any).boxWidth > 0 ? (el as any).boxWidth : (el.width || 800);
            const contentWidth = Math.min(configuredBoxWidth, measureTextWidth(el));
            const deltaForAlign = (function () {
              const a = String(props.align || 'left');
              if (a === 'left') return 0;
              if (a === 'right') return configuredBoxWidth - contentWidth;
              return Math.floor((configuredBoxWidth - contentWidth) / 2);
            })();
            // Work in visual space (visible text)
            let visualLeft = newX + deltaForAlign;
            const minLeft = boundsLeft;
            const maxLeft = boundsRight - contentWidth;
            visualLeft = Math.max(minLeft, Math.min(maxLeft, Math.floor(visualLeft)));
            newX = visualLeft - deltaForAlign;
          } else if (changedY && isText && (alignment === 'top' || alignment === 'middle' || alignment === 'bottom')) {
            const props = (el as any).properties || {};
            const size = Math.max(1, Number(props.fontSize || 48));
            const lh = (typeof props.lineHeight === 'number' ? props.lineHeight : 1);
            const lines = String(props.content || '').split('\n');
            const contentHeight = size * lh * Math.max(1, lines.length);
            let visualTop = newY;
            const minTop = boundsTop;
            const maxTop = boundsBottom - contentHeight;
            visualTop = Math.max(minTop, Math.min(maxTop, Math.floor(visualTop)));
            newY = visualTop;
          } else {
            const vBounds = el.properties?.visibleBounds;
            const vTop = vBounds ? vBounds.top * el.height : 0;
            const vLeft = vBounds ? vBounds.left * el.width : 0;
            const vWidth = vBounds ? (vBounds.right - vBounds.left) * el.width : el.width;
            const vHeight = vBounds ? (vBounds.bottom - vBounds.top) * el.height : el.height;

            if (changedX) {
              const visualLeft = newX + vLeft;
              const minLeft = boundsLeft;
              const maxLeft = boundsRight - vWidth;
              const clampedVLeft = Math.max(minLeft, Math.min(maxLeft, Math.floor(visualLeft)));
              newX = clampedVLeft - vLeft;
            }
            if (changedY) {
              const visualTop = newY + vTop;
              const minTop = boundsTop;
              const maxTop = boundsBottom - vHeight;
              const clampedVTop = Math.max(minTop, Math.min(maxTop, Math.floor(visualTop)));
              newY = clampedVTop - vTop;
            }
          }

          const isHorizontal = (alignment === 'left' || alignment === 'center' || alignment === 'right');
          const nextProps = {
            ...(el.properties || {}),
            isManualPosition: (changedX || changedY) ? true : el.properties?.isManualPosition,
          };
          if (isText && isHorizontal) {
            nextProps.align = alignment;
            nextProps.isManualAlign = true;
          }

          next[idx] = {
            ...el,
            x: newX,
            y: newY,
            properties: nextProps
          };
        });

        return { ...prev, elements: next };
      });
      try {
        requestAnimationFrame(() => {
          try { staticLayerRef.current && staticLayerRef.current.batchDraw(); } catch (_) { }
          try { textLayerRef.current && textLayerRef.current.batchDraw(); } catch (_) { }
          try { activeLayerRef.current && activeLayerRef.current.batchDraw(); } catch (_) { }
        });
      } catch (_) { }
    });
  }, [selectedItemIds, selectedArtworkId, currentDesign.elements, stageSize.width, stageSize.height, compositeProps, graniteTemplate, updateDesign]);
  const selectedArtworkIdRef = useRef<string | null>(null);
  useEffect(() => { selectedArtworkIdRef.current = selectedArtworkId; }, [selectedArtworkId]);
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    artworkId: string;
  }>({ visible: false, x: 0, y: 0, artworkId: '' });
  const [textContextMenu, setTextContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    textId: string;
  }>({ visible: false, x: 0, y: 0, textId: '' });



  // Clear granite image cache when granite image changes
  const [graniteImage, setGraniteImage] = useState<HTMLImageElement | null>(null);
  // Prevent race conditions when switching designs quickly
  const graniteLoadIdRef = useRef<number>(0);
  // Track number of pending image assets (template, background, artwork) while applying a saved design
  const [pendingImageCount, setPendingImageCount] = useState<number>(0);
  // While layout-dependent autosnap/repositioning occurs (e.g., vases), keep the preloader covering the canvas
  const [layoutSettling, setLayoutSettling] = useState<boolean>(false);

  // Function to crop shadow areas from granite images
  const cropShadowAreas = (img: HTMLImageElement): HTMLImageElement => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return img;

    // Calculate crop area to remove shadow borders (typically 10-15% from edges)
    const cropPercent = 0.12; // 12% crop from each side
    const cropX = Math.floor(img.width * cropPercent);
    const cropY = Math.floor(img.height * cropPercent);
    const cropWidth = img.width - (cropX * 2);
    const cropHeight = img.height - (cropY * 2);

    // Set canvas size to cropped dimensions
    canvas.width = cropWidth;
    canvas.height = cropHeight;

    // Disable all shadow effects
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // Draw the cropped portion of the image
    ctx.drawImage(
      img,
      cropX, cropY, cropWidth, cropHeight,  // Source crop area
      0, 0, cropWidth, cropHeight           // Destination area
    );

    // Create new image from cropped canvas
    const croppedImg = new window.Image();
    croppedImg.src = canvas.toDataURL('image/png', 1.0);
    return croppedImg;
  };

  React.useLayoutEffect(() => {
    if (!selectedGraniteImage) {
      graniteLoadIdRef.current += 1; // invalidate any in-flight load
      setGraniteImage(null);
      return;
    }

    const cached = moduleImageCache.get(selectedGraniteImage);
    if (cached) {
      setGraniteImage(cached);
      return;
    }

    const loadId = ++graniteLoadIdRef.current;
    setGraniteImage(null); // Reset before loading new image
    const img = new window.Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      if (loadId !== graniteLoadIdRef.current) return; // stale
      moduleImageCache.set(selectedGraniteImage, img); // cache for future reuse
      setGraniteImage(img);
    };

    img.onerror = () => {
      if (loadId !== graniteLoadIdRef.current) return; // stale
      setGraniteImage(null);
    };

    img.src = selectedGraniteImage;
  }, [selectedGraniteImage]);

  // Professional Safe Zone styles
  const SAFE_ZONE_STROKE = "#2f00ffff"; // Professional Blue
  const SAFE_ZONE_GLOW = "#ff0000ff";

  useEffect(() => {
    const cp = compositeProps as any;
    const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0, width: 0, height: 0 });

    const norm = (n: any) => {
      const v = Number(n || 0);
      return Math.round(v * 100) / 100; // 0.01px precision is more than enough
    };

    const newVal = {
      x: norm(ref.x),
      y: norm(ref.y),
      width: norm(ref.width),
      height: norm(ref.height),
    };
    // Deep equality check to prevent infinite sync loops
    if (
      renderedTemplateOrigin?.x !== newVal.x ||
      renderedTemplateOrigin?.y !== newVal.y ||
      renderedTemplateOrigin?.width !== newVal.width ||
      renderedTemplateOrigin?.height !== newVal.height
    ) {
      setRenderedTemplateOrigin(newVal);
    }
  }, [compositeProps, graniteTemplate, renderedTemplateOrigin, setRenderedTemplateOrigin]);

  // Log 4 corner positions of sample guide lines (pixels)
  useEffect(() => {
    if (!sampleGuideLines.visible) return;

    // Use compositeProps preview if available (visual position), else fallback
    const cp = compositeProps as any;
    const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0 });

    const ox = ref.x || 0;
    const oy = ref.y || 0;

    // Convert guide inches to pixels for absolute coordinate logging
    const ppi = pixelsPerInch > 0 ? pixelsPerInch : 96;
    const h1Px = sampleGuideLines.h1 * ppi;
    const h2Px = sampleGuideLines.h2 * ppi;
    const v1Px = sampleGuideLines.v1 * ppi;
    const v2Px = sampleGuideLines.v2 * ppi;

    const p1 = { x: Math.round(ox + v1Px), y: Math.round(oy + h1Px) }; // Top-Left
    const p2 = { x: Math.round(ox + v2Px), y: Math.round(oy + h1Px) }; // Top-Right
    const p3 = { x: Math.round(ox + v2Px), y: Math.round(oy + h2Px) }; // Bottom-Right
    const p4 = { x: Math.round(ox + v1Px), y: Math.round(oy + h2Px) }; // Bottom-Left


  }, [sampleGuideLines, graniteTemplate, compositeProps, pixelsPerInch]);

  // Fetch placement slots when model/size changes
  useEffect(() => {
    const modelId = selectedGraniteMeta?.modelId;
    const sizeId = selectedGraniteMeta?.sizeId;
    if (modelId && sizeId) {
      const fetchSlots = async () => {
        const cacheKey = `placement_slots_${modelId}_${sizeId}`;
        if (designerApiCacheRef.current[cacheKey]) {
          setPlacementSlots(designerApiCacheRef.current[cacheKey]);
          return;
        }

        try {
          const res = await fetch(buildApiUrl(`/api/designs/get-slots?model_id=${modelId}&size_id=${sizeId}`));
          if (res.ok) {
            const data = await res.json();
            if (data.ok) {
              const rawSlots: any[] = data.slots || [];

              const slotMap = new Map<string, any>();
              for (const slot of rawSlots) {
                const key = `${slot.slot_type}__${slot.placement_code ?? ''}`;
                const existing = slotMap.get(key);
                if (!existing) {
                  slotMap.set(key, slot);
                } else {
                  // Keep whichever was updated more recently
                  const existingTs = new Date(existing.updated_at || existing.created_at || 0).getTime();
                  const slotTs = new Date(slot.updated_at || slot.created_at || 0).getTime();
                  if (slotTs > existingTs) slotMap.set(key, slot);
                }
              }
              const slots = Array.from(slotMap.values());

              setPlacementSlots(slots);
              designerApiCacheRef.current[cacheKey] = slots;
            }
          }
        } catch (e) {
        }
      };

      const timer = setTimeout(() => {
        fetchSlots();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setPlacementSlots([]);
    }
  }, [selectedGraniteMeta?.modelId, selectedGraniteMeta?.sizeId, setPlacementSlots]);

  // Safe zone scanning and clamping logic
  const safeScanRef = useRef<{
    gx: number; gy: number;
    width: number; height: number;
    topY: number[]; bottomY: number[]; // indexed by local x
    leftX: number[]; rightX: number[]; // indexed by local y
  } | null>(null);

  const activeSafeZonePoints = useMemo(() => {
    if (!contractOutlinePts || contractOutlinePts.length < 6) return null;
    const pref = (compositeProps && (compositeProps as any).preview) ? (compositeProps as any).preview : null;
    const haveGran = Boolean(graniteTemplate && typeof graniteTemplate.x === 'number');

    if (pref && haveGran) {
      const gtX = Number(graniteTemplate!.x || 0);
      const gtY = Number(graniteTemplate!.y || 0);
      const gtW = Math.max(1, Number(graniteTemplate!.width || 1));
      const gtH = Math.max(1, Number(graniteTemplate!.height || 1));
      const sx = Math.max(0, Number(pref.width || 0)) / gtW;
      const sy = Math.max(0, Number(pref.height || 0)) / gtH;
      const dx = Number(pref.x || 0);
      const dy = Number(pref.y || 0);

      // Transform points from template-local to stage space
      const pts = [];
      for (let i = 0; i < contractOutlinePts.length; i += 2) {
        const lx = contractOutlinePts[i];
        const ly = contractOutlinePts[i + 1];
        // transform: stage = offset + (local - gt) * scale
        pts.push(dx + (lx - gtX) * sx);
        pts.push(dy + (ly - gtY) * sy);
      }
      return pts;
    }

    return contractOutlinePts;
  }, [contractOutlinePts, compositeProps, graniteTemplate]);

  const clampCenterToLayout = useCallback((
    centerX: number,
    centerY: number,
    width: number,
    height: number,
    visibleBounds?: { left: number, top: number, right: number, bottom: number }
  ) => {
    if (!layoutGuides) return { x: centerX, y: centerY };

    // If visibleBounds is provided (relative 0-1), compute the absolute bounding box of the visible parts
    const leftOffset = visibleBounds ? visibleBounds.left * width : 0;
    const topOffset = visibleBounds ? visibleBounds.top * height : 0;
    const rightOffset = visibleBounds ? (1 - visibleBounds.right) * width : 0;
    const bottomOffset = visibleBounds ? (1 - visibleBounds.bottom) * height : 0;

    const visibleWidth = visibleBounds ? (visibleBounds.right - visibleBounds.left) * width : width;
    const visibleHeight = visibleBounds ? (visibleBounds.bottom - visibleBounds.top) * height : height;

    const vHalfW = visibleWidth / 2;
    const vHalfH = visibleHeight / 2;

    // The "visible center" relative to the top-left of the original box
    const vCenterXRel = leftOffset + vHalfW;
    const vCenterYRel = topOffset + vHalfH;

    // The current stage position of the visible center
    const vCenterX = (centerX - width / 2) + vCenterXRel;
    const vCenterY = (centerY - height / 2) + vCenterYRel;

    // Helper: geometric distance squared
    const distSq = (x1: number, y1: number, x2: number, y2: number) => (x1 - x2) ** 2 + (y1 - y2) ** 2;

    // Helper: closest point on segment
    const getClosestOnSegment = (p: { x: number, y: number }, p1: { x: number, y: number }, p2: { x: number, y: number }) => {
      const x = p1.x, y = p1.y;
      const dx = p2.x - x, dy = p2.y - y;
      const dot = dx * dx + dy * dy;
      if (dot === 0) return p1;
      const t = ((p.x - x) * dx + (p.y - y) * dy) / dot;
      if (t < 0) return p1;
      if (t > 1) return p2;
      return { x: x + t * dx, y: y + t * dy };
    };

    // Helper: is point in polygon (ray casting)
    const isInsidePoly = (pt: { x: number, y: number }, poly: number[]) => {
      const x = pt.x, y = pt.y;
      let inside = false;
      for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
        const xi = poly[i], yi = poly[i + 1];
        const xj = poly[j], yj = poly[j + 1];
        const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    };

    // 1. Try Polygon Clamping (Strict - All corners inside)
    if (activeSafeZonePoints && activeSafeZonePoints.length >= 6) {
      const poly = activeSafeZonePoints;

      let curVCX = vCenterX;
      let curVCY = vCenterY;

      // Ensure visible center is inside
      if (!isInsidePoly({ x: curVCX, y: curVCY }, poly)) {
        let minDist = Infinity;
        let bestX = curVCX, bestY = curVCY;
        for (let i = 0; i < poly.length; i += 2) {
          const x1 = poly[i], y1 = poly[i + 1];
          const x2 = poly[(i + 2) % poly.length], y2 = poly[(i + 3) % poly.length];
          const proj = getClosestOnSegment({ x: curVCX, y: curVCY }, { x: x1, y: y1 }, { x: x2, y: y2 });
          const d = distSq(curVCX, curVCY, proj.x, proj.y);
          if (d < minDist) {
            minDist = d;
            bestX = proj.x;
            bestY = proj.y;
          }
        }
        curVCX = bestX;
        curVCY = bestY;
      }

      // Point pushing (Ensuring visible center and midpoints of visible bounds are inside)
      for (let pass = 0; pass < 4; pass++) {
        const points = [
          { x: curVCX, y: curVCY },           // Center
          { x: curVCX, y: curVCY - vHalfH }, // Top Center
          { x: curVCX, y: curVCY + vHalfH }, // Bottom Center
          { x: curVCX - vHalfW, y: curVCY }, // Left Center
          { x: curVCX + vHalfW, y: curVCY }  // Right Center
        ];

        let shiftX = 0;
        let shiftY = 0;
        let outCount = 0;

        for (const p of points) {
          if (!isInsidePoly(p, poly)) {
            outCount++;
            let pMinDist = Infinity;
            let pProj = p;
            for (let j = 0; j < poly.length; j += 2) {
              const x1 = poly[j], y1 = poly[j + 1];
              const x2 = poly[(j + 2) % poly.length], y2 = poly[(j + 3) % poly.length];
              const proj = getClosestOnSegment(p, { x: x1, y: y1 }, { x: x2, y: y2 });
              const d = distSq(p.x, p.y, proj.x, proj.y);
              if (d < pMinDist) {
                pMinDist = d;
                pProj = proj;
              }
            }
            shiftX += (pProj.x - p.x);
            shiftY += (pProj.y - p.y);
          }
        }

        if (outCount === 0) break;
        curVCX += shiftX / outCount;
        curVCY += shiftY / outCount;
      }

      // Map clamped visible center BACK to the original full-box center
      const finalCenterX = (curVCX - vCenterXRel) + width / 2;
      const finalCenterY = (curVCY - vCenterYRel) + height / 2;
      return { x: finalCenterX, y: finalCenterY };
    }

    // 2. Fallback: Rectangular Clamping
    const minVCX = layoutGuides.left + 2; // small margin
    const maxVCX = layoutGuides.right - 2;
    const minVCY = layoutGuides.top + 2;
    const maxVCY = layoutGuides.bottom - 2;

    // We only strictly clamp the visible center point for the fallback to be as forgiving as possible
    const clampedVCX = Math.max(minVCX, Math.min(maxVCX, vCenterX));
    const clampedVCY = Math.max(minVCY, Math.min(maxVCY, vCenterY));

    const finalCenterX = (clampedVCX - vCenterXRel) + width / 2;
    const finalCenterY = (clampedVCY - vCenterYRel) + height / 2;

    return { x: finalCenterX, y: finalCenterY };
  }, [layoutGuides, safeTopCurve, compositeProps, graniteTemplate, activeSafeZonePoints]);

  // Removed automatic repositioning to prevent stacking/centering bugs.
  useEffect(() => { }, []);



  const isOutsideSafeZone = useCallback((rect: { x: number; y: number; width: number; height: number, visibleBounds?: { left: number, top: number, right: number, bottom: number } }) => {
    try {
      const cx = rect.x + rect.width / 2;
      const cy = rect.y + rect.height / 2;
      const clamped = clampCenterToLayout(cx, cy, rect.width, rect.height, rect.visibleBounds);
      return Math.abs(clamped.x - cx) > 1 || Math.abs(clamped.y - cy) > 1;
    } catch { return false; }
  }, [clampCenterToLayout]);

  // Clamp a center point so the element's bounds stay within the granite/template bounds (for vases)
  const clampCenterToGranite = useCallback((
    centerX: number,
    centerY: number,
    width: number,
    height: number
  ) => {
    if (!graniteTemplate) return { x: centerX, y: centerY };
    const halfW = Math.max(0, Math.floor(width / 2));
    const halfH = Math.max(0, Math.floor(height / 2));
    const cp = compositeProps as any;
    const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0, width: 0, height: 0 });
    const ox = ref.x || 0;
    const oy = ref.y || 0;
    const ow = ref.width || (graniteTemplate?.width || 0);
    const oh = ref.height || (graniteTemplate?.height || 0);

    const minX = ox + halfW;
    const maxX = ox + ow - halfW;
    const minY = oy + halfH;
    const maxY = oy + oh - halfH;
    const clampedX = Math.max(minX, Math.min(maxX, centerX));
    const clampedY = Math.max(minY, Math.min(maxY, centerY));
    return { x: clampedX, y: clampedY };
  }, [graniteTemplate, compositeProps]);

  const clampFixedEsqElementToOwnBounds = useCallback((el: any) => {
    if (designMode !== 'ESQ' || !el?.properties?.isFixedPosition) return { x: Number(el?.x || 0), y: Number(el?.y || 0) };
    const bounds = el?.properties?.esqBounds;
    if (!bounds) return { x: Number(el?.x || 0), y: Number(el?.y || 0) };

    const ppi = Number(pixelsPerInch) || 0;
    if (!ppi || !graniteTemplate) return { x: Number(el?.x || 0), y: Number(el?.y || 0) };

    const { left, right, top, bottom, insetX, insetTop, insetBottom } = computeEsqBoundsPixelFrame({
      bounds,
      compositeProps,
      graniteTemplate,
      graniteConfig: graniteConfig || {},
      pixelsPerInch: ppi,
    });

    if (!Number.isFinite(left) || !Number.isFinite(right) || !Number.isFinite(top) || !Number.isFinite(bottom)) {
      return { x: Number(el?.x || 0), y: Number(el?.y || 0) };
    }
    if (right <= left || bottom <= top) return { x: Number(el?.x || 0), y: Number(el?.y || 0) };

    const fieldLc = String(el?.properties?.fieldName || '').toLowerCase();
    const isEsqNameField = el?.type === 'text' && ['firstname', 'lastname'].includes(fieldLc);
    const vTop = isEsqNameField ? (insetTop + insetBottom) / 2 : insetTop;
    const vBot = isEsqNameField ? (insetTop + insetBottom) / 2 : insetBottom;

    const w = Math.max(1, Number(el.width || 0));
    const h = Math.max(1, Number(el.height || 0));
    // Keep a small inset so glyph anti-aliasing never appears outside guide lines.
    const availableW = Math.max(1, (right - left) - (insetX * 2));
    const availableH = Math.max(1, (bottom - top) - (vTop + vBot));
    const effectiveW = (el?.type === 'text') ? Math.min(w, availableW) : w;
    const effectiveH = (el?.type === 'text') ? Math.min(h, availableH) : h;
    const cx = Number(el.x || 0) + effectiveW / 2;
    const cy = Number(el.y || 0) + effectiveH / 2;
    const minX = left + insetX + effectiveW / 2;
    const maxX = right - insetX - effectiveW / 2;
    const minY = top + vTop + effectiveH / 2;
    const maxY = bottom - vBot - effectiveH / 2;
    const clampedCenterX = minX > maxX ? minX : Math.max(minX, Math.min(maxX, cx));
    const clampedCenterY = minY > maxY ? minY : Math.max(minY, Math.min(maxY, cy));

    if (el?.properties?.role === 'esq_fixed_artwork') {
      const boxW = Math.max(1, right - left);
      const boxH = Math.max(1, bottom - top);
      return { x: left, y: top, width: boxW, height: boxH };
    }

    return { x: clampedCenterX - effectiveW / 2, y: clampedCenterY - effectiveH / 2 };
  }, [designMode, pixelsPerInch, graniteTemplate, graniteConfig, compositeProps]);

  useEffect(() => {
    if (designMode !== 'ESQ') return;
    updateDesign(prev => {
      let changed = false;
      const elements = prev.elements.map(el => {
        if (!el?.properties?.isFixedPosition || !el?.properties?.esqBounds) return el;
        const next = clampFixedEsqElementToOwnBounds(el);
        const xSame = Math.abs(next.x - Number(el.x || 0)) < 0.1;
        const ySame = Math.abs(next.y - Number(el.y || 0)) < 0.1;
        const wSame = next.width === undefined || Math.abs(next.width - Number(el.width || 0)) < 0.1;
        const hSame = next.height === undefined || Math.abs(next.height - Number(el.height || 0)) < 0.1;

        if (xSame && ySame && wSame && hSame) return el;

        changed = true;
        const newEl = { ...el, x: next.x, y: next.y };
        if (next.width !== undefined) newEl.width = next.width;
        if (next.height !== undefined) newEl.height = next.height;
        return newEl;
      });
      return changed ? { ...prev, elements } : prev;
    });
  }, [designMode, clampFixedEsqElementToOwnBounds, updateDesign, currentDesign.elements]);

  // Calculate layout guides based on database configuration
  const calculateLayoutGuides = useCallback(() => {
    if (!graniteTemplate || !graniteConfig?.guides?.[0]?.margins) {
      return null;
    }

    const margins = graniteConfig.guides[0].margins;
    const ppi = pixelsPerInch;
    if (!ppi || ppi <= 0) return null;

    const cp = compositeProps as any;
    const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0, width: 0, height: 0 });
    const ox = ref.x || 0;
    const oy = ref.y || 0;
    const ow = ref.width || (graniteTemplate?.width || 0);
    const oh = ref.height || (graniteTemplate?.height || 0);

    const left = ox + margins.left * ppi;
    const right = ox + ow - margins.right * ppi;
    const top = oy + margins.top * ppi;
    const bottom = oy + oh - margins.bottom * ppi;

    return { left, right, top, bottom };
  }, [graniteTemplate, graniteConfig, pixelsPerInch, compositeProps]);

  // Consolidated layout guides update - handles all granite switching scenarios
  useEffect(() => {

    // Clear guides first to prevent display of stale guidelines
    setLayoutGuides(null);

    if (graniteTemplate && graniteConfig?.guides?.[0]?.margins) {
      // Use a small delay to ensure granite template is fully rendered
      const timer = setTimeout(() => {
        const newLayoutGuides = calculateLayoutGuides();
        if (newLayoutGuides) {
          setLayoutGuides(newLayoutGuides);
        }
      }, 50);
      return () => clearTimeout(timer);
    } else {
    }
  }, [
    graniteTemplate?.id,
    graniteTemplate?.x,
    graniteTemplate?.y,
    graniteTemplate?.width,
    graniteTemplate?.height,
    graniteConfig?.guides,
    pixelsPerInch,
    calculateLayoutGuides
  ]);

  React.useEffect(() => {

  }, [selectedGraniteImage, graniteImage]);

  // Load granite configuration when granite meta changes
  React.useEffect(() => {
    // Always clear config first to prevent stale guidelines
    setGraniteConfig(null);
    setGraniteConfigTimestamp(Date.now());
    setVasePositioningKey(Date.now());

    if (selectedGraniteMeta?.modelId && selectedGraniteMeta?.sizeId && selectedGraniteMeta?.colorId) {
      const timer = setTimeout(() => {
        loadGraniteConfig(selectedGraniteMeta.modelId, selectedGraniteMeta.sizeId, selectedGraniteMeta.colorId);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [selectedGraniteMeta?.modelId, selectedGraniteMeta?.sizeId, selectedGraniteMeta?.colorId, loadGraniteConfig]);


  // Track if we've already repositioned vases after this load
  const vaseRepositionedAfterLoadRef = useRef<boolean>(false);
  const prevIsApplyingDesignRef = useRef<boolean>(false);
  const [needsVaseReposition, setNeedsVaseReposition] = useState<boolean>(false);

  // Mark when a load finishes so we can reposition vases once template/config are ready
  useEffect(() => {
    const wasApplying = prevIsApplyingDesignRef.current;
    prevIsApplyingDesignRef.current = Boolean(isApplyingDesign);
    if (wasApplying && !isApplyingDesign) {
      skipNextVaseAutoPositionRef.current = 0;
      vaseRepositionedAfterLoadRef.current = false;
      setNeedsVaseReposition(true);
    }
  }, [isApplyingDesign]);

  // Also trigger reposition when template/config arrive or change after load
  useEffect(() => {
    if (isApplyingDesign) return;
    if (graniteTemplate && graniteConfig) {
      setNeedsVaseReposition(true);
    }
  }, [
    isApplyingDesign,
    graniteTemplate?.x,
    graniteTemplate?.y,
    graniteTemplate?.width,
    graniteTemplate?.height,
    graniteTemplate?.id,
    graniteConfigTimestamp
  ]);



  // Preloader state for main images
  const [loadingImage, setLoadingImage] = useState(true);
  useEffect(() => {
    try {
      const tplSrcs = (currentDesign.elements || [])
        .filter((el: any) => el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate))
        .map((e: any) => e.properties?.src)
        .filter(Boolean as any);
      if ((typeof isApplyingDesign !== 'undefined' && isApplyingDesign) || (tplSrcs && tplSrcs.length > 0)) {
        setLoadingImage(false);
        return;
      }
    } catch { }

    if (graniteImage || bgUrl) {
      setLoadingImage(false);
    } else {
      setLoadingImage(true);
    }
  }, [graniteImage, bgUrl, isApplyingDesign, currentDesign.elements]);

  const preloadAssetTrackingActiveRef = useRef<boolean>(false);
  const onAssetLoaded = useCallback(() => {
    try { setVisibilityTick(t => t + 1); } catch (e) { }
    if (preloadAssetTrackingActiveRef.current) return;
    setPendingImageCount(c => {
      const next = Math.max(0, c - 1);
      return next;
    });
  }, [setVisibilityTick]);

  useEffect(() => {
    try { (window as any).__designer_onAssetLoaded = onAssetLoaded; } catch (_) { }
    return () => { try { (window as any).__designer_onAssetLoaded = undefined; } catch (_) { } };
  }, [onAssetLoaded]);

  useEffect(() => {
    if (typeof isApplyingDesign === 'undefined') return;
    if (isApplyingDesign) return;
    try { (window as any).__isApplyingDesign = false; } catch { }
  }, [isApplyingDesign]);

  // Failsafe: if applying design persists too long, force-clear
  useEffect(() => {
    if (!(typeof isApplyingDesign !== 'undefined' && isApplyingDesign)) return;
    const t = window.setTimeout(() => {
      try { setIsApplyingDesign && setIsApplyingDesign(false); } catch { }
    }, 2000); // 2s max
    return () => window.clearTimeout(t);
  }, [isApplyingDesign, setIsApplyingDesign]);

  const imageSrcsKey = useMemo(() => {
    try {
      if (!currentDesign) return '';
      const elems = Array.isArray(currentDesign.elements) ? currentDesign.elements : [];
      const sources = elems
        .map((el: any) => ((el.type === 'artwork' || el.type === 'image') ? el.properties?.src : ''))
        .filter(Boolean);
      if ((currentDesign as any)?.background?.image_url) {
        sources.push((currentDesign as any).background.image_url);
      }
      // sort to ensuring stable key regardless of order if that matters (optional)
      return sources.join('|');
    } catch { return ''; }
  }, [currentDesign]);

  useEffect(() => {
    let safety: number | null = null;
    const imgs: HTMLImageElement[] = [];
    let safetyDone = false;

    if (typeof isApplyingDesign !== 'undefined' && isApplyingDesign) {
      preloadAssetTrackingActiveRef.current = true;
      let count = 0;
      const urls: string[] = [];
      try {
        const elems = Array.isArray(currentDesign.elements) ? currentDesign.elements : [];
        elems.forEach((el: any) => {
          const src = el?.properties?.src;
          // Exclude vases from pending image count - they load independently
          const role = String(el?.properties?.role || '').toLowerCase();
          if ((el.type === 'artwork' || el.type === 'image') && typeof src === 'string' && src && role !== 'vase') {
            urls.push(src);
          }
        });
        if ((currentDesign as any)?.background && (currentDesign as any).background.image_url) {
          urls.push(String((currentDesign as any).background.image_url));
        }
      } catch (err) {
        // ignore
      }

      const uniqueUrls = Array.from(new Set(urls.filter(Boolean)));
      count = uniqueUrls.length;

      // Minimum 1 to ensure we show the overlay briefly even for small designs.
      const initial = count;
      setPendingImageCount(initial);

      const dec = () => {
        if (safetyDone) return;
        setPendingImageCount(c => Math.max(0, c - 1));
      };

      uniqueUrls.forEach((u) => {
        try {
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => dec();
          img.onerror = () => dec();
          img.src = u;
          imgs.push(img);
        } catch {
          dec();
        }
      });

      safety = window.setTimeout(() => {
        safetyDone = true;
        setPendingImageCount(0);
      }, 25000);
    }

    return () => {
      if (safety) window.clearTimeout(safety);
      try {
        imgs.forEach((i) => {
          try { (i as any).onload = null; (i as any).onerror = null; } catch { }
        });
      } catch { }
      preloadAssetTrackingActiveRef.current = false;
    };
  }, [isApplyingDesign, currentDesign?.id, imageSrcsKey]);

  useEffect(() => {
    const isInitialEmpty = isApplyingDesign && (currentDesign?.elements?.length || 0) === 0;
    if (pendingImageCount !== 0 || layoutSettling || templatePhaseSettling || compositePending > 0 || isInitialEmpty) return;
    let cancelled = false;
    const cleanupFns: Array<() => void> = [];

    const afterAnimation = () => {
      if (cancelled) return;
      try {
        const s: any = stageRef && (stageRef as any).current;
        if (s && !isTransformingRef.current && typeof s.batchDraw === 'function') s.batchDraw();
      } catch { }
      const t = window.setTimeout(() => {
        if (cancelled) return;
        try { if (setIsApplyingDesign) setIsApplyingDesign(false); } catch { }
      }, 100);
      cleanupFns.push(() => window.clearTimeout(t));
    };

    const r1 = window.requestAnimationFrame(() => {
      const r2 = window.requestAnimationFrame(afterAnimation);
      cleanupFns.push(() => window.cancelAnimationFrame(r2));
    });
    cleanupFns.push(() => window.cancelAnimationFrame(r1));

    return () => { cancelled = true; cleanupFns.forEach(fn => fn()); };
  }, [pendingImageCount, layoutSettling, setIsApplyingDesign, isApplyingDesign, currentDesign]);

  const [templatePhaseSettling, setTemplatePhaseSettling] = useState<boolean>(false);
  const templateSrcsKey = React.useMemo(() => {
    try {
      const templateSrcs = (currentDesign.elements || [])
        .filter((el: any) => el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate))
        .map((e: any) => String(e.properties?.src || ''))
        .filter(Boolean);
      return templateSrcs.join('|');
    } catch { return ''; }
  }, [currentDesign.elements]);

  useLayoutEffect(() => {
    let raf1: number | null = null;
    let raf2: number | null = null;
    let to: number | null = null;
    if (templateSrcsKey) {
      setTemplatePhaseSettling(true);
      // If composite images (preview/base) aren't ready yet, keep settling until they are
      if (compositePending > 0) {
        // Wait for next render cycles; effect will re-run when compositePending changes
        return () => { };
      }
      raf1 = window.requestAnimationFrame(() => {
        raf2 = window.requestAnimationFrame(() => {
          to = window.setTimeout(() => {
            setTemplatePhaseSettling(false);
          }, 250);
        });
      });
    }
    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      if (to) window.clearTimeout(to);
    };
  }, [templateSrcsKey, compositePending]);

  // Debug render-time preloader state

  // Lock layout settling as soon as a saved design starts applying to avoid any intermediate reveals
  useEffect(() => {
    if (typeof isApplyingDesign !== 'undefined' && isApplyingDesign) {
      try { setLayoutSettling(true); } catch { }
    }
  }, [isApplyingDesign]);

  const [showRuler, setShowRuler] = useState<boolean>(() => {
    try { return localStorage.getItem('design_tool.ruler') === 'on'; } catch { return false; }
  });


  const rulerTopRef = useRef<HTMLCanvasElement | null>(null);
  const rulerLeftRef = useRef<HTMLCanvasElement | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const mousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [textEditor, setTextEditor] = useState<{
    fontFamily: string;
    fontSize: number;
    lineHeight: number;
    letterSpacing: number;
    /** When set, inline editor matches ESQ text slot (screen px). */
    slot?: { widthPx: number; heightPx: number };
  } | null>(null);
  const [textEditorPos, setTextEditorPos] = useState<{ left: number; top: number } | null>(null);
  const dragStateRef = useRef<{ active: boolean; dx: number; dy: number } | null>(null);
  const textEditorRef = useRef<HTMLDivElement | null>(null);
  const isDraggingTextRef = useRef<boolean>(false);
  const guidesEmptyRef = useRef<boolean>(true);
  const [imgError, setImgError] = useState(false);
  const [graniteNotAvailable, setGraniteNotAvailable] = useState(false);
  const [showGraniteArtRestriction, setShowGraniteArtRestriction] = useState(false);
  const [restrictionMsg, setRestrictionMsg] = useState('');
  const [resetToGranite, setResetToGranite] = useState<string | null>(null);
  const [suppressDefaultGranite, setSuppressDefaultGranite] = useState(false);
  const [templateRefreshKey, setTemplateRefreshKey] = useState(0);

  // When the tab becomes visible or window regains focus, force redraw and bump a tick
  useEffect(() => {
    const onResume = () => {
      try { setSuppressDefaultGranite(false); } catch { }
      try { setLayoutSettling(false); } catch { }
      try { setPendingImageCount(0); } catch { }
      try { if (setIsApplyingDesign) setIsApplyingDesign(false); } catch { }
      try {
        const s: any = stageRef && (stageRef as any).current;
        if (s && typeof s.batchDraw === 'function') s.batchDraw();
      } catch { }
      [templateLayerRef, staticLayerRef, textLayerRef, guidesLayerRef, backgroundLayerRef].forEach((r: any) => {
        try {
          const l = r && r.current;
          if (!l) return;
          try { if (typeof l.isCached === 'function' && l.isCached()) l.clearCache(); } catch { }
          if (typeof l.draw === 'function') l.draw();
        } catch { }
      });
    };
    const onVis = () => { if (document.visibilityState === 'visible') onResume(); };
    const onPageShow = () => onResume();
    window.addEventListener('focus', onResume);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('focus', onResume);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  useEffect(() => {
    // If there are no template sources to wait for, clear suppression immediately
    const srcs = (currentDesign.elements || [])
      .filter((el: any) => el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate))
      .map((e: any) => e.properties?.src)
      .filter(Boolean as any);
    if (!srcs || srcs.length === 0) {
      setSuppressDefaultGranite(false);
      return;
    }

    let cancelled = false;
    setSuppressDefaultGranite(true);

    let remaining = srcs.length;
    const imgs: HTMLImageElement[] = [];

    const finish = () => {
      if (cancelled) return;
      setSuppressDefaultGranite(false);
    };

    const safety = window.setTimeout(() => {
      finish();
    }, 800);

    srcs.forEach((s: string) => {
      try {
        if (moduleImageCache.has(s)) {
          remaining -= 1;
          if (remaining <= 0) finish();
          return;
        }

        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          moduleImageCache.set(s, img); // prime cache for future loads
          remaining -= 1;
          if (remaining <= 0) finish();
        };
        img.onerror = () => {
          remaining -= 1;
          if (remaining <= 0) finish();
        };
        img.src = s;
        imgs.push(img);
      } catch (err) {
        remaining -= 1;
        if (remaining <= 0) finish();

      }
    });

    return () => {
      cancelled = true;
      window.clearTimeout(safety);
      imgs.forEach(i => {
        try { i.onload = null; i.onerror = null; } catch { };
      });
    };
    // Depend on templateResetKey and currentDesign changes so suppression triggers appropriately
  }, [templateResetKey, currentDesign?.id, currentDesign.elements.length]);

  const hasTemplateOrShape = useMemo(() => {
    return currentDesign.elements.some((el: any) =>
      el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate)
    );
  }, [currentDesign.elements]);

  const shouldShowDefaultGranite = useMemo(() => {
    // In ESQ mode, we WANT to show the default granite background layers
    if (designMode !== 'ESQ' && hasTemplateOrShape) return false;
    if (selectedGraniteImage && !suppressDefaultGranite) {
      if (!selectedGraniteMeta || isResizing) return true;
    }
    return false;
  }, [designMode, hasTemplateOrShape, selectedGraniteImage, suppressDefaultGranite, selectedGraniteMeta, isResizing]);

  // Compute granite image props for rendering
  const graniteImageProps = useMemo(() => {
    if (!shouldShowDefaultGranite || !selectedGraniteImage || !graniteImage) return null;

    const stageWidth = Math.max(1, computeStageWidth());
    const stageHeight = Math.max(1, computeStageHeight());
    const availWidth = Math.max(1, stageWidth - granitePadding * 2);
    const availHeight = Math.max(1, stageHeight - granitePadding * 2);

    const natW = Math.max(1, Math.floor(graniteImage.naturalWidth || graniteImage.width || 1));
    const natH = Math.max(1, Math.floor(graniteImage.naturalHeight || graniteImage.height || 1));
    const scale = Math.min(availWidth / natW, availHeight / natH);
    const displayWidth = Math.floor(natW * scale);
    const displayHeight = Math.floor(natH * scale);

    const x = Math.floor((stageWidth - displayWidth) / 2);
    const y = Math.floor((stageHeight - displayHeight) / 2);

    return {
      image: graniteImage,
      x,
      y,
      width: displayWidth,
      height: displayHeight
    };
  }, [shouldShowDefaultGranite, selectedGraniteImage, graniteImage, computeStageWidth, computeStageHeight, granitePadding]);

  // Consolidated model dimensions for accurate target scaling on mobile
  const mainModelSize = useMemo(() => {
    // 1. Composite (Die + Base) - This is the most complete representation
    if (compositeProps) {
      const p = compositeProps.preview;
      const b = compositeProps.base;
      const x1 = Math.min(p.x, b.x);
      const y1 = Math.min(p.y, b.y);
      const x2 = Math.max(p.x + p.width, b.x + b.width);
      const y2 = Math.max(p.y + p.height, b.y + b.height);
      return { width: x2 - x1, height: y2 - y1 };
    }
    // 2. Granite Image (Old/Fallback mechanism)
    if (graniteImageProps) {
      return { width: graniteImageProps.width, height: graniteImageProps.height };
    }
    // 3. Template element fallback
    if (graniteTemplate) {
      return { width: graniteTemplate.width, height: graniteTemplate.height };
    }
    return null;
  }, [compositeProps, graniteImageProps, graniteTemplate]);

  // Register stageRef with context
  useEffect(() => {
    setStageRef(stageRef);
  }, [setStageRef]);

  // Delete text function
  const deleteText = useCallback((textId: string) => {
    // Batch the update to prevent flickering
    requestAnimationFrame(() => {
      updateDesign(prev => ({
        ...prev,
        elements: prev.elements.filter(el => el.id !== textId)
      }));
      setEditingTextId(null);
      setTextEditor(null);
      setTextEditorPos(null);
    });
  }, [updateDesign]);

  // Close text editor when clicking anywhere outside of it
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!editingTextId) return;
      const panel = textEditorRef.current;
      if (panel && panel.contains(e.target as Node)) return;

      if (isDraggingTextRef.current) return;

      const targetEl = e.target as Element;
      if (targetEl && targetEl.closest('[data-text-delete-overlay="true"]')) {
        return;
      }
      const target = e.target as Element;
      if (target && (target.closest('.konvajs-content') || target.closest('canvas'))) {
        return;
      }

      setEditingTextId(null);
      setTextEditor(null);
      setTextEditorPos(null);
    };
    if (editingTextId) document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [editingTextId]);

  // Handle keyboard shortcuts for text editing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept keys when user is typing in the textarea
      if (!editingTextId) return;

      // Check if the active element is the textarea - if so, don't intercept
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'TEXTAREA') {
        return; // Let the textarea handle all keyboard input
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        setEditingTextId(null);
        setTextEditor(null);
        setTextEditorPos(null);
      }
    };

    if (editingTextId) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [editingTextId, deleteText]);

  // Simplified template elements management with stable reference when unchanged
  const templateElements = useMemo(() => {
    const onlyTemplates = currentDesign.elements.filter(el =>
      el.type === 'artwork' &&
      el.properties?.role === 'template'
    );
    return onlyTemplates;
  }, [currentDesign.elements]);

  // Simple template node component
  const TemplateNode: React.FC<{ el: DesignElement }> = React.memo(({ el }) => {
    const [img, setImg] = useState<HTMLImageElement | null>(null);
    const nodeRef = useRef<any>(null);

    useEffect(() => {
      const src = el.properties?.src;
      if (!src) {
        setImg(null);
        return;
      }

      // Clear existing image to force reload
      setImg(null);

      const image = new window.Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        setImg(image);
        try { if ((window as any).__designer_onAssetLoaded) (window as any).__designer_onAssetLoaded(); } catch (_) { }
      };
      image.onerror = () => {
        setImg(null);
        try { if ((window as any).__designer_onAssetLoaded) (window as any).__designer_onAssetLoaded(); } catch (_) { }
      };
      try {
        image.src = src as string;
      } catch {
        image.src = src as string;
      }
    }, [el.properties?.src]);

    // Cache template image node when ready to reduce redraw flicker
    useEffect(() => {
      const node = nodeRef.current;
      try {
        if (node) {
          const w = node.width();
          const h = node.height();
          if (w > 0 && h > 0) {
            if (node.isCached()) {
              node.clearCache();
            }
            node.cache({ pixelRatio: window.devicePixelRatio || 1 });
            const layer = node.getLayer();
            if (layer) layer.batchDraw();
          }
        }
      } catch (_e) { }
    }, [img, el.width, el.height]);

    if (!img) return null;

    return (
      <Image
        ref={nodeRef}
        image={img}
        x={el.x}
        y={el.y}
        width={el.width}
        height={el.height}
        rotation={el.rotation}
        listening={false}
        draggable={false}
        perfectDrawEnabled={false}
        shadowEnabled={false}
        shadowBlur={0}
        shadowColor="transparent"
        shadowOffsetX={0}
        shadowOffsetY={0}
        shadowOpacity={0}
        filters={[]}
        key={`tpl-img-${el.id}`}
      />
    );
  }, (prevProps, nextProps) => {
    const prev = prevProps.el;
    const next = nextProps.el;
    // Always re-render if src changes, and also check for other properties
    return prev.properties?.src === next.properties?.src &&
      prev.x === next.x &&
      prev.y === next.y &&
      prev.width === next.width &&
      prev.height === next.height &&
      prev.rotation === next.rotation;
  });

  // Memoized layer for templates to avoid rerender on unrelated state changes
  const TemplateLayerMemo = useMemo(() => {
    return React.memo(({ elements }: { elements: DesignElement[] }) => (
      <Layer
        ref={templateLayerRef}
        listening={false}
        perfectDrawEnabled={false}
        clearBeforeDraw={true}
        imageSmoothingEnabled={false}
        preventDefault={false}
        shadowEnabled={false}
        shadowBlur={0}
        shadowColor="transparent"
        shadowOffsetX={0}
        shadowOffsetY={0}
        shadowOpacity={0}
      >
        {elements.map(e => (
          <TemplateNode
            key={`${e.id}-${e.properties?.src || ''}-${e.properties?.colorId || ''}`}
            el={e}
          />
        ))}
      </Layer>
    ), (prev, next) => prev.elements === next.elements);
  }, []);

  // Check if template exists
  const hasTemplate = templateElements.length > 0;

  // Update template selection state when elements change
  useEffect(() => {
    setIsTemplateSelected(hasTemplate);
  }, [hasTemplate, setIsTemplateSelected]);


  // Force complete template reset when templateResetKey changes
  useEffect(() => {
    setTemplateRefreshKey(prev => prev + 1);

    // We can still clean up non-React activeLayer artifacts if needed
    requestAnimationFrame(() => {
      const activeLayer = activeLayerRef.current;
      if (activeLayer) {
        const children = activeLayer.getChildren();
        children.forEach((child: any) => {
          if (child.getAttr('id') && child.getAttr('id').includes('template')) {
            child.destroy();
          }
        });
        activeLayer.draw();
      }
    });
  }, [templateResetKey]);

  useEffect(() => {
    if (isTransformingRef.current) return; // suppress while transforming
    const layer = templateLayerRef.current;
    if (!layer) return;

    const layerWidth = layer.width();
    const layerHeight = layer.height();

    const children = layer.getChildren();
    const hasValidChildren = children.some((child: any) => {
      const childWidth = child.width();
      const childHeight = child.height();
      return childWidth > 0 && childHeight > 0;
    });

    // Only cache if layer and children have valid dimensions
    if (layerWidth > 0 && layerHeight > 0 && hasValidChildren) {
      try {
        // Use requestAnimationFrame to ensure smooth caching
        requestAnimationFrame(() => {
          if (layer) {
            if (layer.isCached()) {
              layer.clearCache();
            }
            layer.cache({ pixelRatio: 1 });
            layer.batchDraw();
          }
        });
      } catch (error) {
      }
    }
  }, [templateElements, stageSize.width, stageSize.height]);


  // Force template layer redraw when stage size changes
  useEffect(() => {
    if (isTransformingRef.current) return; // suppress while transforming
    if (stageSize.width > 0 && stageSize.height > 0) {
      const layer = templateLayerRef.current;
      if (layer) {
        const layerWidth = layer.width();
        const layerHeight = layer.height();
        if (layerWidth > 0 && layerHeight > 0) {
          try {
            // Use requestAnimationFrame for smooth redraws
            requestAnimationFrame(() => {
              if (layer) {
                layer.draw();
              }
            });
          } catch (error) {
          }
        }
      }
    }
  }, [stageSize.width, stageSize.height]);

  // Cache grass background layer as well
  useEffect(() => {
    if (isTransformingRef.current) return; // suppress while transforming
    const layer = backgroundLayerRef.current;
    if (!layer) return;

    const layerWidth = layer.width();
    const layerHeight = layer.height();

    if (layerWidth > 0 && layerHeight > 0) {
      try {
        requestAnimationFrame(() => {
          try {
            if (layer && typeof layer.isCached === 'function' && layer.isCached()) {
              try { layer.clearCache(); } catch (_) { }
            }
            // Re-cache the layer to keep drawing fast, then force a draw
            try { layer.cache({ pixelRatio: 1 }); } catch (_) { }
            try { layer.draw(); } catch (_) { }
          } catch (err) {
          }
        });
      } catch (_) { }
    }
  }, [bgUrl, stageSize.width, stageSize.height]);



  const { scale, viewportStyle } = useCanvasScale(
    containerRef,
    DESIGN_WIDTH,
    DESIGN_HEIGHT,
    mainModelSize?.width,
    mainModelSize?.height
  );

  // Respond to toolbar events: apply/clear guide layout
  useEffect(() => {
    const onApply = (e: any) => {
      try {
        const { topIn = 0.75, bottomIn = 0.75, leftIn = 0.75, rightIn = 0.75 } = (e?.detail || {}) as any;
        const ppi = pixelsPerInch;
        if (!ppi || ppi <= 0) return;
        // Determine granite bounds (template element or displayed granite image)
        let gX = 0, gY = 0, gW = computeStageWidth(), gH = computeStageHeight();
        try {
          const tpl = graniteTemplate;
          if (tpl) {
            gX = tpl.x; gY = tpl.y; gW = tpl.width; gH = tpl.height;
          }
        } catch (_) { }
        const toPx = (inch: number) => (ppi > 0 ? inch * ppi : inch * 96); // fallback 96 dpi if unknown
        const topPx = gY + Math.max(0, Math.floor(toPx(topIn)));
        const bottomPx = gY + Math.max(0, Math.floor(gH - toPx(bottomIn)));
        const leftPx = gX + Math.max(0, Math.floor(toPx(leftIn)));
        const rightPx = gX + Math.max(0, Math.floor(gW - toPx(rightIn)));
        setLayoutGuides({ top: topPx, bottom: bottomPx, left: leftPx, right: rightPx });
      } catch (_) { }
    };
    const onClear = () => {
      setLayoutGuides(null);
      setGuides([]);
      setMeasureOverlays([]);
      setPersistentAreaSelection(null);
      setAreaRect({ active: false, x: 0, y: 0, w: 0, h: 0 });
    };
    window.addEventListener('guidelayout:apply', onApply as any);
    window.addEventListener('guidelayout:clear', onClear as any);
    return () => {
      window.removeEventListener('guidelayout:apply', onApply as any);
      window.removeEventListener('guidelayout:clear', onClear as any);
    };
  }, [pixelsPerInch, computeStageWidth, computeStageHeight, graniteTemplate]);



  const fitAndCenter = useCallback((el: DesignElement): DesignElement => {
    const W = stageSize.width || 1200;
    const H = stageSize.height || 600;
    const isTemplate = (el as any)?.properties?.role === 'template' || (el as any)?.properties?.isTemplate === true || String((el as any).id || '').startsWith('template-');

    const naturalWidth = Math.max(1, Math.floor(el.width || 1));
    const naturalHeight = Math.max(1, Math.floor(el.height || 1));

    if (isTemplate) {
      const availW = Math.max(1, W - granitePadding * 2);
      const availH = Math.max(1, H - granitePadding * 2);
      const scale = Math.min(1, Math.min(availW / naturalWidth, availH / naturalHeight));
      const width = Math.max(1, Math.floor(naturalWidth * scale));
      const height = Math.max(1, Math.floor(naturalHeight * scale));
      const x = Math.floor((W - width) / 2);
      const y = Math.floor((H - height) / 2);
      return { ...el, x, y, width, height };
    }

    let bx = 0, by = 0, bw = W, bh = H;
    const templateW = graniteTemplate?.width || 0;
    const templateH = graniteTemplate?.height || 0;

    // Use template or layout guides if they have sane dimensions
    if (templateW > 50 && templateH > 50) {
      bx = graniteTemplate?.x || 0;
      by = graniteTemplate?.y || 0;
      bw = templateW;
      bh = templateH;
    } else if (layoutGuides && (layoutGuides.right - layoutGuides.left > 100)) {
      bx = layoutGuides.left || 0;
      by = layoutGuides.top || 0;
      bw = layoutGuides.right - bx;
      bh = layoutGuides.bottom - by;
    } else {
      bw = 400;
      bh = 400;
      bx = (W - bw) / 2;
      by = (H - bh) / 2;
    }

    // Default insertion factor: 90% of the reference area (effectively "original size" bounded by stage)
    const insertionFactor = 0.9;
    let scale = Math.min(bw / naturalWidth, bh / naturalHeight) * insertionFactor;

    scale = Math.min(1, scale); // Ensure it doesn't exceed 100% natural size or stage bounds
    // Previously capped at 0.3 insertion factor, updated to 0.9 to satisfy "original size" request

    let width = Math.max(10, Math.floor(naturalWidth * scale));
    let height = Math.max(10, Math.floor(naturalHeight * scale));



    const x = el.properties?.fixedPosX !== undefined && el.properties?.fixedPosX !== null
      ? Math.floor(bx + Number(el.properties.fixedPosX))
      : Math.floor(bx + (bw - width) / 2);

    // Special case for SL12 or any with fixedPosY: position relative to top of face
    let y: number;
    if (el.properties?.fixedPosY !== undefined && el.properties?.fixedPosY !== null) {
      y = Math.floor(by + Number(el.properties.fixedPosY));
    } else {
      y = Math.floor(by + (bh - height) / 2);
    }

    return { ...el, x, y, width, height };
  }, [stageSize.width, stageSize.height, layoutGuides, graniteTemplate, granitePadding]);

  useEffect(() => {
    setStageSize({ width: DESIGN_WIDTH, height: DESIGN_HEIGHT });
  }, []);

  // Disable right-click context menu across the designer area (and as a fallback, window)
  useEffect(() => {
    const preventContextMenu = (e: Event) => { e.preventDefault(); };
    const el = containerRef.current;
    window.addEventListener('contextmenu', preventContextMenu);
    if (el) el.addEventListener('contextmenu', preventContextMenu);
    return () => {
      window.removeEventListener('contextmenu', preventContextMenu);
      if (el) el.removeEventListener('contextmenu', preventContextMenu);
    };
  }, []);

  useEffect(() => {
    // When stageSize changes, mark as resizing and clear after a short debounce
    if (stageSize.width || stageSize.height) {
      setIsResizing(true);
      const id = window.setTimeout(() => setIsResizing(false), 160);
      return () => window.clearTimeout(id);
    }
    return;
  }, [stageSize.width, stageSize.height]);

  // Listen for ruler toggle events from toolbar
  useEffect(() => {
    const onToggle = () => {
      try { setShowRuler(localStorage.getItem('design_tool.ruler') === 'on'); } catch { }
    };
    const onToggleGuidelines = () => {
      setShowGuidelineSettings(prev => {
        const next = !prev;
        if (next) {
          setSampleGuideLines(s => ({ ...s, visible: true }));
        }
        return next;
      });
    };
    const onToggleHideUI = (e: any) => {
      const shouldHide = e?.detail?.hide !== undefined ? e.detail.hide : !hideDesignerUI;
      setHideDesignerUI(shouldHide);
      if (shouldHide) {
        // When hiding UI, also deselect all and hide guide lines
        deselectAllRef.current?.();
        setSampleGuideLines(s => ({ ...s, visible: false }));
      }
    };
    window.addEventListener('ruler:toggle', onToggle as any);
    window.addEventListener('guidelines:toggle', onToggleGuidelines as any);
    window.addEventListener('designer:hideui', onToggleHideUI as any);
    return () => {
      window.removeEventListener('ruler:toggle', onToggle as any);
      window.removeEventListener('guidelines:toggle', onToggleGuidelines as any);
      window.removeEventListener('designer:hideui', onToggleHideUI as any);
    };
  }, [hideDesignerUI]);

  // Draw simple rulers (top and left) with 10px minor/50px major ticks
  const drawRulers = useCallback(() => {
    if (!showRuler) return;
    const top = rulerTopRef.current;
    const left = rulerLeftRef.current;
    const stageDesignW = computeStageWidth();
    const stageDesignH = computeStageHeight();
    const containerEl = containerRef.current;
    const containerRect = containerEl ? containerEl.getBoundingClientRect() : null;
    const containerW = Math.max(1, Math.floor(containerRect?.width || stageDesignW));
    const containerH = Math.max(1, Math.floor(containerRect?.height || stageDesignH));
    const s = Math.max(0.0001, Number(scale || 1));
    const stagePxW = stageDesignW * s;
    const stagePxH = stageDesignH * s;
    const stageOffsetX = Math.floor((containerW - stagePxW) / 2);
    const stageOffsetY = Math.floor((containerH - stagePxH) / 2);
    const mouseX = mousePosRef.current.x;
    const mouseY = mousePosRef.current.y;
    const ppi = pixelsPerInch > 0.0001 ? pixelsPerInch : 96;
    const minorIn = 0.25;
    const majorIn = 1;
    const minorPx = minorIn * ppi;
    const majorPx = majorIn * ppi;
    if (top) {
      top.width = containerW; top.height = 24;
      const ctx = top.getContext('2d'); if (!ctx) return;
      ctx.clearRect(0, 0, top.width, top.height);
      ctx.fillStyle = '#f4f6f8'; ctx.fillRect(0, 0, top.width, top.height);
      ctx.strokeStyle = '#cbd5e1';
      const visibleDesignStartX = (0 - stageOffsetX) / s;
      const visibleDesignEndX = (containerW - stageOffsetX) / s;
      const startTickX = Math.floor(visibleDesignStartX / minorPx) * minorPx;
      const endTickX = Math.ceil(visibleDesignEndX / minorPx) * minorPx;
      for (let x = startTickX; x <= endTickX + 0.0001; x += minorPx) {
        const sx = stageOffsetX + x * s;
        if (sx < -50 || sx > containerW + 50) continue;
        const isMajor = Math.abs((x / majorPx) - Math.round(x / majorPx)) < 0.0001;
        const h = isMajor ? 12 : 6;
        ctx.beginPath(); ctx.moveTo(sx + 0.5, 24); ctx.lineTo(sx + 0.5, 24 - h); ctx.stroke();
        if (isMajor) {
          const inchVal = Math.round(x / ppi);
          ctx.fillStyle = '#475569';
          ctx.font = '10px sans-serif';
          ctx.fillText(String(inchVal), sx + 2, 10);
        }
      }
      ctx.strokeStyle = '#2563eb';
      const mouseSX = stageOffsetX + mouseX * s;
      ctx.beginPath(); ctx.moveTo(mouseSX + 0.5, 0); ctx.lineTo(mouseSX + 0.5, 24); ctx.stroke();
    }
    if (left) {
      left.width = 24; left.height = containerH;
      const ctx = left.getContext('2d'); if (!ctx) return;
      ctx.clearRect(0, 0, left.width, left.height);
      ctx.fillStyle = '#f4f6f8'; ctx.fillRect(0, 0, left.width, left.height);
      ctx.strokeStyle = '#cbd5e1';
      const visibleDesignStartY = (0 - stageOffsetY) / s;
      const visibleDesignEndY = (containerH - stageOffsetY) / s;
      const startTickY = Math.floor(visibleDesignStartY / minorPx) * minorPx;
      const endTickY = Math.ceil(visibleDesignEndY / minorPx) * minorPx;
      for (let y = startTickY; y <= endTickY + 0.0001; y += minorPx) {
        const sy = stageOffsetY + y * s;
        if (sy < -50 || sy > containerH + 50) continue;
        const isMajor = Math.abs((y / majorPx) - Math.round(y / majorPx)) < 0.0001;
        const w = isMajor ? 12 : 6;
        ctx.beginPath(); ctx.moveTo(24, sy + 0.5); ctx.lineTo(24 - w, sy + 0.5); ctx.stroke();
        if (isMajor) {
          const inchVal = Math.round(y / ppi);
          ctx.save();
          ctx.fillStyle = '#475569';
          ctx.font = '10px sans-serif';
          ctx.translate(0, sy + 3);
          ctx.rotate(-Math.PI / 2);
          ctx.fillText(String(inchVal), -18, 10);
          ctx.restore();
        }
      }
      ctx.strokeStyle = '#2563eb';
      const mouseSY = stageOffsetY + mouseY * s;
      ctx.beginPath(); ctx.moveTo(0, mouseSY + 0.5); ctx.lineTo(24, mouseSY + 0.5); ctx.stroke();
    }
  }, [showRuler, computeStageWidth, computeStageHeight, scale, pixelsPerInch]);

  useEffect(() => { drawRulers(); }, [drawRulers, stageSize.width, stageSize.height, showRuler]);

  useEffect(() => {
    if (!showRuler) return;
    const el = containerRef.current;
    if (!el) return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try { drawRulers(); } catch { }
      });
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [showRuler, drawRulers]);

  useEffect(() => {
    updateDesign(prev => {
      let changed = false;
      const next = prev.elements.map(e => {
        const props = e.properties || {};
        const isModel = props.isTemplate === true || props.template === true || props.role === 'template' || props.role === 'model' || e.id?.startsWith('template-');
        if (e.type === 'artwork' && isModel) {
          const centered = fitAndCenter(e);
          if (
            centered.x !== e.x ||
            centered.y !== e.y ||
            centered.width !== e.width ||
            centered.height !== e.height
          ) {
            changed = true;
            return centered;
          }
        }
        return e;
      });
      return changed ? { ...prev, elements: next } : prev;
    });
  }, [stageSize.width, stageSize.height, fitAndCenter, updateDesign]);

  // Force template repositioning when stage size changes
  useEffect(() => {
    if (stageSize.width > 0 && stageSize.height > 0) {
      updateDesign(prev => {
        let changed = false;
        const next = prev.elements.map(e => {
          const props = e.properties || {};
          const isTemplate = props.role === 'template' ||
            props.isTemplate === true ||
            props.template === true ||
            e.id === 'preview-image' ||
            e.id.startsWith('template-');

          if (e.type === 'artwork' && isTemplate) {
            const repositioned = fitAndCenter(e);
            if (
              repositioned.x !== e.x ||
              repositioned.y !== e.y ||
              repositioned.width !== e.width ||
              repositioned.height !== e.height
            ) {
              changed = true;
              return repositioned;
            }
          }
          return e;
        });
        return changed ? { ...prev, elements: next } : prev;
      });
    }
  }, [stageSize.width, stageSize.height, fitAndCenter, updateDesign]);

  const ArtworkNode: React.FC<{
    el: DesignElement;
    onRightClick?: (e: any, artworkId: string) => void;
    listening?: boolean;
    perfect?: boolean;
    isSelected?: boolean
  }> = React.memo(({ el, onRightClick, listening = true, perfect = true, isSelected = false }) => {
    const [img, setImg] = useState<HTMLImageElement | null>(() => {
      const initialSrc = el.properties?.vaseSrcOriginal || el.properties?.src;

      if (initialSrc && imageCacheRef.current) {
        return imageCacheRef.current.get(initialSrc) || null;
      }
      return null;
    });
    const [attempt, setAttempt] = useState(0);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const imageRef = useRef<any>(null);
    const lastUpdateRef = useRef<number>(0);
    const lastElementUpdateRef = useRef<string | null>(null);
    const lastLoadedSrcRef = useRef<string | null>(null);
    const elementRef = useRef<DesignElement>(el);

    useEffect(() => {
      elementRef.current = el;
    }, [el]);

    const isElementSelected = useMemo(() => {
      try { return isSelected || selectedArtworkId === el.id; } catch { return !!isSelected; }
    }, [isSelected, selectedArtworkId, el.id]);

    const isTemplate = useMemo(() =>
      el.properties?.role === 'template' || el.properties?.isTemplate === true,
      [el.properties?.role, el.properties?.isTemplate]
    );

    const isGrey = useMemo(() => {
      const activeColorId = Number(selectedGraniteMeta?.colorId || currentDesign?.selectedColorId || 0);
      const activeColorObj = (sharedColors || []).find((c: any) => Number(c.id) === activeColorId);
      const code = String(activeColorObj?.code || '').toUpperCase();
      const name = String(activeColorObj?.name || '').toUpperCase();
      return code === 'MG' || code === 'GREY' || name.includes('GRAY') || name.includes('GREY') || activeColorId === 12;
    }, [selectedGraniteMeta?.colorId, currentDesign?.selectedColorId]);

    // Make artworks draggable by default (unless explicitly disabled) and always allow 'work' items to be draggable
    const draggable = useMemo(() => {
      const role = String(el.properties?.role || el.properties?.kind || '').toLowerCase();
      // Vases and SL12 are positioned specifically and must not be draggable
      if (role === 'vase') return false;

      const isFixed = el.properties?.isFixedPosition === true;
      if (isFixed) return false;

      const isWork = role === 'work';
      return !isTemplate && (isWork || el.properties?.draggable !== false);
    }, [el.properties?.draggable, el.properties?.role, el.properties?.kind, isTemplate, el.properties?.isSL12, el.properties?.isFixedPosition]);

    // For vase elements, prefer the original vase src stored at insertion time so
    // template/granite updates don't accidentally replace the vase image.
    const src = useMemo(() => {
      const props = el.properties || {};
      if (props.vaseSrcOriginal) return props.vaseSrcOriginal;
      return props.src;
    }, [el.properties?.src, el.properties?.vaseSrcOriginal]);

    const displayWidth = el.width;
    const displayHeight = el.height;


    const finalX = el.x;
    const finalY = el.y;
    const clampedFixedPos = useMemo(
      () => clampFixedEsqElementToOwnBounds(el),
      [el, clampFixedEsqElementToOwnBounds]
    );
    const renderX = (designMode === 'ESQ' && el.properties?.isFixedPosition) ? clampedFixedPos.x : finalX;
    const renderY = (designMode === 'ESQ' && el.properties?.isFixedPosition) ? clampedFixedPos.y : finalY;
    const renderWidth = (designMode === 'ESQ' && el.properties?.isFixedPosition && clampedFixedPos.width !== undefined) ? clampedFixedPos.width : (isVase(el) ? displayWidth : (el.width || 0));
    const renderHeight = (designMode === 'ESQ' && el.properties?.isFixedPosition && clampedFixedPos.height !== undefined) ? clampedFixedPos.height : (isVase(el) ? displayHeight : (el.height || 0));


    // Center calculation fallback for elements with no width yet
    const effectiveWidth = (displayWidth || el.width || 0);
    const effectiveHeight = (displayHeight || el.height || 0);
    const centerX = renderX + effectiveWidth / 2;
    const centerY = renderY + effectiveHeight / 2;

    useEffect(() => {
      if (!src) {
        setImg(null);
        return;
      }

      let canceled = false;
      let loadStarted = false;
      let loadCompleted = false;
      const markDone = () => {
        if (loadStarted && !loadCompleted) {
          loadCompleted = true;
          _canvasLoadDone();
        }
      };

      const handleImageReady = (image: HTMLImageElement) => {
        if (canceled) return;
        // Keep component-level cache in sync for legacy callers
        imageCacheRef.current.set(src, image);
        setImg(image);
        // Signal completion to the toolbar progress bar
        markDone();
        if (lastLoadedSrcRef.current !== src) {
          lastLoadedSrcRef.current = src;
          try { if ((window as any).__designer_onAssetLoaded) (window as any).__designer_onAssetLoaded(); } catch (_) { }
          return;
        }
        try { if ((window as any).__designer_onAssetLoaded) (window as any).__designer_onAssetLoaded(); } catch (_) { }
      };

      const moduleCached = moduleImageCache.get(src);
      if (moduleCached) {
        handleImageReady(moduleCached);
        return;
      }

      // Only clear image if not already loaded, avoiding flicker when transitioning sources
      if (!img) {
        setImg(null);
      }

      if (lastLoadedSrcRef.current === src && img) {
        return;
      }

      // Start tracking this load in the toolbar progress bar
      loadStarted = true;
      _canvasLoadStart();

      const existingFetch = moduleImageFetching.get(src);
      if (existingFetch) {
        existingFetch.then(image => {
          if (!canceled && image) {
            handleImageReady(image);
          } else {
            markDone();
          }
        });
        return () => { canceled = true; };
      }

      // No cache, no in-flight — start a fresh fetch.
      const i = new window.Image();
      i.crossOrigin = 'anonymous';

      const assignAndHandle = (finalSrc: string) => {
        i.onload = () => {
          moduleImageCache.set(src, i);
          moduleImageFetching.delete(src);
          if (canceled) return;
          handleImageReady(i);
        };
        i.onerror = () => {
          moduleImageFetching.delete(src);
          if (canceled) return;
          if (attempt < 5) {
            setTimeout(() => setAttempt(a => a + 1), 300);
          }
          markDone();
          try { if ((window as any).__designer_onAssetLoaded) (window as any).__designer_onAssetLoaded(); } catch (_) { }
        };
        i.src = finalSrc;
      };

      const fetchPromise: Promise<HTMLImageElement | null> = (async () => {
        try {
          if (String(src).startsWith('data:') || !String(src).toLowerCase().includes('.svg')) {
            assignAndHandle(src);
            return i;
          }
          const res = await fetch(src, { mode: 'cors' });
          if (!res.ok) throw new Error('bad status');
          const text = await res.text();
          let svgText = text;
          if (!/width=["']\d+/i.test(svgText)) {
            const viewBoxMatch = svgText.match(/viewBox=["']\s*0\s+0\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)/i);
            if (viewBoxMatch) {
              svgText = svgText.replace('<svg', `<svg width="${viewBoxMatch[1]}" height="${viewBoxMatch[2]}"`);
            }
          }
          if (el.properties?.artworkStrokeWidth) {
            const sw = el.properties.artworkStrokeWidth;
            if (!svgText.includes('stroke-width')) {
              svgText = svgText.replace('</svg>', `<style>path,line,polyline,polygon,circle,ellipse{stroke-width:${sw}px !important;}</style></svg>`);
            }
          }
          const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
          assignAndHandle(url);
          return i;
        } catch {
          moduleImageFetching.delete(src);
          const isDataUrl = String(src).startsWith('data:');
          const cacheBusted = (attempt === 0 || isDataUrl) ? src : `${src}${src.includes('?') ? '&' : '?'}cb=${Date.now()}`;
          assignAndHandle(cacheBusted);
          return i;
        }
      })();

      moduleImageFetching.set(src, fetchPromise);

      return () => {
        canceled = true;
        i.onload = null as any;
        i.onerror = null as any;
        markDone();
      };
    }, [src, attempt, updateDesign, el.id, fitAndCenter]);



    useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

    const onDragEnd = useCallback((e: any) => {
      // Reset opacity after drag
      e.target.setAttr('opacity', 1);
      // Clear guides when drag finishes
      setGuides([]);
      setMeasureOverlays([]);
      setSafeZoneHit(false);

      const curW = elementRef.current.width || displayWidth;
      const curH = elementRef.current.height || displayHeight;
      const currentEl = elementRef.current;
      // Do not clamp or restrict position; take the node's current position directly
      const centerX = e.target.x();
      const centerY = e.target.y();

      const now = Date.now();
      const newX = centerX - curW / 2;
      const newY = centerY - curH / 2;
      const newRect = { x: newX, y: newY, width: currentEl.width, height: currentEl.height, id: currentEl.id, properties: currentEl.properties };

      try { setSafeWarnVisible(isOutsideSafeZone({ x: newX, y: newY, width: curW, height: curH, visibleBounds: currentEl.properties?.visibleBounds })); } catch { }
      // Do not prevent overlap; allow free placement
      if ((Math.abs(newX - currentEl.x) > 0.1 || Math.abs(newY - currentEl.y) > 0.1) &&
        (now - lastUpdateRef.current) > 16) {
        lastUpdateRef.current = now;
        // Batch the update to prevent flickering
        requestAnimationFrame(() => {
          updateDesign(prev => {
            const elementIndex = prev.elements.findIndex(x => x.id === currentEl.id);
            if (elementIndex === -1) return prev;
            const currentElement = prev.elements[elementIndex];
            if (Math.abs(currentElement.x - newX) < 0.1 && Math.abs(currentElement.y - newY) < 0.1) {
              return prev;
            }
            const newElements = prev.elements.map((element, index) =>
              index === elementIndex
                ? {
                  ...element,
                  x: newX,
                  y: newY,
                  properties: {
                    ...element.properties,
                    autosnap: false,
                    isManualPosition: true
                  }
                }
                : element
            );
            return { ...prev, elements: newElements };
          });
        });
      }
    }, [updateDesign, currentDesign.elements, displayWidth, displayHeight]);


    const onDragStart = useCallback((e: any) => {
      try {
        if (e.target && typeof e.target.clearCache === 'function') {
          e.target.clearCache();
        }
      } catch (_) { }
      e.target.setAttr('opacity', 0.8);
      // Clear existing guides at drag start
      setGuides([]);
      setMeasureOverlays([]);
    }, []);

    const onDragMove = useCallback((e: any) => {
      // Prevent unnecessary re-renders during drag move
      e.cancelBubble = true;
      try {
        const node = e.target;
        let desiredCenterX = node.x();
        let desiredCenterY = node.y();
        const curW = Math.max(1, (typeof node.width === 'function' ? node.width() : (el.width || 0)));
        const curH = Math.max(1, (typeof node.height === 'function' ? node.height() : (el.height || 0)));


        // Compute snap against stage, layout guides and other elements
        const { snappedX, snappedY, guides: g } = computeSnap(el.id, desiredCenterX, desiredCenterY, curW, curH);
        if (Math.abs(snappedX - desiredCenterX) <= SNAP_TOLERANCE) desiredCenterX = snappedX;
        if (Math.abs(snappedY - desiredCenterY) <= SNAP_TOLERANCE) desiredCenterY = snappedY;

        const currentEl = elementRef.current;
        const clamped = clampCenterToLayout(desiredCenterX, desiredCenterY, curW, curH, currentEl.properties?.visibleBounds);
        const isPushed = Math.abs(clamped.x - desiredCenterX) > 0.1 || Math.abs(clamped.y - desiredCenterY) > 0.1;
        setSafeZoneHit(isPushed);

        // Clamped dragging (restrict to safe zone)
        node.x(clamped.x);
        node.y(clamped.y);
        // Show alignment guides
        setGuides(g);
        try { const gl = guidesLayerRef.current; if (gl) gl.batchDraw(); } catch (_) { }
        try {
          const x = desiredCenterX - curW / 2;
          const y = desiredCenterY - curH / 2;
          setSafeWarnVisible(isOutsideSafeZone({ x, y, width: curW, height: curH, visibleBounds: currentEl.properties?.visibleBounds }));
        } catch { }
      } catch (_) { }
    }, [computeSnap, el.id, displayWidth, displayHeight, clampCenterToLayout, isOutsideSafeZone]);


    const transformRafRef = useRef<number | null>(null);

    const onTransformStart = useCallback(() => {
      isTransformingRef.current = true;
      try {
        const tpl = templateLayerRef.current;
        const st = staticLayerRef.current;
        const node = imageRef.current;
        if (tpl && typeof tpl.clearCache === 'function') { try { tpl.clearCache(); } catch (_) { } }
        if (st && typeof st.clearCache === 'function') { try { st.clearCache(); } catch (_) { } }
        if (node && typeof node.clearCache === 'function') { try { node.clearCache(); } catch (_) { } }
      } catch (_) { }
    }, []);

    const onTransform = useCallback((e?: any) => {
      try {
        // Only schedule one redraw per frame to avoid flicker
        if (transformRafRef.current == null) {
          transformRafRef.current = requestAnimationFrame(() => {
            transformRafRef.current = null;
            try {
              const active = activeLayerRef.current;
              if (active) active.batchDraw();
            } catch (_) { }
            try {
              const st = staticLayerRef.current;
              if (st) st.batchDraw();
            } catch (_) { }
            try {
              const tl = textLayerRef.current;
              if (tl) tl.batchDraw();
            } catch (_) { }
          });
        }
        if (e) { try { e.cancelBubble = true; } catch (_) { } }
      } catch (_) { }
    }, [el.properties]);

    const onTransformEnd = useCallback((e: any) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const rotation = node.rotation();
      const newWidth = Math.max(20, node.width() * scaleX);
      const newHeight = Math.max(20, node.height() * scaleY);
      node.scaleX(1);
      node.scaleY(1);
      // If this is a vase, do not allow size changing - use displayWidth/displayHeight
      let appliedWidth = newWidth;
      let appliedHeight = newHeight;
      if (isVase(el)) {
        appliedWidth = displayWidth;
        appliedHeight = displayHeight;

        try {
          node.width(appliedWidth);
          node.height(appliedHeight);
          node.scaleX(1);
          node.scaleY(1);
          node.offsetX(appliedWidth / 2);
          node.offsetY(appliedHeight / 2);
        } catch (err) { }
      } else {
        node.width(appliedWidth);
        node.height(appliedHeight);
      }
      const role = String(el.properties?.role || el.properties?.kind || '').toLowerCase();
      const isArtwork = role === 'artwork' || role === 'work';

      if (!isArtwork) {
        try {
          const tolPx = inchesToPixels(0.05); // ~0.05 in
          let snapW = appliedWidth;
          let snapH = appliedHeight;
          for (const other of currentDesign.elements) {
            if (!other || other.id === el.id) continue;
            const otherRole = String(other.properties?.role || other.properties?.kind || '').toLowerCase();
            if (otherRole === 'template' || other.properties?.isTemplate === true) continue;
            if (Math.abs(other.width - snapW) <= tolPx) snapW = other.width;
            if (Math.abs(other.height - snapH) <= tolPx) snapH = other.height;
          }
          if (Math.abs(snapW - appliedWidth) > 0.1) { appliedWidth = snapW; try { node.width(appliedWidth); } catch (_) { } }
          if (Math.abs(snapH - appliedHeight) > 0.1) { appliedHeight = snapH; try { node.height(appliedHeight); } catch (_) { } }
        } catch (_) { }
      }
      // Allow free position (no clamp); use current node center
      const centerX = node.x();
      const centerY = node.y();
      const now = Date.now();
      const currentEl = elementRef.current;
      const newX = centerX - appliedWidth / 2;
      const newY = centerY - appliedHeight / 2;
      node.offsetX(newWidth / 2);
      node.offsetY(appliedHeight / 2);
      setSafeZoneHit(false);
      const newRect = { x: newX, y: newY, width: appliedWidth, height: appliedHeight, id: currentEl.id, properties: currentEl.properties };
      try { setSafeWarnVisible(isOutsideSafeZone({ x: newX, y: newY, width: appliedWidth, height: appliedHeight, visibleBounds: currentEl.properties?.visibleBounds })); } catch { }
      // If rotationLocked is set on this element, ignore rotation changes
      const rotationLocked = Boolean(currentEl.properties?.rotationLocked === true);
      const appliedRotation = rotationLocked ? currentEl.rotation : rotation;
      if (rotationLocked) {
        try { node.rotation(appliedRotation); } catch (err) { }
      }

      if ((Math.abs(newX - currentEl.x) > 0.1 || Math.abs(newY - currentEl.y) > 0.1 ||
        Math.abs(newWidth - currentEl.width) > 0.1 || Math.abs(newHeight - currentEl.height) > 0.1 ||
        Math.abs(appliedRotation - currentEl.rotation) > 0.1) &&
        (now - lastUpdateRef.current) > 16) {
        lastUpdateRef.current = now;
        // Defer the state write to the next frame to avoid mid-frame re-render flicker
        requestAnimationFrame(() => updateDesign(prev => {
          const elementIndex = prev.elements.findIndex(x => x.id === currentEl.id);
          if (elementIndex === -1) return prev;
          const currentElement = prev.elements[elementIndex];
          if (Math.abs(currentElement.x - newX) < 0.1 && Math.abs(currentElement.y - newY) < 0.1 &&
            Math.abs(currentElement.width - appliedWidth) < 0.1 && Math.abs(currentElement.height - appliedHeight) < 0.1 &&
            Math.abs(currentElement.rotation - appliedRotation) < 0.1) {
            return prev;
          }
          const newElements = prev.elements.map((element, index) =>
            index === elementIndex
              ? {
                ...element,
                x: newX,
                y: newY,
                width: appliedWidth,
                height: appliedHeight,
                rotation: appliedRotation,
                properties: {
                  ...element.properties,
                  userResized: true,
                  isManualPosition: true,
                  isManualSize: true
                }
              }
              : element
          );
          return { ...prev, elements: newElements };
        }));
      }

      try {
        const prev = transformCacheStateRef.current;
        const restoreLayer = (layer: any, prevWasCached: boolean | undefined, prevListening: boolean | undefined) => {
          if (!layer) return;
          try {
            // If layer was not cached before transform, clear the cache; otherwise leave it cached.
            if (!prevWasCached && layer.isCached && layer.isCached()) {
              try { layer.clearCache(); } catch (_) { }
            }
            // Restore listening flag only if we recorded a previous value
            if (typeof prevListening === 'boolean' && typeof layer.listening === 'function') {
              try { layer.listening(prevListening); } catch (_) { }
            }
            try { layer.batchDraw(); } catch (_) { }
          } catch (_) { }
        };

        if (prev) {
          restoreLayer(staticLayerRef.current, prev.stWasCached, prev.stPrevListening);
          restoreLayer(templateLayerRef.current, prev.tplWasCached, prev.tplPrevListening);
        } else {
          // Fallback: if we have no prev record, do conservative redraws
          try { staticLayerRef.current && staticLayerRef.current.batchDraw(); } catch (_) { }
          try { templateLayerRef.current && templateLayerRef.current.batchDraw(); } catch (_) { }
        }

        // Background left uncached; just redraw
        const bg = backgroundLayerRef.current;
        if (bg) { try { bg.batchDraw(); } catch (_) { } }

        // Clear node cache only if we cached it in onTransformStart
        try {
          const node = imageRef.current;
          if (node && node.isCached && node.isCached() && prev && prev.nodeWasCached) {
            try { node.clearCache(); } catch (_) { }
            const layer = node.getLayer && node.getLayer();
            if (layer) layer.batchDraw();
          }
        } catch (_) { }
      } catch (_) { }
      // Cancel any pending frame draw after transform ends
      try { if (transformRafRef.current != null) { cancelAnimationFrame(transformRafRef.current); transformRafRef.current = null; } } catch (_) { }
      transformCacheStateRef.current = null;
      isTransformingRef.current = false;
    }, [updateDesign, currentDesign.elements, displayWidth, displayHeight, el.properties, el.id, inchesToPixels]);

    const onSelect = useCallback((e: any) => {
      e.cancelBubble = true;
      if (isTemplate) return;
      // Always single-select this element and clear any area selection state
      requestAnimationFrame(() => {
        setSelectedArtworkId(el.id);
        setSelectedItemIds([el.id]);
        setPersistentAreaSelection(null);
        setAreaSelectActive(false);

        // ESQ: clicking fixed template text/artwork should show its position outline.
        const bounds = el.properties?.esqBounds;
        if (designMode === 'ESQ' && bounds && (bounds.h1 !== undefined)) {
          setSampleGuideLines({
            h1: Number(bounds.h1),
            h2: Number(bounds.h2),
            v1: Number(bounds.v1),
            v2: Number(bounds.v2),
            visible: true
          });
        }

        // Ensure any previous selection visuals are cleared immediately
        try { staticLayerRef.current && staticLayerRef.current.batchDraw(); } catch (_) { }
        try { textLayerRef.current && textLayerRef.current.batchDraw(); } catch (_) { }
        try { activeLayerRef.current && activeLayerRef.current.batchDraw(); } catch (_) { }
      });

      const slotType: string = el.properties?.slotType || el.properties?.placement || '';
      const isPorcelainRole = el.properties?.role === 'porcelain';
      const validSlotTypes = ['corner', 'corner_left', 'corner_right', 'left', 'top', 'special'];
      if (slotType && !el.properties?.isCustomUpload && !isPorcelainRole && validSlotTypes.includes(slotType)) {
        const placementCode = el.properties?.placementCode || el.properties?.placement_code;
        const matchingSlot = placementSlots.find((s: any) =>
          s.slot_type === slotType && (!placementCode || s.placement_code === placementCode)
        ) ?? placementSlots.find((s: any) => s.slot_type === slotType)
          ?? { slot_type: slotType, placement_code: el.properties?.placement } as any;
        setActivePlacementSlot(matchingSlot);
      } else if (isPorcelainRole) {
        // Clicking the uploaded porcelain photo clears any leftover slot outline
        setActivePlacementSlot(null);
      }
    }, [isTemplate, el.id, el.properties, placementSlots, setActivePlacementSlot, designMode, setSampleGuideLines]);

    const onDoubleClick = useCallback((e?: any) => {
      if (e && typeof e.cancelBubble !== 'undefined') {
        e.cancelBubble = true;
      }
      const isPhotoRole = el.properties?.role === 'photo' || ['oval', 'circle', 'rounded-rect', 'rect'].includes(el.properties?.shape as string) || el.properties?.userUploaded === true;
      if (!isPhotoRole) return;

      if (el.properties?.role === 'porcelain') {
        setOpenModal('porcelain');
        return;
      }

      // If double clicked, open the adjuster
      setAdjustingImageEl(el);
    }, [el, setOpenModal, setAdjustingImageEl]);

    return (
      <React.Fragment>

        <Image
          ref={imageRef}
          id={el.id}
          key={el.id}
          x={renderX + renderWidth / 2}
          y={renderY + renderHeight / 2}
          width={renderWidth}
          height={renderHeight}
          rotation={el.rotation}
          scaleX={1}
          scaleY={1}
          offsetX={renderWidth / 2}
          offsetY={renderHeight / 2}
          image={img || undefined}
          perfectDrawEnabled={!!perfect}
          shadowForStrokeEnabled={false}
          strokeScaleEnabled={false}
          shadowEnabled={false}
          shadowBlur={0}
          shadowColor="transparent"
          shadowOffsetX={0}
          shadowOffsetY={0}
          shadowOpacity={0}
          filters={[]}
          sceneFunc={(context, shape) => {
            const image = shape.getAttr('image');
            const width = shape.width();
            const height = shape.height();

            if (el.properties?.role === 'esq_fixed_artwork') {
              if (image) {
                try {
                  context.drawImage(image as CanvasImageSource, 0, 0, width, height);
                } catch (_) { }
              }
              return;
            }

            const isMirrored = el.flipX || false;
            const isFlipped = el.flipY || false;

            context.save();

            if (isMirrored || isFlipped) {
              context.scale(
                isMirrored ? -1 : 1,
                isFlipped ? -1 : 1
              );

              context.translate(
                isMirrored ? -width : 0,
                isFlipped ? -height : 0
              );
            }

            if (el.properties?.shape === 'oval' || el.properties?.shape === 'circle' || el.properties?.shape === 'rounded-rect' || el.properties?.shape === 'rect') {
              const cx = width / 2;
              const cy = height / 2;

              if (el.properties?.shape === 'circle') {
                const diameter = Math.max(1, Math.floor(Math.min(width, height)) - 1.5);
                const r = diameter / 2;
                context.beginPath();
                context.arc(cx, cy, r, 0, Math.PI * 2);
              } else if (el.properties?.shape === 'oval') {
                const rx = Math.max(1, (width - (el.properties?.role === 'porcelain' ? 0 : 1.5)) / 2);
                const ry = Math.max(1, (height - (el.properties?.role === 'porcelain' ? 0 : 1.5)) / 2);
                context.beginPath();
                context.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
              } else if (el.properties?.shape === 'rounded-rect') {
                const r = Math.min(width, height) * 0.15;
                context.beginPath();
                if (typeof (context as any).roundRect === 'function') {
                  // Apply the same 1.5px margin to prevent flat edges at the corners
                  (context as any).roundRect(0.75, 0.75, width - 1.5, height - 1.5, r);
                } else {
                  context.rect(0.75, 0.75, width - 1.5, height - 1.5);
                }
              } else {
                // rect - just draw a normal rect with the safety margin
                context.beginPath();
                context.rect(0.75, 0.75, width - 1.5, height - 1.5);
              }

              context.closePath();
              if (!image) {
                context.fillStyle = 'rgba(0,0,0,0.75)';
                context.fill();
                context.lineWidth = 4;
                if (typeof (context as any).setLineDash === 'function') {
                  (context as any).setLineDash([10, 8]);
                }
                context.strokeStyle = '#ffffff';
                context.stroke();
                context.restore();
                return;
              }
              context.clip();
            }

            if (image) {
              try {
                const imgW = (image as HTMLImageElement).naturalWidth || (image as any).width || 1;
                const imgH = (image as HTMLImageElement).naturalHeight || (image as any).height || 1;
                const nodeW = Math.max(1, width);
                const nodeH = Math.max(1, height);
                const imgRatio = imgW / imgH;
                const nodeRatio = nodeW / nodeH;

                // Check if this is an Art or Profile image
                const role = String(el.properties?.role || '').toLowerCase();
                const shape = el.properties?.shape;
                const isShaped = shape && shape !== 'rect' && shape !== 'rounded-rect' && shape !== 'none';

                if (isShaped) {
                  // COVER: Scale to fill box entirely, cropping excess for shaped items (porcelain oval/circle)
                  let sx = 0, sy = 0, sw = imgW, sh = imgH;
                  if (imgRatio > nodeRatio) {
                    // image wider -> crop sides
                    sw = Math.floor(imgH * nodeRatio);
                    sh = imgH;
                    sx = Math.floor((imgW - sw) / 2);
                    sy = 0;
                  } else {
                    // image taller -> crop top/bottom
                    sw = imgW;
                    sh = Math.floor(imgW / nodeRatio);
                    sx = 0;
                    sy = Math.floor((imgH - sh) / 2);
                  }
                  context.drawImage(image as CanvasImageSource, sx, sy, sw, sh, 0, 0, nodeW, nodeH);
                } else {
                  // CONTAIN: Calculate dimensions to fit image entirely inside box while maintaining aspect ratio
                  let drawW = nodeW;
                  let drawH = nodeH;
                  let drawX = 0;
                  let drawY = 0;
                  if (imgRatio > nodeRatio) {
                    drawW = nodeW;
                    drawH = nodeW / imgRatio;
                    drawY = (nodeH - drawH) / 2;
                  } else {
                    drawH = nodeH;
                    drawW = nodeH * imgRatio;
                    drawX = (nodeW - drawW) / 2;
                  }

                  const isPhoto = role === 'photo' || role === 'porcelain';
                  const strokeWidthProp = el.properties?.artworkStrokeWidth ?? el.properties?.strokeWidth;
                  const strokeOffset = typeof strokeWidthProp === 'number'
                    ? strokeWidthProp
                    : (isGrey && !isPhoto ? 0.35 : 0);

                  if (strokeOffset > 0 && !isPhoto) {
                    const d = Math.min(2.0, strokeOffset);
                    context.drawImage(image as CanvasImageSource, 0, 0, imgW, imgH, drawX - d, drawY, drawW, drawH);
                    context.drawImage(image as CanvasImageSource, 0, 0, imgW, imgH, drawX + d, drawY, drawW, drawH);
                    context.drawImage(image as CanvasImageSource, 0, 0, imgW, imgH, drawX, drawY - d, drawW, drawH);
                    context.drawImage(image as CanvasImageSource, 0, 0, imgW, imgH, drawX, drawY + d, drawW, drawH);
                  }

                  context.drawImage(image as CanvasImageSource, 0, 0, imgW, imgH, drawX, drawY, drawW, drawH);
                }
              } catch (err) {
                try { context.drawImage(image, 0, 0, width, height); } catch (_) { }
              }
            }

            context.restore();
          }}
          listening={listening}
          draggable={!el.properties?.isFixedPosition && el.properties?.draggable !== false}
          onDragStart={draggable ? onDragStart : undefined}
          onDragMove={draggable ? onDragMove : undefined}
          onDragEnd={draggable ? onDragEnd : undefined}
          onTransformStart={onTransformStart}
          onTransform={onTransform}
          onTransformEnd={onTransformEnd}
          onClick={!isTemplate ? onSelect : undefined}
          onTap={!isTemplate ? onSelect : undefined}
          onDblClick={onDoubleClick}
          onDblTap={onDoubleClick}
          onMouseDown={undefined}
          stroke={!isTemplate && isElementSelected ? 'transparent' : undefined}
          strokeWidth={0}
          dash={undefined}
        />
        {/* Vase size label handled in VaseElement */}

      </React.Fragment>
    );
  }, (prevProps, nextProps) => {
    const prev = prevProps.el;
    const next = nextProps.el;

    const isTemplate = prev.properties?.role === 'template' || prev.properties?.isTemplate === true;

    if (isTemplate) {
      return prev.properties?.src === next.properties?.src;
    }

    // For vase elements, include vaseSrcOriginal in equality check so they are
    // treated as distinct from template changes which update template.src.
    const prevIsVase = String(prev.properties?.role || prev.properties?.kind || '').toLowerCase() === 'vase';
    const nextIsVase = String(next.properties?.role || next.properties?.kind || '').toLowerCase() === 'vase';

    if (prevIsVase || nextIsVase) {
      return (
        prev.id === next.id &&
        prev.x === next.x &&
        prev.y === next.y &&
        prev.width === next.width &&
        prev.height === next.height &&
        prev.rotation === next.rotation &&
        prev.flipX === next.flipX &&
        prev.flipY === next.flipY &&
        // Compare original vase srcs (may be undefined for non-vases)
        (prev.properties?.vaseSrcOriginal || prev.properties?.src) === (next.properties?.vaseSrcOriginal || next.properties?.src) &&
        prev.properties?.role === next.properties?.role &&
        prev.properties?.isTemplate === next.properties?.isTemplate &&
        prev.properties?.draggable === next.properties?.draggable &&
        prevProps.isSelected === nextProps.isSelected &&
        prevProps.listening === nextProps.listening &&
        prevProps.perfect === nextProps.perfect
      );
    }

    return (
      prev.id === next.id &&
      prev.x === next.x &&
      prev.y === next.y &&
      prev.width === next.width &&
      prev.height === next.height &&
      prev.rotation === next.rotation &&
      prev.flipX === next.flipX &&
      prev.flipY === next.flipY &&
      prev.properties?.src === next.properties?.src &&
      prev.properties?.role === next.properties?.role &&
      prev.properties?.isTemplate === next.properties?.isTemplate &&
      prev.properties?.draggable === next.properties?.draggable &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.listening === nextProps.listening &&
      prevProps.perfect === nextProps.perfect
    );
  });

  const TextNode: React.FC<{ el: DesignElement; selected?: boolean; isEditing?: boolean; graniteColorId?: number | string; onRightClick?: (e: any, textId: string) => void; trRef: any }> = React.memo(({ el, selected, isEditing, graniteColorId, onRightClick, trRef }) => {
    const { content, fontSize, align, fontFamily, color, lineHeight, letterSpacing, outline } = el.properties || {};
    const elementId = el.id;
    const textRef = useRef<any>(null);
    const rectRef = useRef<any>(null);
    const clampedFixedPos = useMemo(
      () => clampFixedEsqElementToOwnBounds(el),
      [el, clampFixedEsqElementToOwnBounds]
    );
    const renderX = (designMode === 'ESQ' && el.properties?.isFixedPosition) ? clampedFixedPos.x : el.x;
    const renderY = (designMode === 'ESQ' && el.properties?.isFixedPosition) ? clampedFixedPos.y : el.y;
    const fixedEsqClip = useMemo(() => {
      if (!(designMode === 'ESQ' && el.properties?.isFixedPosition && el.properties?.esqBounds)) return null;
      const bounds = el.properties.esqBounds as any;
      const ppi = Number(pixelsPerInch) || 0;
      const cp = compositeProps as any;
      const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0 });
      if (!bounds || !ppi || !graniteTemplate || !ref) return null;
      const frame = computeEsqBoundsPixelFrame({
        bounds,
        compositeProps,
        graniteTemplate,
        graniteConfig: graniteConfig || {},
        pixelsPerInch: ppi,
      });
      const { left, right, top, bottom, insetX, insetTop, insetBottom } = frame;
      if (!Number.isFinite(left) || !Number.isFinite(right) || !Number.isFinite(top) || !Number.isFinite(bottom)) return null;
      if (right <= left || bottom <= top) return null;
      // ESQ fixed text slots: symmetric vertical inset so slot sits evenly between V1/V2 guides (matches dashed outlines).
      const fnLower = String(el.properties?.fieldName || '').toLowerCase();
      const isEsqFixedTextField =
        designMode === 'ESQ' &&
        (['firstname', 'separator', 'lastname', 'dob', 'dod', 'verse', 'verse1', 'verse2', 'verse3'].includes(fnLower) || fnLower.startsWith('verse'));
      const vTop = isEsqFixedTextField ? (insetTop + insetBottom) / 2 : insetTop;
      const vBot = isEsqFixedTextField ? (insetTop + insetBottom) / 2 : insetBottom;
      return {
        x: left - renderX + insetX,
        y: top - renderY + vTop,
        // Integer px so clip, Konva Text height, auto-fit maxH, and inline editor slot stay in sync (no 1px snap).
        width: Math.max(1, Math.round((right - left) - (insetX * 2))),
        height: Math.max(1, Math.round((bottom - top) - (vTop + vBot))),
      };
    }, [designMode, el.properties?.isFixedPosition, el.properties?.esqBounds, el.properties?.fieldName, pixelsPerInch, compositeProps, graniteTemplate, graniteConfig, renderX, renderY]);

    const activeColorObj = React.useMemo(() => {
      return sharedColors.find((c: any) => Number(c.id) === Number(graniteColorId));
    }, [sharedColors, graniteColorId]);

    const isPJB = activeColorObj?.code === 'PJB' || String(activeColorObj?.name).toUpperCase().includes('BLACK');
    const isGrey = activeColorObj?.code === 'MG' || activeColorObj?.code === 'Grey' || String(activeColorObj?.name).toUpperCase().includes('GRAY') || String(activeColorObj?.name).toUpperCase().includes('GREY');

    // Strip "-back" suffix so back-side IDs (e.g. "verse-back") match front-side field names
    const baseElementId = elementId.replace(/-back$/, '');
    const isAdditionalText = baseElementId.toLowerCase().startsWith('additionaltext');
    // LSP/QD: verse/additional text allow mixed case (elementId-based).
    // ESQ: fixed fields have arbitrary IDs; use fieldName instead.
    const fieldNameLc = String(el.properties?.fieldName || '').toLowerCase();
    const isWritingField =
      baseElementId === 'verse' ||
      isAdditionalText ||
      (designMode === 'ESQ' && (fieldNameLc === 'verse' || fieldNameLc.startsWith('verse')));
    const isNameWithCasingField =
      baseElementId === 'lastName' ||
      baseElementId === 'fullName' ||
      fieldNameLc === 'lastname' ||
      fieldNameLc === 'fullname';
    const shouldAllowMixedCase = isWritingField || isNameWithCasingField;

    const lockedFontFamily = fontFamily || '';
    const renderFontFamily = useMemo(() => {
      if (designMode === 'ESQ' && (fieldNameLc === 'verse' || fieldNameLc.startsWith('verse'))) {
        return isPJB ? 'MonotypeCorsiva' : 'Vermarco';
      }
      const ff = String(lockedFontFamily || '').trim();
      if (ff) return mapEsqDbFontToLoadedFamily(ff, fieldNameLc, isPJB);
      return 'MLCModifiedRomanSKS';
    }, [lockedFontFamily, designMode, fieldNameLc, isPJB]);
    const isFixedPositionText = el.properties?.isFixedPosition === true && el.type === 'text';
    const baseFontSize = Number(el.properties?.baseFontSize || fontSize || 24);
    const minScale = Number(el.properties?.minScale);
    const maxScale = Number(el.properties?.maxScale);
    const ppi = Number(pixelsPerInch) || 96;

    const isVerseField = fieldNameLc === 'verse' || fieldNameLc.startsWith('verse');
    const isScriptFont = renderFontFamily === 'MonotypeCorsiva' || renderFontFamily.toLowerCase().includes('script') || renderFontFamily.toLowerCase().includes('corsiva');
    const esqFontMultiplier = (designMode === 'ESQ' && isVerseField && isScriptFont) ? 1.40 : 1.0;

    const minFontPx = isFixedPositionText
      ? 1
      : (Number.isFinite(minScale) && minScale > 0 ? (minScale * ppi * esqFontMultiplier) : 1);
    const maxFontPx = Number.isFinite(maxScale) && maxScale > 0 ? (maxScale * ppi * esqFontMultiplier) : (isVerseField && designMode === 'ESQ' ? 0.875 * ppi * esqFontMultiplier : Number.POSITIVE_INFINITY);
    const effectiveBaseFontSize = isFixedPositionText
      ? (Number.isFinite(maxScale) && maxScale > 0
        ? maxScale * ppi * esqFontMultiplier
        : Number.isFinite(minScale) && minScale > 0
          ? minScale * ppi * esqFontMultiplier
          : baseFontSize < 5 ? baseFontSize * ppi * esqFontMultiplier : baseFontSize * esqFontMultiplier)
      : baseFontSize;
    const computedFontSize = Math.min(
      Math.max(Number(fontSize || 24) * (isVerseField && designMode === 'ESQ' && Number(fontSize || 24) < 40 ? esqFontMultiplier : 1), minFontPx),
      maxFontPx
    );

    const [isFontReady, setIsFontReady] = useState<boolean>(() => {
      try {
        const ff = String(renderFontFamily || '').trim();
        if (!ff) return true;
        const fontsApi: any = (document as any).fonts;
        if (!fontsApi || typeof fontsApi.load !== 'function') return true;
        const size = Math.max(1, Math.floor(computedFontSize || 16));
        if (typeof fontsApi.check === 'function') {
          return !!fontsApi.check(`${size}px "${ff}"`);
        }
        return false;
      } catch {
        return true;
      }
    });

    useEffect(() => {
      let cancelled = false;
      try {
        const ff = String(renderFontFamily || '').trim();
        if (!ff) { setIsFontReady(true); return; }
        const fontsApi: any = (document as any).fonts;
        if (!fontsApi || typeof fontsApi.load !== 'function') { setIsFontReady(true); return; }

        if (isFontReady) return;

        setIsFontReady(false);
        const size = Math.max(1, Math.floor(computedFontSize || 16));
        fontsApi.load(`${size}px "${ff}"`).then(() => {
          if (cancelled) return;
          setIsFontReady(true);
          try { textLayerRef.current && textLayerRef.current.batchDraw(); } catch (_) { }
          try { activeLayerRef.current && activeLayerRef.current.batchDraw(); } catch (_) { }
        }).catch(() => {
          if (cancelled) return;
          setIsFontReady(true);
        });
      } catch {
        try { setIsFontReady(true); } catch { }
      }
      return () => { cancelled = true; };
    }, [renderFontFamily, computedFontSize, isFontReady]);
    const isFixedBox = typeof (el as any).boxWidth === 'number' && (el as any).boxWidth > 0;

    const fixedEsqLaneWidth = useMemo(() => {
      if (!isFixedPositionText) return undefined;
      const bounds = el.properties?.esqBounds;
      const ppi = Number(pixelsPerInch) || 0;
      const cp = compositeProps as any;
      const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0 });
      if (!bounds || !ppi || !graniteTemplate || !ref) return undefined;
      const frame = computeEsqBoundsPixelFrame({
        bounds,
        compositeProps,
        graniteTemplate,
        graniteConfig: graniteConfig || {},
        pixelsPerInch: ppi,
      });
      const { left, right, insetX } = frame;
      if (!Number.isFinite(left) || !Number.isFinite(right) || right <= left) return undefined;
      return Math.max(1, Math.round((right - left) - insetX * 2));
    }, [isFixedPositionText, el.properties?.esqBounds, pixelsPerInch, compositeProps, graniteTemplate, graniteConfig]);

    const configuredBoxWidth = isFixedPositionText
      ? Math.max(1, Math.min(Number(el.width || (el as any).boxWidth || 1), Number(fixedEsqLaneWidth || Number(el.width || 1))))
      : (isFixedBox ? (el as any).boxWidth : undefined);

    const esqNameSlotFit = Boolean(
      designMode === 'ESQ' &&
      isFixedPositionText &&
      fixedEsqClip &&
      (
        ['firstname', 'separator', 'lastname', 'dob', 'dod', 'verse'].includes(String(el.properties?.fieldName || '').toLowerCase()) ||
        String(el.properties?.fieldName || '').toLowerCase().startsWith('verse') ||
        String(el.properties?.fieldName || '').toLowerCase().includes('verse')
      )
    );
    // Small padding to prevent text from touching the selection outline borders
    const esqKonvaPadding = 2;
    // Minimal outline for name slots to prevent bleeding outside green outline
    const textOutlineWidth =
      outline
        ? esqNameSlotFit
          ? Math.max(0.5, computedFontSize / 32)
          : Math.max(1, computedFontSize / 20)
        : 0;
    // Konva paints ascenders/stroke slightly above the Text box; bleed clip upward so nothing hits the Group mask.
    const esqGroupClipBleedTop =
      fixedEsqClip && designMode === 'ESQ' && isFixedPositionText && el.type === 'text'
        ? Math.max(
          10,
          Math.ceil(computedFontSize * 0.2) + Math.ceil(textOutlineWidth * 2.5) + 4
        )
        : 0;

    // Full slot box; Text node is positioned at slot center via offsetX/offsetY so H+V align match the outline.
    const esqNameBoxW = esqNameSlotFit && fixedEsqClip ? fixedEsqClip.width : 0;
    const esqNameBoxH = esqNameSlotFit && fixedEsqClip ? fixedEsqClip.height : 0;

    const fillColor = useMemo(() => {
      const props = el.properties || {};
      if (props.frost) return '#e5e7eb';
      if (props.polish) return '#ffffff';

      const activeColor = String(color || '#000000').toUpperCase();
      const gid = Number(graniteColorId);

      // Black granite (PJB) -> Flip black text to white
      if (isPJB && (activeColor === '#000000' || activeColor === '#000')) {
        return '#FFFFFF';
      }

      // Grey granite (MG) -> Flip white text to black
      if (isGrey && (activeColor === '#FFFFFF' || activeColor === '#FFF')) {
        return '#000000';
      }

      return color || '#000000';
    }, [el.properties, graniteColorId, isPJB, isGrey]);

    // ESQ: pick max font (with fractional px) that fits the slot; render with fixed height + verticalAlign middle.
    useLayoutEffect(() => {
      if (designMode !== 'ESQ') return;
      if (el.type !== 'text' || !isFixedPositionText) return;
      const esqAutoFitField = String(el.properties?.fieldName || '').toLowerCase();
      // Auto-fit all fixed text fields so nothing gets clipped inside the outline.
      if (!['firstname', 'separator', 'lastname', 'dob', 'dod', 'verse', 'verse1', 'verse2', 'verse3'].includes(esqAutoFitField) && !esqAutoFitField.startsWith('verse')) return;
      if (!fixedEsqClip || !fixedEsqClip.width) return;
      if (el.properties?.isManualSize) return;
      // Skip auto-fit while editing to prevent flickering during typing
      if (editingTextId === el.id) return;

      const raw = String(content || '').trim();
      if (!raw) return;

      const maxW = Math.max(1, Math.round(fixedEsqClip.width));
      // Use full slot height so text scales up to the complete slot bounds.
      const maxHInner = Math.max(1, Math.round(fixedEsqClip.height));
      const displayText = shouldAllowMixedCase ? raw : raw.toUpperCase();
      const hasExplicitNewlines = displayText.includes('\n');
      const textLines = hasExplicitNewlines ? displayText.split(/\r?\n/) : [displayText.replace(/\r?\n+/g, ' ')];
      const fontFam = String(renderFontFamily || '').trim() || 'serif';
      const ls = letterSpacing || 0;
      const lhVal = hasExplicitNewlines ? (lineHeight || 1.1) : 1;

      const ppiVal = Number(pixelsPerInch) || 96;
      const targetVerseScale = (Number.isFinite(maxScale) && maxScale > 0) ? maxScale : 0.875;
      const isScriptVerseFont = fontFam === 'MonotypeCorsiva' || fontFam.toLowerCase().includes('script') || fontFam.toLowerCase().includes('corsiva');
      const verseFontMultiplier = isScriptVerseFont ? 1.40 : 1.0;
      const effectiveMaxH = (designMode === 'ESQ' && (esqAutoFitField === 'verse' || esqAutoFitField.startsWith('verse')))
        ? Math.max(maxHInner, targetVerseScale * ppiVal * verseFontMultiplier * (hasExplicitNewlines ? 1.8 : 1.0))
        : (Number.isFinite(maxScale) && maxScale > 0)
          ? Math.max(maxHInner, maxScale * ppiVal)
          : (Number.isFinite(minScale) && minScale > 0)
            ? Math.max(maxHInner, minScale * ppiVal)
            : maxHInner;

      const fitsSlot = (fs: number) => {
        let naturalW = 0;
        let naturalH = Math.max(fs, textLines.length * fs * lhVal);
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.font = `${fs}px "${fontFam}", serif`;
            textLines.forEach(lineStr => {
              const m = ctx.measureText(lineStr);
              const w = m.width + Math.max(0, (lineStr.length - 1) * ls);
              if (w > naturalW) naturalW = w;
            });
          }
        } catch { }

        if (!naturalW) {
          textLines.forEach(lineStr => {
            const w = lineStr.length * fs * 0.65;
            if (w > naturalW) naturalW = w;
          });
        }

        const availableW = Math.max(1, maxW - (esqKonvaPadding * 2));
        return {
          ok: naturalW <= availableW && naturalH <= effectiveMaxH,
          width: naturalW,
          height: naturalH,
        };
      };

      const slotHFull = Math.max(1, Math.round(fixedEsqClip.height));
      const minCandidate = 6;
      // Cap to effectiveBaseFontSize so text scales up to its configured DB max_scale (in inches).
      const maxCandidate = Math.min(maxFontPx, effectiveBaseFontSize, 2000);
      let lo = minCandidate;
      let hi = Math.max(minCandidate, Math.min(maxCandidate, Math.max(Math.ceil(maxHInner * 6), 48)));
      let best = minCandidate;
      while (lo <= hi) {
        const mid = Math.floor(((lo + hi) / 2) * 4) / 4;
        const fit = fitsSlot(mid);
        if (fit.ok) {
          best = mid;
          lo = mid + 0.25;
        } else {
          hi = mid - 0.25;
        }
      }
      while (best > minCandidate && !fitsSlot(best).ok) best -= 0.25;

      let refined = best;
      for (let i = 0; i < 80; i++) {
        const t = refined + 0.25;
        if (t > maxCandidate) break;
        if (fitsSlot(t).ok) refined = t;
        else break;
      }
      best = Math.min(Math.max(refined, 6), maxFontPx, effectiveBaseFontSize);

      const isDateField = esqAutoFitField === 'dob' || esqAutoFitField === 'dod';
      const counterpartFieldName = esqAutoFitField === 'dob' ? 'dod' : 'dob';
      const counterpartEl = isDateField
        ? (currentDesign?.elements || []).find((x: any) =>
          x.type === 'text' &&
          x.properties?.role === 'esq_fixed_text' &&
          String(x.properties?.fieldName || '').toLowerCase() === counterpartFieldName &&
          Boolean(String(x.properties?.content || '').trim())
        )
        : null;
      const counterpartFs = counterpartEl ? Number(counterpartEl.properties?.fontSize || 0) : 0;
      const targetFontSizeOuter = (counterpartFs > 0 && isDateField) ? Math.min(best, counterpartFs) : best;
      const fsNow = Number(fontSize || 0);

      if (
        Math.abs(fsNow - targetFontSizeOuter) < 0.3 &&
        (!isDateField || !counterpartEl || Math.abs(counterpartFs - targetFontSizeOuter) < 0.3)
      ) {
        return;
      }

      updateDesign(prev => {
        const idx = prev.elements.findIndex(x => x.id === el.id);
        if (idx === -1) return prev;
        const curr = prev.elements[idx] as any;
        const counterpartIdx = isDateField
          ? prev.elements.findIndex(x =>
            x.type === 'text' &&
            x.properties?.role === 'esq_fixed_text' &&
            String(x.properties?.fieldName || '').toLowerCase() === counterpartFieldName &&
            Boolean(String(x.properties?.content || '').trim())
          )
          : -1;

        let targetFontSize = best;
        if (counterpartIdx !== -1) {
          const counterpartElInPrev = prev.elements[counterpartIdx] as any;
          const counterpartFsInPrev = Number(counterpartElInPrev.properties?.fontSize || 0);
          if (counterpartFsInPrev > 0) {
            targetFontSize = Math.min(best, counterpartFsInPrev);
          }
        }

        const cfs = Number(curr.properties?.fontSize || 0);
        const cw = Number(curr.width || 0);
        const ch = Number(curr.height || 0);
        const counterpartFsNow = counterpartIdx !== -1 ? Number(prev.elements[counterpartIdx]?.properties?.fontSize || 0) : 0;
        const fsThreshold = 0.3;

        if (
          Math.abs(cfs - targetFontSize) < fsThreshold &&
          Math.abs(cw - maxW) < 1 &&
          Math.abs(ch - slotHFull) < 1.5 &&
          (!isDateField || counterpartIdx === -1 || Math.abs(counterpartFsNow - targetFontSize) < fsThreshold)
        ) {
          return prev;
        }

        return {
          ...prev,
          elements: prev.elements.map((x, i) => {
            if (i === idx) {
              return {
                ...x,
                width: maxW,
                boxWidth: maxW,
                height: slotHFull,
                properties: {
                  ...curr.properties,
                  fontSize: targetFontSize,
                },
              };
            }
            if (isDateField && i === counterpartIdx) {
              return {
                ...x,
                properties: {
                  ...x.properties,
                  fontSize: targetFontSize,
                },
              };
            }
            return x;
          }),
        };
      });
    }, [
      designMode,
      el.type,
      el.id,
      isFixedPositionText,
      el.properties?.fieldName,
      el.properties?.isManualSize,
      fixedEsqClip?.height,
      fixedEsqClip?.width,
      content,
      align,
      renderFontFamily,
      letterSpacing,
      fontSize,
      shouldAllowMixedCase,
      isFontReady,
      updateDesign,
      outline,
      editingTextId,
      minFontPx,
      maxFontPx,
    ]);

    const onTransform = useCallback((e: any) => {
      const node = e.target;
      const textNode = textRef.current;
      if (!textNode) return;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      // Reset scale to 1 to avoid raster blurring
      node.scaleX(1);
      node.scaleY(1);

      const anchor = trRef.current ? trRef.current.getActiveAnchor() : '';
      const isWidthOnly = anchor === 'middle-left' || anchor === 'middle-right';

      // Sync the invisible visual-box rect if present, to prevent "stale bounds" causing drag issues
      const rectNode = rectRef.current;

      if (isWidthOnly) {
        // Side handles: Change width (reflow)
        const currentWidth = textNode.width();
        const newWidth = Math.max(10, currentWidth * scaleX);
        textNode.width(newWidth);

        if (rectNode) {
          rectNode.width(newWidth);
          rectNode.x(0);
        }
      } else {
        // Corner handles: Scale font size (proportional)
        const newSize = Math.min(Math.max(textNode.fontSize() * scaleY, minFontPx), maxFontPx);
        textNode.fontSize(newSize);

        if (textNode.width()) {
          // Fixed width mode: scale width
          const hadFixedBox = (el as any).boxWidth > 0;
          if (hadFixedBox) {
            const newWidth = Math.max(10, textNode.width() * scaleX);
            textNode.width(newWidth);
            if (rectNode) {
              rectNode.width(newWidth);
              rectNode.x(0);
            }
          } else {
            textNode.width(undefined); // Auto mode
            // In auto mode, simply scaling the rect is a good approximation until render
            if (rectNode) {
              rectNode.width(rectNode.width() * scaleX);
              rectNode.height(rectNode.height() * scaleY);
              rectNode.x(rectNode.x() * scaleX);
              rectNode.y(rectNode.y() * scaleY);
            }
          }
        } else {
          // Auto width mode (width undefined)
          if (rectNode) {
            rectNode.width(rectNode.width() * scaleX);
            rectNode.height(rectNode.height() * scaleY);
            rectNode.x(rectNode.x() * scaleX);
            rectNode.y(rectNode.y() * scaleY);
          }
        }
      }
    }, [trRef]);

    const helperCanvasRef = useRef<HTMLCanvasElement | null>(null);

    React.useLayoutEffect(() => {
      const textNode = textRef.current;
      const rectNode = rectRef.current;
      if (!textNode || !rectNode) return;

      if (esqNameSlotFit) {
        // ESQ fixed "name/date/verse" slots: visually center the glyphs inside the slot.
        // Konva's verticalAlign can still appear off because different fonts have different
        // ascender/descender metrics. We measure actual glyph bounds and shift Y so the
        // bounds are centered within the fixed slot box.
        try {
          if (!fixedEsqClip || !fixedEsqClip.height) return;

          const lines = textNode.textArr || (content || '').split('\n');
          const size = textNode.fontSize();
          const family = textNode.fontFamily();
          const lh = textNode.lineHeight();
          const align = textNode.align();
          const boxW = textNode.width();
          const padding = textNode.padding();

          if (!helperCanvasRef.current) {
            helperCanvasRef.current = document.createElement('canvas');
          }
          const ctx = helperCanvasRef.current.getContext('2d');
          if (!ctx) return;

          ctx.font = `${size}px ${family}`;
          ctx.textBaseline = 'alphabetic';

          let minY = Infinity;
          let maxY = -Infinity;

          lines.forEach((line: any, i: number) => {
            const textStr = typeof line === 'string' ? line : line.text;
            const metrics = ctx.measureText(textStr);

            // Same tuned baseline offset as the non-ESQ measurement path
            const baselineOffset = size * 0.73;
            const lineTop = padding + i * size * lh;
            const lineBaselineY = lineTop + baselineOffset;

            const top = lineBaselineY - metrics.actualBoundingBoxAscent;
            const bottom = lineBaselineY + metrics.actualBoundingBoxDescent;

            if (top < minY) minY = top;
            if (bottom > maxY) maxY = bottom;

            // Keep TS/linters happy (align used in other path)
            void align;
            void boxW;
          });

          if (minY === Infinity) return;

          // Add cushion so strokes/descenders don't touch the outline edge
          const cushion = 2;
          const contentH = Math.max(1, (maxY - minY) + cushion * 2);

          const slotTop = Number(fixedEsqClip.y || 0);
          const slotH = Math.max(1, Number(fixedEsqClip.height || 1));
          const desiredY = contentH <= slotH
            ? slotTop + (slotH - contentH) / 2 - (minY - cushion)
            : slotTop - (minY - cushion);

          if (Math.abs(Number(textNode.y()) - desiredY) > 0.25) {
            textNode.y(desiredY);
          }

          if (rectNode) {
            const rectY = Math.min(slotTop, minY - cushion + desiredY);
            const rectH = Math.max(slotH, contentH);
            if (Math.abs(Number(rectNode.y()) - rectY) > 0.25 || Math.abs(Number(rectNode.height()) - rectH) > 0.25) {
              rectNode.y(rectY);
              rectNode.height(rectH);
            }
          }
          try { textNode.getLayer()?.batchDraw(); } catch { }
        } catch { }
        return;
      }

      const lines = textNode.textArr || (content || '').split('\n');
      const size = textNode.fontSize();
      const family = textNode.fontFamily();
      const lh = textNode.lineHeight();
      const align = textNode.align();
      const boxW = textNode.width();
      const padding = textNode.padding();

      if (!helperCanvasRef.current) {
        helperCanvasRef.current = document.createElement('canvas');
      }
      const ctx = helperCanvasRef.current.getContext('2d');
      if (!ctx) return;

      ctx.font = `${size}px ${family}`;
      ctx.textBaseline = 'alphabetic';

      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      lines.forEach((line: any, i: number) => {
        const textStr = typeof line === 'string' ? line : line.text;
        const metrics = ctx.measureText(textStr);

        // Tuned baseline offset (0.73 seems to match common font metrics better than 0.8)
        const baselineOffset = size * 0.73;
        const lineTop = padding + i * size * lh;
        const lineBaselineY = lineTop + baselineOffset;

        const top = lineBaselineY - metrics.actualBoundingBoxAscent;
        const bottom = lineBaselineY + metrics.actualBoundingBoxDescent;

        const advance = metrics.width;
        let lineX = padding;

        if (align === 'center') {
          lineX = padding + (boxW - advance) / 2;
        } else if (align === 'right') {
          lineX = padding + (boxW - advance);
        }

        const left = lineX - metrics.actualBoundingBoxLeft;
        const right = lineX + metrics.actualBoundingBoxRight;

        if (left < minX) minX = left;
        if (right > maxX) maxX = right;
        if (top < minY) minY = top;
        if (bottom > maxY) maxY = bottom;
      });

      if (minX === Infinity) {
        rectNode.visible(false);
        return;
      }

      minX = Math.floor(minX) - 1;
      maxX = Math.ceil(maxX) + 1;
      minY = Math.floor(minY) - 1;
      maxY = Math.ceil(maxY) + 1;

      const nx = minX, ny = minY, nw = maxX - minX, nh = maxY - minY;
      if (
        rectNode.x() !== nx || rectNode.y() !== ny ||
        rectNode.width() !== nw || rectNode.height() !== nh
      ) {
        rectNode.x(nx);
        rectNode.y(ny);
        rectNode.width(nw);
        rectNode.height(nh);
        rectNode.visible(true);
        try { rectNode.getLayer()?.batchDraw(); } catch (_) { }
      } else {
        rectNode.visible(true);
      }

    }, [content, fontSize, align, renderFontFamily, lineHeight, configuredBoxWidth, esqNameSlotFit]);

    const onTransformEnd = useCallback((e: any) => {
      const node = e.target;
      const textNode = textRef.current;
      if (!textNode) return;

      const newX = node.x();
      const newY = node.y();
      const newRotation = node.rotation();
      const newWidth = textNode.width();
      const newFontSize = Math.min(Math.max(textNode.fontSize(), minFontPx), maxFontPx);

      // Determine if we should save a fixed 'boxWidth'
      const anchor = trRef.current ? trRef.current.getActiveAnchor() : '';
      const isWidthOnly = anchor === 'middle-left' || anchor === 'middle-right';

      let finalBoxWidth = (el as any).boxWidth; // Default to existing state

      if (isWidthOnly) {
        // If side drag, we are forcing Fixed Width mode
        finalBoxWidth = newWidth;
      } else {
        // If corner drag (scaling)
        if ((el as any).boxWidth > 0) {
          // If already fixed, update the fixed width
          finalBoxWidth = newWidth;
        } else {
          // If auto, remains auto (undefined or 0)
          finalBoxWidth = undefined;
        }
      }

      requestAnimationFrame(() => {
        updateDesign(prev => {
          const idx = prev.elements.findIndex(x => x.id === el.id);
          if (idx === -1) return prev;
          return {
            ...prev,
            elements: prev.elements.map((x, i) => i === idx ? {
              ...x,
              x: newX,
              y: newY,
              width: newWidth, // Legacy compat: always save current visual width to 'width'
              rotation: newRotation,
              boxWidth: finalBoxWidth, // Save our smart boxWidth (or undefined)
              properties: {
                ...x.properties,
                fontSize: newFontSize,
                isManualPosition: true,
                isManualSize: true
              }
            } : x)
          };
        });
        try { textLayerRef.current && textLayerRef.current.batchDraw(); } catch (_) { }
      });
    }, [el.id, updateDesign, minFontPx, maxFontPx]);

    const onDragStart = useCallback((e: any) => {
      isDraggingTextRef.current = true;
      e.target.setAttr('opacity', 0.8);
      setGuides([]);
      setMeasureOverlays([]);
    }, []);

    const onDragMove = useCallback((e: any) => {
      e.cancelBubble = true;
      try {
        const node = e.target;
        const textNode = textRef.current;
        const rectNode = rectRef.current;
        if (!textNode) return;

        let desiredLeft = node.x();
        let desiredTop = node.y();
        const curW = textNode.width();
        const curH = textNode.height();

        let visibleBounds: { left: number, top: number, right: number, bottom: number } | undefined;
        try {
          if (rectNode && curW > 0 && curH > 0) {
            const rx = rectNode.x();
            const ry = rectNode.y();
            const rw = rectNode.width();
            const rh = rectNode.height();
            const left = Math.max(0, Math.min(1, rx / curW));
            const top = Math.max(0, Math.min(1, ry / curH));
            const right = Math.max(0, Math.min(1, (rx + rw) / curW));
            const bottom = Math.max(0, Math.min(1, (ry + rh) / curH));
            if (right > left && bottom > top) visibleBounds = { left, top, right, bottom };
          }
        } catch { }

        // Calculate center for clamping
        let desiredCenterX = desiredLeft + curW / 2;
        let desiredCenterY = desiredTop + curH / 2;

        // Compute snap
        const { snappedX, snappedY, guides: g } = computeSnap(el.id, desiredCenterX, desiredCenterY, curW, curH);
        if (Math.abs(snappedX - desiredCenterX) <= SNAP_TOLERANCE) desiredCenterX = snappedX;
        if (Math.abs(snappedY - desiredCenterY) <= SNAP_TOLERANCE) desiredCenterY = snappedY;

        // Safe Zone Check
        const clamped = clampCenterToLayout(desiredCenterX, desiredCenterY, curW, curH, visibleBounds);
        const isPushed = Math.abs(clamped.x - desiredCenterX) > 0.1 || Math.abs(clamped.y - desiredCenterY) > 0.1;
        setSafeZoneHit(isPushed);

        node.x(clamped.x - curW / 2);
        node.y(clamped.y - curH / 2);

        const guidesEmpty = g.length === 0;
        if (!guidesEmpty || !guidesEmptyRef.current) {
          guidesEmptyRef.current = guidesEmpty;
          setGuides(g);
        }
        try { const gl = guidesLayerRef.current; if (gl) gl.batchDraw(); } catch (_) { }
        try {
          const x = desiredCenterX - curW / 2;
          const y = desiredCenterY - curH / 2;
          setSafeWarnVisible(isOutsideSafeZone({ x, y, width: curW, height: curH, visibleBounds }));
        } catch { }

      } catch (_) { }
    }, [computeSnap, el.id, clampCenterToLayout, isOutsideSafeZone]);

    const onDragEnd = useCallback((e: any) => {
      isDraggingTextRef.current = false;
      guidesEmptyRef.current = true; // reset so next drag starts fresh
      e.target.setAttr('opacity', 1);
      setGuides([]);
      setMeasureOverlays([]);
      setSafeZoneHit(false);

      const nx = e.target.x();
      const ny = e.target.y();
      const curW = textRef.current?.width() || 0;
      const curH = textRef.current?.height() || 0;

      let visibleBounds: { left: number, top: number, right: number, bottom: number } | undefined;
      try {
        const rectNode = rectRef.current;
        if (rectNode && curW > 0 && curH > 0) {
          const rx = rectNode.x();
          const ry = rectNode.y();
          const rw = rectNode.width();
          const rh = rectNode.height();
          const left = Math.max(0, Math.min(1, rx / curW));
          const top = Math.max(0, Math.min(1, ry / curH));
          const right = Math.max(0, Math.min(1, (rx + rw) / curW));
          const bottom = Math.max(0, Math.min(1, (ry + rh) / curH));
          if (right > left && bottom > top) visibleBounds = { left, top, right, bottom };
        }
      } catch { }

      try { setSafeWarnVisible(isOutsideSafeZone({ x: nx, y: ny, width: curW, height: curH, visibleBounds })); } catch { }

      requestAnimationFrame(() => updateDesign(prev => {
        const idx = prev.elements.findIndex(x => x.id === el.id);
        if (idx === -1) return prev;
        const curr = prev.elements[idx];
        return {
          ...prev, elements: prev.elements.map((x, i) => i === idx ? {
            ...x,
            x: nx,
            y: ny,
            properties: { ...curr.properties, isManualPosition: true }
          } : x)
        };
      }));
    }, [updateDesign, el.id, isOutsideSafeZone]);

    const onClick = useCallback((e: any) => {
      e.cancelBubble = true;
      const now = Date.now();
      const isDouble = (lastTextGroupClick.id === el.id && now - lastTextGroupClick.time < 300);
      lastTextGroupClick = { id: el.id, time: now };
      const fieldName = String(el.properties?.fieldName || '').toLowerCase();
      const baseId = String(el.id || '').replace(/-back$/, '').toLowerCase();
      const isDateField =
        ['dob', 'dod', 'weddingdate', 'p2_dob', 'p2_dod'].includes(baseId) ||
        ['dob', 'dod', 'weddingdate', 'p2_dob', 'p2_dod'].includes(fieldName);

      if (isDouble) {
        if (designMode === 'ESQ' && el.properties?.esqTextReadOnly) {
          return;
        }
        if (isDateField) {
          setEsqDatePicker({
            elementId: el.id,
            value: parseEsqDateContentToInput(String(el.properties?.content || ''), fieldName)
          });
          return;
        }
        // IMPORTANT: Do not mutate ESQ fixed text geometry on double-click.
        // Opening editor must not cause tiny snap/reposition.

        setEditingTextId(el.id);
        setSelectedArtworkId(null);
        setSelectedItemIds([]);
        const stage = e.target.getStage();
        const containerRect = stage.container().getBoundingClientRect();
        const s = scale || 1;
        let screenX = containerRect.left + renderX * s;
        let screenY = containerRect.top + renderY * s;
        let slot: { widthPx: number; heightPx: number } | undefined;
        if (esqNameSlotFit && fixedEsqClip) {
          screenX = containerRect.left + (renderX + fixedEsqClip.x) * s;
          screenY = containerRect.top + (renderY + fixedEsqClip.y) * s;
          slot = {
            widthPx: fixedEsqClip.width * s,
            heightPx: fixedEsqClip.height * s,
          };
        } else {
          screenX = screenX;
          screenY = screenY;
        }
        setTextEditorPos({ left: screenX, top: screenY });
        setTextEditor({
          fontFamily: renderFontFamily,
          fontSize: Math.round(computedFontSize * s * 1000) / 1000,
          lineHeight: esqNameSlotFit ? 1 : lineHeight || 1,
          letterSpacing: Math.round((letterSpacing || 0) * s * 1000) / 1000,
          ...(slot ? { slot } : {}),
        });

        // ESQ: show selected fixed field/artwork position outline while editing.
        const bounds = el.properties?.esqBounds;
        if (designMode === 'ESQ' && bounds && (bounds.h1 !== undefined)) {
          setSampleGuideLines({
            h1: Number(bounds.h1),
            h2: Number(bounds.h2),
            v1: Number(bounds.v1),
            v2: Number(bounds.v2),
            visible: true
          });
        }

      } else {
        requestAnimationFrame(() => {
          setSelectedArtworkId(el.id);
          setSelectedItemIds([el.id]);
          setPersistentAreaSelection(null);
          setAreaSelectActive(false);

          // ESQ: clicking fixed template text/artwork should show its position outline.
          const bounds = el.properties?.esqBounds;
          if (designMode === 'ESQ' && bounds && (bounds.h1 !== undefined)) {
            setSampleGuideLines({
              h1: Number(bounds.h1),
              h2: Number(bounds.h2),
              v1: Number(bounds.v1),
              v2: Number(bounds.v2),
              visible: true
            });
          }

          try { staticLayerRef.current && staticLayerRef.current.batchDraw(); } catch (_) { }
          try { textLayerRef.current && textLayerRef.current.batchDraw(); } catch (_) { }
          try { activeLayerRef.current && activeLayerRef.current.batchDraw(); } catch (_) { }
        });
      }
    }, [el.id, el.type, el.properties?.fieldName, el.properties?.content, el.properties?.isFixedPosition, renderX, renderY, scale, renderFontFamily, computedFontSize, lineHeight, letterSpacing, updateDesign, pixelsPerInch, compositeProps, graniteTemplate, graniteConfig, clampFixedEsqElementToOwnBounds, esqNameSlotFit, fixedEsqClip, designMode, parseEsqDateContentToInput, setSampleGuideLines]);


    return (
      <Group
        id={el.id}
        x={renderX}
        y={renderY}
        rotation={el.rotation}
        // Fixed ESQ text elements must never be hard clipped so descenders and multi-line verses render completely
        clipX={undefined}
        clipY={undefined}
        clipWidth={undefined}
        clipHeight={undefined}
        draggable={!el.properties?.isFixedPosition && el.properties?.draggable !== false}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onTransform={onTransform}
        onTransformEnd={onTransformEnd}
        onClick={onClick}
        onTap={onClick}
      >
        {/* Bounding-box Rect: position/size are set imperatively via rectRef in the
            useLayoutEffect above — no React state involved so no re-renders on update. */}
        <Rect
          ref={rectRef}
          x={esqNameSlotFit ? (fixedEsqClip?.x ?? 0) : 0}
          y={esqNameSlotFit ? (fixedEsqClip?.y ?? 0) : 0}
          width={esqNameSlotFit ? (fixedEsqClip?.width ?? 0) : 0}
          height={esqNameSlotFit ? (fixedEsqClip?.height ?? 0) : 0}
          visible={esqNameSlotFit}
          fill="transparent"
          listening={false}
          strokeEnabled={false}
        />
        <Text
          ref={textRef}
          x={esqNameSlotFit ? (fixedEsqClip?.x ?? 0) : 0}
          y={esqNameSlotFit ? (fixedEsqClip?.y ?? 0) : 0}
          offsetX={0}
          offsetY={0}
          text={
            shouldAllowMixedCase
              ? (String(content || '').includes('\n') ? String(content || '') : String(content || '').replace(/\r?\n+/g, ' '))
              : (String(content || '').includes('\n') ? String(content || '').toUpperCase() : String(content || '').replace(/\r?\n+/g, ' ').toUpperCase())
          }
          fontSize={Math.max(1, computedFontSize || 1)}
          fontFamily={renderFontFamily}
          width={esqNameSlotFit ? Math.max(1, esqNameBoxW || 1) : (configuredBoxWidth && configuredBoxWidth > 0 ? configuredBoxWidth : undefined)}
          height={undefined}
          verticalAlign={undefined}
          padding={esqKonvaPadding}
          fill={fillColor}
          align={align || 'center'}
          wrap={String(content || '').includes('\n') ? 'word' : (esqNameSlotFit ? 'none' : 'word')}
          lineHeight={String(content || '').includes('\n') ? (lineHeight || 1.1) : (esqNameSlotFit ? 1 : (lineHeight || 1.1))}
          letterSpacing={letterSpacing || 0}
          stroke={outline ? '#000000' : (isGrey ? fillColor : undefined)}
          strokeWidth={outline ? textOutlineWidth : (isGrey ? Math.max(0.35, (computedFontSize || 1) * 0.018) : 0)}
          strokeScaleEnabled={false}
          shadowColor={!outline && isGrey ? fillColor : undefined}
          shadowBlur={!outline && isGrey ? 0.15 : 0}
          shadowOpacity={!outline && isGrey ? 1 : 0}
          shadowOffset={{ x: 0, y: 0 }}
          lineJoin="round"
          lineCap="round"
          perfectDrawEnabled={true}
          visible={
            isFontReady &&
            editingTextId !== el.id &&
            Boolean(String(content || '').trim()) &&
            Boolean(computedFontSize && computedFontSize > 0)
          }
        />
      </Group>
    );
  });

  const showContextMenu = useCallback((e: any, artworkId: string) => {
    e.evt.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    const cx = e.evt.clientX;
    const cy = e.evt.clientY;
    if (rect) {
      setContextMenu({
        visible: true,
        x: cx - rect.left,
        y: cy - rect.top,
        artworkId
      });
    } else {
      setContextMenu({ visible: true, x: cx, y: cy, artworkId });
    }
  }, []);

  const hideContextMenu = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, artworkId: '' });
  }, []);

  const showTextContextMenu = useCallback((e: any, textId: string) => {
    e.evt.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    const cx = e.evt.clientX;
    const cy = e.evt.clientY;
    if (rect) {
      setTextContextMenu({ visible: true, x: cx - rect.left, y: cy - rect.top, textId });
    } else {
      setTextContextMenu({ visible: true, x: cx, y: cy, textId });
    }
  }, []);

  const hideTextContextMenu = useCallback(() => {
    setTextContextMenu({ visible: false, x: 0, y: 0, textId: '' });
  }, []);

  // Filter elements based on active side and incompatible artwork based on granite color
  const filteredElements = useMemo(() => {
    const activeSide = currentDesign.activeSide || 'front';

    // 1. Filter by side first (Template/Base shown on both sides)
    let elements = currentDesign.elements.filter(el => {
      const role = String(el.properties?.role || el.properties?.kind || '').toLowerCase();
      if (role === 'template' || role === 'base' || el.properties?.isTemplate === true) {
        return true;
      }

      // If it's a vase, let's show it on both sides as they are on the base
      if (role === 'vase') return true;

      const elSide = el.side || 'front';
      return elSide === activeSide;
    });

    // 2. Filter by granite color compatibility
    const colorId = Number(selectedGraniteMeta?.colorId);

    if (colorId === 14) {
      // For black granite (PJB), only show Laser artwork
      return elements.filter(el => {
        if (el.type !== 'artwork' || el.properties?.role === 'template') return true;
        const role = String(el.properties?.role || '').toLowerCase();
        if (role === 'vase' || role === 'work' || role === 'porcelain' || role === 'esq_fixed_artwork' || el.properties?.isCustomUpload === true) return true;
        const artworkType = el.properties?.type;
        const graniteType = String(el.properties?.graniteType || '').toUpperCase();
        return artworkType === 'Laser' || graniteType === 'PJB' || !graniteType;
      });
    } else if (colorId === 12) {
      // For grey granite, only show Sandblast artwork
      return elements.filter(el => {
        if (el.type !== 'artwork' || el.properties?.role === 'template') return true;
        const role = String(el.properties?.role || '').toLowerCase();
        if (role === 'vase' || role === 'work' || role === 'porcelain' || role === 'esq_fixed_artwork' || el.properties?.isCustomUpload === true) return true;
        const artworkType = el.properties?.type;
        const graniteType = String(el.properties?.graniteType || '').toUpperCase();
        return artworkType === 'Sandblast' || graniteType === 'GREY' || graniteType === 'GRAY' || graniteType === 'MG' || !graniteType;
      });
    } else {
      return elements;
    }
  }, [currentDesign.elements, currentDesign.activeSide, selectedGraniteMeta?.colorId]);

  // Artwork-only list and stable signature so text-only changes don't rerender artworks layer
  const filteredArtwork = useMemo(() => filteredElements.filter(e => e.type === 'artwork'), [filteredElements]);
  const artworkSignature = useMemo(() => {
    try {
      return filteredArtwork.map(e => {
        const p = e.properties || {};
        const fx = e.flipX ? '1' : '0';
        const fy = e.flipY ? '1' : '0';
        const vvid = p.vaseVariantId || '';
        return `${e.id}:${e.x}:${e.y}:${e.width}:${e.height}:${e.rotation}:${fx}:${fy}:${p.src || ''}:${p.role || ''}:${p.isTemplate ? '1' : '0'}:${vvid}`;
      }).join('|');

    } catch { return String(filteredArtwork.length); }
  }, [filteredArtwork]);

  const nonSelectedArtworkElements = useMemo(() => {
    // Render all non-template artwork elements except porcelain. Keep selected element in-place to avoid layer churn.
    const elements = filteredArtwork.filter(e =>
      !isVase(e) &&
      String(e.properties?.role || '').toLowerCase() !== 'template' &&
      String(e.properties?.role || '').toLowerCase() !== 'porcelain'
    );

    return elements.map(e => (
      <ArtworkNode
        key={e.id}
        el={e}
        onRightClick={showContextMenu}
        listening={true}
        perfect={false}
        isSelected={false}
      />
    ));
  },
    [artworkSignature, showContextMenu]
  );

  const porcelainElements = useMemo(() => {
    // Render all porcelain elements.
    const elements = filteredArtwork.filter(e => String(e.properties?.role || '').toLowerCase() === 'porcelain');

    return elements.map(e => (
      <ArtworkNode
        key={e.id}
        el={e}
        onRightClick={showContextMenu}
        listening={true}
        perfect={false}
        isSelected={false}
      />
    ));
  },
    [artworkSignature, showContextMenu]
  );

  const vaseElements = useMemo(() => {
    const ppiOk = Number(pixelsPerInch) > 0;
    const base = (compositeProps && (compositeProps as any).base) ? (compositeProps as any).base : null;
    const baseOk = Boolean(base && isFinite(base.x) && isFinite(base.y) && isFinite(base.width) && isFinite(base.height));
    if (!ppiOk || !baseOk) return [];

    const elements = filteredArtwork.filter(e => isVase(e));

    return elements.map(e => (

      <VaseElement
        key={e.id}
        el={e}
        stageSize={stageSize}
        graniteTemplate={graniteTemplate}
        compositeProps={compositeProps}
        pixelsPerInch={pixelsPerInch}
        isSerptopRightOnly={isSerptopRightOnly}
        selectedArtworkId={selectedArtworkId}
        onSelect={setSelectedArtworkId}
        updateDesign={updateDesign}
        imageCache={imageCacheRef.current}
        onAssetLoaded={(window as any).__designer_onAssetLoaded}
        onRightClick={showContextMenu}
      />

    ));
  }, [artworkSignature, pixelsPerInch, compositeProps, stageSize, graniteTemplate, isSerptopRightOnly, selectedArtworkId, updateDesign]);


  const textElements = useMemo(() => {
    return filteredElements
      .filter(e => e.type === 'text')
      .map(e => (
        <TextNode
          key={e.id}
          el={e}
          selected={selectedArtworkId === e.id}
          isEditing={editingTextId === e.id}
          graniteColorId={selectedGraniteMeta?.colorId}
          onRightClick={showTextContextMenu}
          trRef={trRef}
        />
      ));
  }, [filteredElements, selectedArtworkId, editingTextId, fontsLoaded, trRef]);

  const deselectAll = useCallback(() => {
    // Batch the deselection update to prevent flickering
    requestAnimationFrame(() => {
      setSelectedArtworkId(null);
      setSelectedItemIds([]); // Clear area selection
      setPersistentAreaSelection(null); // Clear persistent area selection
      setAreaSelectActive(false); // Reset area select mode
      hideContextMenu();
      hideTextContextMenu();
      // Hide ESQ selection outlines
      setSampleGuideLines(prev => ({ ...prev, visible: false }));
    });
  }, [setSelectedArtworkId, setSelectedItemIds, setPersistentAreaSelection, setAreaSelectActive, hideContextMenu, hideTextContextMenu, setSampleGuideLines]);
  // Keep ref in sync for use in early event listeners
  deselectAllRef.current = deselectAll;

  const handleStageClick = (e: any) => {
    const stage = e.target.getStage();
    const target = e.target;

    // Handle clicks on the stage background itself
    if (target === stage) {
      deselectAll();
      return;
    }

    const id = target.id();
    const el = id ? currentDesign.elements.find((x: any) => x.id === id) : null;

    // If we clicked on something that isn't a design element, deselect
    if (!el) {
      deselectAll();
      return;
    }

    // Special logic for parents with inner artworks (like SL12)
    if (el.properties?.isSL12) {
      const pos = stage.getPointerPosition();
      if (pos) {
        const inv = 1 / (scale || 1);
        const ix = pos.x * inv;
        const iy = pos.y * inv;

        const inners = currentDesign.elements.filter(x =>
          x.properties?.isInnerArtwork &&
          x.properties?.parentId === el.id &&
          ix >= x.x && ix <= x.x + x.width &&
          iy >= x.y && iy <= x.y + x.height
        );

        if (inners.length > 0) {
          const inner = inners[inners.length - 1];
          setSelectedArtworkId(inner.id);
          setSelectedItemIds([inner.id]);
          return;
        }
      }
    }

    // Clicks on template or stone base elements should deselect the active foreground item
    const isTemplateOrBase = el && (
      String(el.properties?.role || '').toLowerCase() === 'template' ||
      String(el.properties?.role || '').toLowerCase() === 'base' ||
      el.properties?.isTemplate === true
    );

    if (isTemplateOrBase) {
      deselectAll();
    }
  };

  const handleStageMouseDown = (e: any) => {
    const target = e.target;
    const stage = e.target.getStage();

    if (target === stage) {
      deselectAll();
    } else {
      const id = target.id();
      const el = id ? currentDesign.elements.find((x: any) => x.id === id) : null;
      const isTemplateOrBase = el && (
        String(el.properties?.role || '').toLowerCase() === 'template' ||
        String(el.properties?.role || '').toLowerCase() === 'base' ||
        el.properties?.isTemplate === true
      );
      if (isTemplateOrBase) {
        deselectAll();
      }
    }
  };

  useEffect(() => {
    const layer = staticLayerRef.current;
    if (!layer) return;
    try {
      // Always keep layer listening so clicks on other text elements work
      layer.listening(true);
      layer.batchDraw();
    } catch { }
  }, [selectedArtworkId]);

  const selectedArtwork = useMemo(() => {
    if (!selectedArtworkId) return null;
    const artwork = currentDesign.elements.find(el => el.id === selectedArtworkId);

    // Sync transformer if selection changes
    if (trRef.current && stageRef.current) {
      // This is handled in a separate effect for robustness, but we can double check here or leave it to the effect below
    }
    return artwork;
  }, [selectedArtworkId, currentDesign.elements]);

  // Sync transformer nodes with selection
  useEffect(() => {
    if (!trRef.current) return;
    const stage = stageRef.current && stageRef.current.getStage ? stageRef.current.getStage() : stageRef.current;
    if (!stage) return;

    const nodes: any[] = [];
    if (selectedArtworkId) {
      const node = stage.findOne('#' + selectedArtworkId);
      if (node) nodes.push(node);
    }

    trRef.current.nodes(nodes);
    trRef.current.getLayer().batchDraw();
  }, [selectedArtworkId, currentDesign.elements]);

  const flipHorizontal = useCallback(() => {
    if (!selectedArtwork) return;
    const id = selectedArtwork.id;
    // Batch the update to prevent flickering
    requestAnimationFrame(() => {
      updateDesign(prev => {
        const elementIndex = prev.elements.findIndex(x => x.id === selectedArtwork.id);
        if (elementIndex === -1) return prev;

        const newElements = prev.elements.map((element, index) =>
          index === elementIndex
            ? { ...element, flipX: !element.flipX }
            : element
        );

        return { ...prev, elements: newElements };
      });
      // Force a redraw on next frames so Konva doesn't keep cached visuals
      try {
        requestAnimationFrame(() => {
          try {
            const stage = stageRef && (stageRef as any).current;
            const node = stage && stage.findOne && id ? stage.findOne(`#${id}`) : null;
            if (node) {
              try { node.clearCache(); } catch (_) { }
              const layer = node.getLayer();
              if (layer) {
                try { layer.clearCache(); } catch (_) { }
                try { layer.batchDraw(); } catch (_) { }
              }
            }
            if (stage && !isTransformingRef.current && typeof stage.batchDraw === 'function') {
              try { stage.batchDraw(); } catch (_) { }
            }
          } catch (_) { }
        });
      } catch (_) { }
    });
  }, [selectedArtwork, updateDesign]);

  const flipVertical = useCallback(() => {
    if (!selectedArtwork) return;
    const id = selectedArtwork.id;
    // Batch the update to prevent flickering
    requestAnimationFrame(() => {
      updateDesign(prev => {
        const elementIndex = prev.elements.findIndex(x => x.id === selectedArtwork.id);
        if (elementIndex === -1) return prev;

        const newElements = prev.elements.map((element, index) =>
          index === elementIndex
            ? { ...element, flipY: !element.flipY }
            : element
        );

        return { ...prev, elements: newElements };
      });
      // Force a redraw on next frames so Konva doesn't keep cached visuals
      try {
        requestAnimationFrame(() => {
          try {
            const stage = stageRef && (stageRef as any).current;
            const node = stage && stage.findOne && id ? stage.findOne(`#${id}`) : null;
            if (node) {
              try { node.clearCache(); } catch (_) { }
              const layer = node.getLayer();
              if (layer) {
                try { layer.clearCache(); } catch (_) { }
                try { layer.batchDraw(); } catch (_) { }
              }
            }
            if (stage && !isTransformingRef.current && typeof stage.batchDraw === 'function') {
              try { stage.batchDraw(); } catch (_) { }
            }
          } catch (_) { }
        });
      } catch (_) { }
    });
  }, [selectedArtwork, updateDesign]);

  const getSelectedId = () => selectedArtwork?.id || (selectedItemIds.length > 0 ? selectedItemIds[0] : null) || contextMenu.artworkId || null;

  // Opens the Art panel targeting the same slot as the right-clicked artwork so
  // the user can pick a replacement from the full artwork grid.
  const replaceArtwork = useCallback(() => {
    const id = contextMenu.artworkId || selectedArtwork?.id;
    if (!id) return;
    const artwork = currentDesign.elements.find(el => el.id === id);
    if (!artwork) return;
    const slotType: string = artwork.properties?.slotType || artwork.properties?.placement || '';
    const validSlotTypes = ['corner', 'corner_left', 'corner_right', 'left', 'top', 'special', 'porcelain'];
    if (!slotType || artwork.properties?.isCustomUpload || !validSlotTypes.includes(slotType)) {
      setActivePlacementSlot(null);
      setOpenModal('art');
      hideContextMenu();
      return;
    }

    const matchingSlot = placementSlots.find(s => s.slot_type === slotType)
      ?? { slot_type: slotType, placement_code: artwork.properties?.placement } as any;

    setActivePlacementSlot(matchingSlot);
    setOpenModal('art');
    hideContextMenu();
  }, [contextMenu.artworkId, selectedArtwork, currentDesign.elements, placementSlots, setActivePlacementSlot, setOpenModal, hideContextMenu]);

  const copyArtwork = useCallback(() => {
    const selId = getSelectedId();
    if (!selId) return;
    const artwork = currentDesign.elements.find(el => el.id === selId);
    if (!artwork) return;

    const role = String(artwork.properties?.role || artwork.properties?.kind || '').toLowerCase();
    if (role === 'porcelain') return;

    // Special handling for vase elements
    if (role === 'vase') {
      // Find the granite template to determine positioning
      const graniteTemplate = currentDesign.elements.find(el =>
        el.type === 'artwork' && el.properties?.role === 'template'
      );

      if (!graniteTemplate) {
        hideContextMenu();
        return;
      }

      // Get current vase side
      const currentSide = String((artwork.properties as any)?.side || 'left').toLowerCase();

      // Check if there's already a vase on the opposite side
      const existingVases = currentDesign.elements.filter(el =>
        el.type === 'artwork' && el.properties?.role === 'vase'
      );

      const oppositeSide = currentSide === 'left' ? 'right' : 'left';
      const hasOppositeVase = existingVases.some(vase =>
        String((vase.properties as any)?.side || 'left').toLowerCase() === oppositeSide
      );

      // If there's already a vase on the opposite side, don't copy
      if (hasOppositeVase) {
        hideContextMenu();
        return;
      }

      if (isSerptopRightOnly) {
        hideContextMenu();
        return;
      }

      const newVaseAuto = {
        ...artwork,
        id: `vase-${oppositeSide}-${Date.now()}`,
        properties: { ...artwork.properties, side: oppositeSide, autosnap: true, fromSaved: true, draggable: false }
      };

      requestAnimationFrame(() => {
        updateDesign(prev => ({
          ...prev,
          elements: [...prev.elements, newVaseAuto]
        }));
        hideContextMenu();
      });

      return;
    }

    // Default behavior for non-vase elements
    let newArtwork = {
      ...artwork,
      id: `art-${Date.now()}`,
      x: artwork.x,
      y: artwork.y,
      properties: artwork.properties
    };

    // Find the nearest non-overlapping nearby spot, clamped to layout guides when available
    try {
      const tryOffsets: number[] = [2, 4, 6, 8, 10, 12, 16, 20, 24, 28, 32];
      let placed = false;
      for (const s of tryOffsets) {
        const candidates = [
          { dx: s, dy: s }, { dx: s, dy: 0 }, { dx: 0, dy: s }, { dx: -s, dy: s },
          { dx: s, dy: -s }, { dx: -s, dy: 0 }, { dx: 0, dy: -s }, { dx: -s, dy: -s }
        ];
        for (const cnd of candidates) {
          let cx = artwork.x + cnd.dx;
          let cy = artwork.y + cnd.dy;
          if (layoutGuides) {
            const halfW = Math.max(0, Math.floor(newArtwork.width / 2));
            const halfH = Math.max(0, Math.floor(newArtwork.height / 2));
            const cc = clampCenterToLayout(cx + halfW, cy + halfH, newArtwork.width, newArtwork.height);
            cx = Math.floor(cc.x - halfW);
            cy = Math.floor(cc.y - halfH);
          }
          const probe = { ...newArtwork, x: cx, y: cy } as any;
          if (!overlapsAny(probe, currentDesign.elements)) {
            newArtwork.x = cx;
            newArtwork.y = cy;
            placed = true;
            break;
          }
        }
        if (placed) break;
      }

      // Fallback: scan a light grid within layout guides to find first free slot
      if (!placed) {
        if (layoutGuides) {
          const step = 10;
          outer: for (let gy = layoutGuides.top; gy <= layoutGuides.bottom - newArtwork.height; gy += step) {
            for (let gx = layoutGuides.left; gx <= layoutGuides.right - newArtwork.width; gx += step) {
              const probe = { ...newArtwork, x: Math.floor(gx), y: Math.floor(gy) } as any;
              if (!overlapsAny(probe, currentDesign.elements)) {
                newArtwork.x = probe.x;
                newArtwork.y = probe.y;
                placed = true;
                break outer;
              }
            }
          }
        }
        // If still not placed, as a last resort keep a small offset and clamp
        if (!placed) {
          let cx = artwork.x + 6; let cy = artwork.y + 6;
          if (layoutGuides) {
            const halfW = Math.max(0, Math.floor(newArtwork.width / 2));
            const halfH = Math.max(0, Math.floor(newArtwork.height / 2));
            const cc = clampCenterToLayout(cx + halfW, cy + halfH, newArtwork.width, newArtwork.height);
            cx = Math.floor(cc.x - halfW);
            cy = Math.floor(cc.y - halfH);
          }
          newArtwork.x = cx; newArtwork.y = cy;
        }
      }
    } catch (_) { }

    // Batch the update to prevent flickering
    requestAnimationFrame(() => {
      updateDesign(prev => ({
        ...prev,
        elements: [...prev.elements, newArtwork]
      }));
      hideContextMenu();
    });
  }, [selectedArtwork, contextMenu.artworkId, currentDesign.elements, updateDesign, hideContextMenu, graniteConfig, pixelsPerInch]);

  const mirrorArtwork = useCallback(() => {
    const id = getSelectedId();
    if (!id) return;
    const artwork = currentDesign.elements.find(el => el.id === id);
    if (!artwork) return;
    const role = String(artwork.properties?.role || artwork.properties?.kind || '').toLowerCase();
    // Do not allow mirror on vase elements
    if (role === 'vase') {
      hideContextMenu();
      return;
    }
    // Batch the update to prevent flickering
    requestAnimationFrame(() => {
      updateDesign(prev => {
        const elementIndex = prev.elements.findIndex(x => x.id === id);
        if (elementIndex === -1) return prev;

        const newElements = prev.elements.map((element, index) =>
          index === elementIndex
            ? { ...element, flipX: !element.flipX }
            : element
        );

        return { ...prev, elements: newElements };
      });
      hideContextMenu();
    });

    // Clear caches and force Konva redraw on next animation frame so the
    // mirrored image appears immediately (prevents needing to click elsewhere).
    try {
      requestAnimationFrame(() => {
        try {
          const stage = stageRef && (stageRef as any).current;
          const node = stage && stage.findOne && id ? stage.findOne(`#${id}`) : null;
          if (node) {
            try { node.clearCache(); } catch (_) { }
            const layer = node.getLayer && node.getLayer();
            if (layer) {
              try { layer.clearCache(); } catch (_) { }
              try { layer.batchDraw(); } catch (_) { }
            }
          }
          if (stage && typeof stage.batchDraw === 'function') {
            try { stage.batchDraw(); } catch (_) { }
          }
        } catch (_) { }
      });
    } catch (_) { }
  }, [selectedArtwork, contextMenu.artworkId, updateDesign, hideContextMenu]);

  const flipArtwork = useCallback(() => {
    const id = getSelectedId();
    if (!id) return;
    const artwork = currentDesign.elements.find(el => el.id === id);
    if (!artwork) return;
    const role = String(artwork.properties?.role || artwork.properties?.kind || '').toLowerCase();
    // Do not allow flip on vase elements
    if (role === 'vase') {
      hideContextMenu();
      return;
    }
    // Batch the update to prevent flickering
    requestAnimationFrame(() => {
      updateDesign(prev => {
        const elementIndex = prev.elements.findIndex(x => x.id === id);
        if (elementIndex === -1) return prev;

        const newElements = prev.elements.map((element, index) =>
          index === elementIndex
            ? { ...element, flipY: !element.flipY }
            : element
        );

        return { ...prev, elements: newElements };
      });
      hideContextMenu();
    });

    // Force Konva redraw so the flip is visible immediately.
    try {
      requestAnimationFrame(() => {
        try {
          const stage = stageRef && (stageRef as any).current;
          const node = stage && stage.findOne && id ? stage.findOne(`#${id}`) : null;
          if (node) {
            try { node.clearCache(); } catch (_) { }
            const layer = node.getLayer && node.getLayer();
            if (layer) {
              try { layer.clearCache(); } catch (_) { }
              try { layer.batchDraw(); } catch (_) { }
            }
          }
          if (stage && typeof stage.batchDraw === 'function') {
            try { stage.batchDraw(); } catch (_) { }
          }
        } catch (_) { }
      });
    } catch (_) { }
  }, [selectedArtwork, contextMenu.artworkId, updateDesign, hideContextMenu]);

  const bringToFront = useCallback(() => {
    const id = getSelectedId();
    if (!id) return;
    // Batch the update to prevent flickering
    requestAnimationFrame(() => {
      updateDesign(prev => {
        const elementIndex = prev.elements.findIndex(x => x.id === id);
        if (elementIndex === -1) return prev;

        const element = prev.elements[elementIndex];
        const newElements = [
          ...prev.elements.filter((_, index) => index !== elementIndex),
          element
        ];

        return { ...prev, elements: newElements };
      });
      hideContextMenu();
    });
  }, [selectedArtwork, contextMenu.artworkId, updateDesign, hideContextMenu]);

  const deleteArtwork = useCallback(() => {
    const id = getSelectedId();

    if (!id) {
      return;
    }

    try {

      updateDesign(prev => {
        const nextElements = prev.elements.filter(el => el.id !== id);
        return { ...prev, elements: nextElements };
      });

      // Clear selection states immediately

      setSelectedArtworkId(null);
      setSelectedItemIds([]);
      setPersistentAreaSelection(null);
      hideContextMenu();
      hideTextContextMenu();

      // Force draw stage
      try {
        const stage = stageRef.current?.getStage?.() || stageRef.current;
        if (stage) stage.batchDraw();
      } catch (_) { }

    } catch (err) {
    }
  }, [updateDesign, hideContextMenu, hideTextContextMenu, getSelectedId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenu.visible) {
        hideContextMenu();
      }
      if (textContextMenu.visible) {
        hideTextContextMenu();
      }
    };

    if (contextMenu.visible || textContextMenu.visible) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu.visible, textContextMenu.visible, hideContextMenu, hideTextContextMenu]);



  // Composite/base positioning

  // Custom canvas rendering for granite image - DISABLED TO PREVENT SHADOW ISSUES
  useEffect(() => {
    // Disabled to prevent shadow issues
    return;
  }, [graniteImageProps, computeStageWidth, computeStageHeight]);

  // Handle granite image not available scenario (for shape/color combos)
  useEffect(() => {
    if (designMode === 'ESQ') return;
    if (graniteNotAvailable) {
      if (window.confirm('Selected type does not exist\n\nWould you like to reset to the default (grey) granite?')) {
        // Remove shape/model/template element and reset to default granite
        // Batch the update to prevent flickering
        requestAnimationFrame(() => {
          updateDesign(prev => ({
            ...prev,
            elements: prev.elements.filter(
              el => !(el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate))
            )
          }));
          if (setSelectedGraniteImage) setSelectedGraniteImage(null);
          setSelectedGraniteMeta(null);
        });
      }
      setGraniteNotAvailable(false);
    }
  }, [graniteNotAvailable, setSelectedGraniteImage, setSelectedGraniteMeta, updateDesign]);

  // When color changes, try to find matching granite image for selectedGraniteMeta (shape/color combo)
  useEffect(() => {
    if (designMode === 'ESQ') return;
    if (!selectedGraniteMeta) return;
    // If color changed, try to fetch granite image for new color
    if (
      currentDesign.selectedColorId &&
      currentDesign.selectedColorId !== selectedGraniteMeta.colorId
    ) {
      // Clear guides immediately so previous color's guides don't linger
      try { setLayoutGuides(null); } catch (_) { }
      try { setGraniteConfig(null); } catch (_) { }

      const reqId = ++colorFetchReqIdRef.current;
      fetchModelDetails(selectedGraniteMeta.modelId, currentDesign.selectedColorId as number)
        .then(data => {
          if (reqId !== colorFetchReqIdRef.current) return; // stale
          const sizes = Array.isArray(data.sizes) ? data.sizes : [];
          const match = sizes.find((s: any) => s.id === selectedGraniteMeta.sizeId);
          if (match && match.preview_image) {
            const imgSrc = buildApiUrl(`/api/uploads/${String(match.preview_image).replace(/^\/?uploads\//, '')}`);

            // Only clear selectedGraniteImage if the new src is different from current
            // This prevents flicker when the same image is already loaded (e.g., on initial load)
            if (selectedGraniteImage && selectedGraniteImage !== imgSrc) {
              if (setSelectedGraniteImage) setSelectedGraniteImage(null);
            }

            setSelectedGraniteMeta({
              ...selectedGraniteMeta,
              colorId: currentDesign.selectedColorId ?? 0,
              src: imgSrc
            });
            setGraniteNotAvailable(false);

            // Update template/model element src in design
            updateDesign(prev => {
              let nextElements = prev.elements.map(el =>
                el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate)
                  ? { ...el, properties: { ...el.properties, src: imgSrc, colorId: currentDesign.selectedColorId } }
                  : el
              );
              // If new color is black (PJB, colorId === 14), remove Sandblast artworks
              if (currentDesign.selectedColorId === 14) {
                nextElements = nextElements.filter(el =>
                  !(el.type === 'artwork' && el.properties?.role !== 'template' && el.properties?.type === 'Sandblast')
                );
              }
              return { ...prev, elements: nextElements };
            });
          } else {
            // If granite image not found, still remove Sandblast artwork if color is black
            if (currentDesign.selectedColorId === 14) {
              // Batch the update to prevent flickering
              requestAnimationFrame(() => {
                updateDesign(prev => ({
                  ...prev,
                  elements: prev.elements.filter(
                    el => !(el.type === 'artwork' && el.properties?.role !== 'template' && el.properties?.type === 'Sandblast')
                  )
                }));
              });
            }
            setGraniteNotAvailable(true);
          }
        })
        .catch(error => {
          if ((error as any)?.name === 'AbortError') return; // cancelled
          setGraniteNotAvailable(true);
        });
    }
  }, [currentDesign.selectedColorId, selectedGraniteMeta, setSelectedGraniteImage, setSelectedGraniteMeta, updateDesign, selectedGraniteImage]);

  useEffect(() => {
    let t: number | null = null;

    // If a saved design is currently being applied, skip the check entirely.
    if (typeof isApplyingDesign !== 'undefined' && isApplyingDesign) return;
    if (suppressDefaultGranite) return;

    // Debounce a little to allow related state (e.g. template/image restore) to finish.
    t = window.setTimeout(() => {
      const colorId = selectedGraniteMeta?.colorId ?? (currentDesign as any)?.selectedColorId;
      const colorName = sharedColors.find(c => Number(c.id) === Number(colorId))?.name || (currentDesign as any).selectedColorName;
      const graniteType = getGraniteForColor(Number(colorId) || null, colorName);
      if (!graniteType) {
        setShowGraniteArtRestriction(false);
        return;
      }

      const invalidArt = currentDesign.elements.find(el =>
        el.type === 'artwork' &&
        el.properties?.role !== 'template' &&
        (
          (graniteType === 'GREY' && el.properties?.type !== 'Sandblast') ||
          (graniteType === 'PJB' && el.properties?.type !== 'Laser')
        )
      );

      if (invalidArt) {
        setShowGraniteArtRestriction(false);
      } else {
        setShowGraniteArtRestriction(false);
      }
    }, 220);

    return () => { if (t) window.clearTimeout(t); };
  }, [currentDesign.elements, selectedGraniteMeta, isApplyingDesign, suppressDefaultGranite]);
  const prevApplyingRef = useRef<boolean>(false);
  const lastApplyingEndAtRef = useRef<number>(0);

  // Record when applying just ended to suppress loadingImage-driven overlay for a short window
  useEffect(() => {
    const curr = Boolean(isApplyingDesign);
    const prev = prevApplyingRef.current;
    if (prev && !curr) {
      lastApplyingEndAtRef.current = Date.now();
    }
    prevApplyingRef.current = curr;
  }, [isApplyingDesign]);




  // Global click listener to hide alignment toolbar when clicking left panel or top toolbar
  useEffect(() => {
    const handleWindowMouseDown = (evt: MouseEvent) => {
      // If nothing is selected, we have nothing to deselect
      if (!selectedArtworkId && selectedItemIds.length === 0) return;

      const target = evt.target as HTMLElement;
      if (!target) return;

      // 1. If we clicked the Konva Stage, ignore it here (Stage listeners handle it)
      if (containerRef.current?.contains(target)) {
        return;
      }

      // 2. If we clicked inside the AlignmentToolbar, do NOT deselect
      if (target.closest('#alignment-toolbar-container')) {
        return;
      }

      // 3. Special case: if we are clicking a modal or its overlay, ignore
      if (target.closest('.ant-modal') || target.closest('.ant-modal-mask')) {
        return;
      }

      // 4. Otherwise (LeftPanel, top Toolbar, elsewhere), deselect
      deselectAll();
    };

    window.addEventListener('mousedown', handleWindowMouseDown, true); // use capture phase
    return () => window.removeEventListener('mousedown', handleWindowMouseDown, true);
  }, [selectedArtworkId, selectedItemIds, deselectAll]);

  return (
    <Root ref={containerRef} style={{
      backgroundColor: bgColor,
      backgroundImage: bgUrl ? `url(${bgUrl})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat'
    }}>
      {areTextAssetsLoaded && (
        <>
          <div style={{ fontFamily: 'MLCModifiedRomanSKS', opacity: 0, position: 'absolute', pointerEvents: 'none', zIndex: -1 }}>.</div>
          <div style={{ fontFamily: 'MLCCondensedRomanSKS', opacity: 0, position: 'absolute', pointerEvents: 'none', zIndex: -1 }}>.</div>
          <div style={{ fontFamily: 'Vermarco', opacity: 0, position: 'absolute', pointerEvents: 'none', zIndex: -1 }}>.</div>
          <div style={{ fontFamily: 'BagindaScript', opacity: 0, position: 'absolute', pointerEvents: 'none', zIndex: -1 }}>.</div>
          <div style={{ fontFamily: 'PamegaScript', opacity: 0, position: 'absolute', pointerEvents: 'none', zIndex: -1 }}>.</div>
          <div style={{ fontFamily: 'MonotypeCorsiva', opacity: 0, position: 'absolute', pointerEvents: 'none', zIndex: -1 }}>.</div>
        </>
      )}

      {/* Alignment Toolbar - shows when a shape is selected (hidden for fixed position) */}
      {!hideDesignerUI && (selectedArtwork || selectedItemIds.length > 0) && !selectedArtwork?.properties?.isFixedPosition && (
        <AlignmentToolbar
          selectedElement={selectedArtwork}
          selectedItemIds={selectedItemIds}
          onAlign={handleAlignment}
          stageSize={stageSize}
          position={{ top: '50%', right: 20 }}
          orientation="vertical"
          onCopy={copyArtwork}
          onMirror={mirrorArtwork}
          onFlip={flipArtwork}
          onBringToFront={bringToFront}
          onDelete={deleteArtwork}
          textControls={(() => {
            if (selectedArtwork?.type !== 'text') return null;
            const p = (selectedArtwork as any).properties || {};
            return {
              fontFamily: String(p.fontFamily || ''),
              fontSize: pixelsPerInch > 0 ? Number(((p.fontSize || 48) / pixelsPerInch).toFixed(3)) : Number(p.fontSize || 48),
              lineSpacing: Math.max(1, p.lineHeight || 1.1),
              kerning: p.letterSpacing ?? 2,
              isVCut: !!p.vcut,
              isFrost: !!p.frost,
              isPolish: !!p.polish,
              isOutline: !!p.outline,
              isShape: !!p.shape,
              onChange: (next: any) => {
                updateDesign(prev => {
                  const idx = prev.elements.findIndex(e => e.id === selectedArtwork.id);
                  if (idx === -1) return prev;
                  const curr = prev.elements[idx];
                  let finalNext = { ...next };
                  // Convert fontSize (inches) back to pixels.
                  // Store as exact float — Math.round destroys precision at low DPI
                  // (e.g. Math.round(0.875 * 35) = 30 → 30/35 = 0.857, not 0.875).
                  if (typeof next.fontSize === 'number') {
                    finalNext.fontSize = pixelsPerInch > 0 ? next.fontSize * pixelsPerInch : next.fontSize;
                  }
                  // Map kerning to letterSpacing
                  if (next.kerning !== undefined) {
                    finalNext.letterSpacing = next.kerning;
                    delete finalNext.kerning;
                  }
                  // Map lineSpacing to lineHeight (floor at 1 so ascenders are never clipped)
                  if (next.lineSpacing !== undefined) {
                    finalNext.lineHeight = Math.max(1, next.lineSpacing);
                    delete finalNext.lineSpacing;
                  }

                  // CRITICAL: Mark as manually adjusted so syncEditorFromForm preserves these changes
                  const np = {
                    ...curr.properties,
                    ...finalNext,
                    isManualSize: true,
                    isManualFont: finalNext.fontFamily ? true : curr.properties?.isManualFont
                  };

                  const newEls = prev.elements.slice();
                  newEls[idx] = { ...curr, properties: np };
                  return { ...prev, elements: newEls };
                });
              },
              onToggleVCut: () => updateDesign(prev => {
                const idx = prev.elements.findIndex(e => e.id === selectedArtwork.id);
                if (idx === -1) return prev;
                const curr = prev.elements[idx];
                const np = { ...curr.properties, vcut: !curr.properties?.vcut };
                const newEls = prev.elements.map((el, i) => i === idx ? { ...curr, properties: np } : el);
                return { ...prev, elements: newEls };
              }),
              onToggleFrost: () => updateDesign(prev => {
                const idx = prev.elements.findIndex(e => e.id === selectedArtwork.id);
                if (idx === -1) return prev;
                const curr = prev.elements[idx];
                const np = { ...curr.properties, frost: !curr.properties?.frost };
                const newEls = prev.elements.map((el, i) => i === idx ? { ...curr, properties: np } : el);
                return { ...prev, elements: newEls };
              }),
              onTogglePolish: () => updateDesign(prev => {
                const idx = prev.elements.findIndex(e => e.id === selectedArtwork.id);
                if (idx === -1) return prev;
                const curr = prev.elements[idx];
                const np = { ...curr.properties, polish: !curr.properties?.polish };
                const newEls = prev.elements.map((el, i) => i === idx ? { ...curr, properties: np } : el);
                return { ...prev, elements: newEls };
              }),
              onToggleOutline: () => updateDesign(prev => {
                const idx = prev.elements.findIndex(e => e.id === selectedArtwork.id);
                if (idx === -1) return prev;
                const curr = prev.elements[idx];
                const np = { ...curr.properties, outline: !curr.properties?.outline };
                const newEls = prev.elements.map((el, i) => i === idx ? { ...curr, properties: np } : el);
                return { ...prev, elements: newEls };
              }),
              onToggleShape: () => updateDesign(prev => {
                const idx = prev.elements.findIndex(e => e.id === selectedArtwork.id);
                if (idx === -1) return prev;
                const curr = prev.elements[idx];
                const np = { ...curr.properties, shape: !curr.properties?.shape };
                const newEls = prev.elements.map((el, i) => i === idx ? { ...curr, properties: np } : el);
                return { ...prev, elements: newEls };
              })
            };
          })()}
          canMirrorFlip={(() => {
            try {
              const role = String((selectedArtwork as any)?.properties?.role || (selectedArtwork as any)?.properties?.kind || '').toLowerCase();
              return role !== 'vase';
            } catch { return true; }
          })()}
          isVase={(() => {
            try {
              const role = String((selectedArtwork as any)?.properties?.role || (selectedArtwork as any)?.properties?.kind || '').toLowerCase();
              return role === 'vase';
            } catch { return false; }
          })()}
          canCopyVase={(() => {
            try {
              if (!selectedArtwork) return false;
              const role = String((selectedArtwork as any)?.properties?.role || (selectedArtwork as any)?.properties?.kind || '').toLowerCase();
              if (role !== 'vase') return true;
              // If vase configuration is right-only, disable copy
              try {
                if (isSerptopRightOnly) return false;
              } catch { }

              // For vases, check if there's already a vase on the opposite side
              const currentSide = String((selectedArtwork as any)?.properties?.side || 'left').toLowerCase();
              const oppositeSide = currentSide === 'left' ? 'right' : 'left';

              const existingVases = currentDesign.elements.filter(el =>
                el.type === 'artwork' && el.properties?.role === 'vase'
              );

              const hasOppositeVase = existingVases.some(vase =>
                String((vase.properties as any)?.side || 'left').toLowerCase() === oppositeSide
              );

              return !hasOppositeVase;
            } catch { return true; }
          })()}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = (e.target.files && e.target.files[0]) || null;
          const targetId = pendingPhotoIdRef.current;
          if (!file || !targetId) return;

          const reader = new FileReader();
          reader.onload = async () => {
            const dataUrl = reader.result as string;
            try {
              const img = new window.Image();
              img.crossOrigin = 'anonymous';
              await new Promise<void>((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = () => reject(new Error('Image load failed'));
                img.src = dataUrl;
              });

              const targetRatio = 0.75; // width / height (3:4 portrait)
              const origW = img.naturalWidth || img.width;
              const origH = img.naturalHeight || img.height;

              let sx = 0, sy = 0, sw = origW, sh = origH;
              const origRatio = origW / origH;
              if (origRatio > targetRatio) {
                sw = Math.floor(origH * targetRatio);
                sx = Math.floor((origW - sw) / 2);
              } else if (origRatio < targetRatio) {
                sh = Math.floor(origW / targetRatio);
                sy = Math.floor((origH - sh) / 2);
              }

              const cappedW = Math.min(sw, 1024);
              const outW = Math.max(256, Math.floor(cappedW));
              const outH = Math.max(Math.floor(outW / targetRatio), 256);

              const canvas = document.createElement('canvas');
              canvas.width = outW;
              canvas.height = outH;
              const ctx = canvas.getContext('2d');
              if (!ctx) throw new Error('Failed to get canvas context');
              ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
              const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.85);

              updateDesign(prev => {
                const next = prev.elements.map(el => {
                  if (el.id !== targetId) return el;
                  return {
                    ...el,
                    properties: {
                      ...el.properties,
                      src: croppedDataUrl,
                      placeholder: false
                    }
                  } as any;
                });
                return { ...prev, elements: next };
              });
            } catch (err) {
              try {
                const url = URL.createObjectURL(file);
                updateDesign(prev => {
                  const next = prev.elements.map(el => {
                    if (el.id !== targetId) return el;
                    return {
                      ...el,
                      properties: {
                        ...el.properties,
                        src: url,
                        placeholder: false
                      }
                    } as any;
                  });
                  return { ...prev, elements: next };
                });
              } catch (e) {
              }
            }
            pendingPhotoIdRef.current = null;
          };
          reader.readAsDataURL(file);
        }}
      />
      <div style={{
        width: viewportStyle.width,
        height: viewportStyle.height,
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      }}>
        <Stage
          ref={stageRef}
          width={Math.floor(computeStageWidth() * (scale || 1))}
          height={Math.floor(computeStageHeight() * (scale || 1))}
          scaleX={scale}
          scaleY={scale}
          style={{
            opacity: 1,
            pointerEvents: 'auto' as const,
            transition: 'opacity 120ms ease'
          }}
          key="main-stage"
          onClick={handleStageClick}
          shadowEnabled={false}
          shadowBlur={0}
          shadowColor="transparent"
          shadowOffsetX={0}
          shadowOffsetY={0}
          shadowOpacity={0}
          onMouseMove={(e: any) => {
            // Avoid any React state updates during transforms to prevent flicker
            if (isTransformingRef.current) {
              return;
            }
            const stage = e.target.getStage();
            if (!stage) return;
            const pos = stage.getPointerPosition();
            if (pos) {
              const inv = 1 / (scale || 1);
              const ix = pos.x * inv;
              const iy = pos.y * inv;
              mousePosRef.current = { x: Math.max(0, Math.min(computeStageWidth(), ix)), y: Math.max(0, Math.min(computeStageHeight(), iy)) };
              if (areaSelectActive && areaRect.active) {
                const startX = areaRect.x; const startY = areaRect.y;
                setAreaRect(prev => ({ ...prev, w: ix - startX, h: iy - startY }));
              }
            }
            if (showRuler) drawRulers();
          }}
          onMouseDown={(e) => {
            if (e.evt.button === 2) {
              hideContextMenu();
            }
            if (areaSelectActive) {
              const stage = e.target.getStage();
              if (!stage) return;
              const pos = stage.getPointerPosition();
              if (!pos) return;
              const inv = 1 / (scale || 1);
              setAreaRect({ active: true, x: pos.x * inv, y: pos.y * inv, w: 0, h: 0 });
              return;
            }
            handleStageMouseDown(e);
          }}
          onMouseUp={(e) => {
            if (areaRect.active) {
              const rect = areaRect;
              const areaLeft = Math.min(rect.x, rect.x + rect.w);
              const areaRight = Math.max(rect.x, rect.x + rect.w);
              const areaTop = Math.min(rect.y, rect.y + rect.h);
              const areaBottom = Math.max(rect.y, rect.y + rect.h);

              // Find all elements that intersect with the selection area
              const selectedIds: string[] = [];
              currentDesign.elements.forEach(el => {
                // Skip template elements
                const role = String(el.properties?.role || el.properties?.kind || '').toLowerCase();
                if (role === 'template' || el.properties?.isTemplate === true) return;

                const elLeft = el.x;
                const elRight = el.x + el.width;
                const elTop = el.y;
                const elBottom = el.y + el.height;

                // Check if element intersects with selection area
                if (elLeft < areaRight && elRight > areaLeft && elTop < areaBottom && elBottom > areaTop) {
                  selectedIds.push(el.id);
                }
              });

              // Update selected items
              if (selectedIds.length > 0) {
                setSelectedItemIds(selectedIds);
                setSelectedArtworkId(selectedIds[0]); // Set first item as primary selection
              } else {
                setSelectedItemIds([]);
                setSelectedArtworkId(null);
              }

              // Create persistent area selection instead of clearing
              setPersistentAreaSelection({
                active: true,
                x: areaLeft,
                y: areaTop,
                w: areaRight - areaLeft,
                h: areaBottom - areaTop
              });

              // Keep areaSelectActive true for persistent selection
              setAreaRect({ active: false, x: 0, y: 0, w: 0, h: 0 });
            }
          }}
        >
          {/* Background drawn via CSS on Root. Keep Konva background transparent. */}
          {/* Granite composite/base layer: allow composite even when template exists */}
          {(compositeProps || (designMode === 'ESQ' && graniteImageProps) || (!hasTemplateOrShape && graniteImageProps)) && (
            <Layer listening={false} imageSmoothingEnabled={true} shadowEnabled={false}>
              {compositeProps ? (
                <>
                  {Boolean(compositeProps.preview) && (
                    <Image
                      image={(compositeProps.preview as any).image}
                      x={compositeProps.preview.x}
                      y={compositeProps.preview.y}
                      width={compositeProps.preview.width}
                      height={compositeProps.preview.height}
                      listening={false}
                      perfectDrawEnabled={false}
                      shadowEnabled={false}
                    />
                  )}
                  <Image
                    image={compositeProps.base.image}
                    x={compositeProps.base.x}
                    y={compositeProps.base.y}
                    width={compositeProps.base.width}
                    height={compositeProps.base.height}
                    listening={false}
                    perfectDrawEnabled={false}
                    shadowEnabled={false}
                  />
                </>
              ) : (
                <Image
                  image={graniteImageProps!.image}
                  x={graniteImageProps!.x}
                  y={graniteImageProps!.y}
                  width={graniteImageProps!.width}
                  height={graniteImageProps!.height}
                  listening={false}
                  perfectDrawEnabled={false}
                  shadowEnabled={false}
                />
              )}
            </Layer>
          )}

          {/* Render template/model layer as a fallback when composite images aren't ready */}
          {(() => {
            const compositeReady = Boolean(
              compositeProps &&
              (compositeProps as any).preview && (compositeProps as any).preview.image &&
              (compositeProps as any).base && (compositeProps as any).base.image
            );
            return (hasTemplate && !compositeReady) ? (
              <TemplateLayerMemo key={`tpl-layer-${templateRefreshKey}`} elements={templateElements} />
            ) : null;
          })()}

          {/* Memoized active safe-zone points for rendering, clipping and clamping */}
          {(() => {
            // We'll define a variable here to use in the following layers, but since this is inside the render block,
            // we'll actually use a useMemo above for proper reactivity. 
            // I will add the useMemo after the state definitions.
            return null;
          })()}

          {/* Main design layer: artworks, vases, text and selection UI */}
          <Layer
            ref={(node) => {
              staticLayerRef.current = node;
              vaseLayerRef.current = node;
              textLayerRef.current = node;
              activeLayerRef.current = node;
            }}
            imageSmoothingEnabled={true}
            listening={true}
            shadowEnabled={false}
          >
            {/* 1. Non-selected artwork elements (clipped) */}
            <Group
              clipFunc={(ctx) => {
                if (activeSafeZonePoints && (activeSafeZonePoints as any).length >= 6) {
                  const pts = activeSafeZonePoints as number[];
                  ctx.beginPath();
                  ctx.moveTo(pts[0], pts[1]);
                  for (let i = 2; i < pts.length; i += 2) {
                    ctx.lineTo(pts[i], pts[i + 1]);
                  }
                  ctx.closePath();
                } else if (layoutGuides) {
                  ctx.rect(
                    layoutGuides.left,
                    layoutGuides.top,
                    layoutGuides.right - layoutGuides.left,
                    layoutGuides.bottom - layoutGuides.top
                  );
                }
              }}
            >
              {nonSelectedArtworkElements}
            </Group>

            {/* 2. Vases (rendered middle) */}
            {vaseElements}

            {/* 3. Text elements */}
            <Group
              clipFunc={designMode === 'ESQ' ? undefined : (ctx) => {
                if (activeSafeZonePoints && (activeSafeZonePoints as any).length >= 6) {
                  const pts = activeSafeZonePoints as number[];
                  ctx.beginPath();
                  ctx.moveTo(pts[0], pts[1]);
                  for (let i = 2; i < pts.length; i += 2) {
                    ctx.lineTo(pts[i], pts[i + 1]);
                  }
                  ctx.closePath();
                } else if (layoutGuides) {
                  ctx.rect(
                    layoutGuides.left,
                    layoutGuides.top,
                    layoutGuides.right - layoutGuides.left,
                    layoutGuides.bottom - layoutGuides.top
                  );
                }
              }}
            >
              {(designMode === 'ESQ' || showPersonalTextOnCanvas) ? textElements : null}
            </Group>

            {/* 4. Porcelain elements (rendered on top of text, clipped) */}
            <Group
              clipFunc={(ctx) => {
                if (activeSafeZonePoints && (activeSafeZonePoints as any).length >= 6) {
                  const pts = activeSafeZonePoints as number[];
                  ctx.beginPath();
                  ctx.moveTo(pts[0], pts[1]);
                  for (let i = 2; i < pts.length; i += 2) {
                    ctx.lineTo(pts[i], pts[i + 1]);
                  }
                  ctx.closePath();
                } else if (layoutGuides) {
                  ctx.rect(
                    layoutGuides.left,
                    layoutGuides.top,
                    layoutGuides.right - layoutGuides.left,
                    layoutGuides.bottom - layoutGuides.top
                  );
                }
              }}
            >
              {porcelainElements}
            </Group>

            {/* 5. Unified active layer for selection UI, transformers */}
            {!hideDesignerUI && (selectedArtwork || selectedItemIds.length > 0) && (() => {
              const selId = selectedArtworkId || (selectedItemIds.length > 0 ? selectedItemIds[0] : null);
              const el = currentDesign.elements.find(e => e.id === selId);
              if (!el) return null;

              const role = String(el.properties?.role || el.properties?.kind || '').toLowerCase();
              const isVase = role === 'vase';
              const isText = el.type === 'text';

              // Text nodes can be hidden while browsing other modals.
              // If a text node is hidden, don't render a transformer for it.
              if (isText && !(designMode === 'ESQ' || showPersonalTextOnCanvas)) return null;

              // Case 1: Custom Selection Box for Vases - usually handled differently or not at all here
              if (isVase) return null;

              // Hide transformer for fixed position elements (ESQ mode)
              if (el.properties?.isFixedPosition) return null;

              // Case 2: Standard Transformer (Text and Other Artwork)
              return (
                <Transformer
                  ref={(node) => {
                    if (trRef) (trRef as any).current = node;
                    if (node && el) {
                      const stage = node.getStage();
                      if (stage) {
                        const selectedNode = stage.findOne('#' + el.id);
                        if (selectedNode) {
                          const alreadyAttached =
                            node.nodes().length === 1 && node.nodes()[0] === selectedNode;
                          if (!alreadyAttached) {
                            node.nodes([selectedNode]);
                            node.getLayer()?.batchDraw();
                          }
                        } else {
                          if (node.nodes().length > 0) node.nodes([]);
                        }
                      }
                    }
                  }}
                  keepRatio={true}
                  borderStroke={isText ? "#5D722C" : "#5d722c"}
                  anchorStroke="white"
                  anchorFill={isText ? "#5D722C" : "#5d722c"}
                  anchorSize={10}
                  anchorCornerRadius={5}
                  anchorStrokeWidth={1.5}
                  borderStrokeWidth={2}
                  borderDash={undefined}
                  rotateEnabled={!isText && (!el.properties?.isFixedPosition || isAdminEmail) && !el.properties?.isInnerArtwork && !el.properties?.rotationLocked && el.properties?.slotType !== 'special' && el.properties?.slotType !== 'porcelain' && el.properties?.role !== 'porcelain'}
                  resizeEnabled={(!el.properties?.isFixedPosition || isAdminEmail) && !el.properties?.isInnerArtwork && el.properties?.slotType !== 'special' && el.properties?.slotType !== 'porcelain' && el.properties?.role !== 'porcelain'}
                  enabledAnchors={((el.properties?.isFixedPosition && !isAdminEmail) || el.properties?.isInnerArtwork || el.properties?.slotType === 'special' || el.properties?.slotType === 'porcelain' || el.properties?.role === 'porcelain')
                    ? []
                    : (isText
                      ? ['middle-left', 'middle-right']
                      : ['top-left', 'top-right', 'bottom-left', 'bottom-right']
                    )
                  }
                  boundBoxFunc={(oldBox, newBox) => {
                    if (newBox.width < 10 || newBox.height < 10) return oldBox;
                    return newBox;
                  }}
                  onTransform={(e) => {
                    const node = e.target;
                    const box = node.getClientRect();
                    const cx = box.x + box.width / 2;
                    const cy = box.y + box.height / 2;
                    const clamped = clampCenterToLayout(cx, cy, box.width, box.height, el.properties?.visibleBounds);
                    const isPushed = Math.abs(clamped.x - cx) > 1 || Math.abs(clamped.y - cy) > 1;
                    setSafeZoneHit(isPushed);
                  }}
                  onTransformEnd={() => setSafeZoneHit(false)}
                />
              );
            })()}
          </Layer>

          {/* Guides overlay: drawn last for visibility */}
          <Layer
            ref={guidesLayerRef}
            id="guides-layer"
            name="guides-layer"
            listening={false}
            shadowEnabled={false}
            shadowBlur={0}
            shadowColor="transparent"
            shadowOffsetX={0}
            shadowOffsetY={0}
            visible={true}
            shadowOpacity={0}
            key={`guides-layer-${graniteTemplate?.id || graniteTemplate?.x || 'no-template'}-${selectedGraniteMeta?.colorId || currentDesign.selectedColorId || 'no-color'}-${vasePositioningKey}`}
          >
            {/* Contract outline: follow curved shape when traced; transform to preview if needed; else rectangular inset fallback. */}
            {((graniteTemplate || (compositeProps && (compositeProps as any).preview)) && Number(pixelsPerInch) > 0) ? (
              (() => {
                const inset = 0 * pixelsPerInch;
                const pref = (compositeProps && (compositeProps as any).preview) ? (compositeProps as any).preview : null;

                // Use memoized active points for rendering to ensure consistency
                if (activeSafeZonePoints && activeSafeZonePoints.length >= 6) {
                  const pref = (compositeProps && (compositeProps as any).preview) ? (compositeProps as any).preview : null;
                  const isVisible = safeZoneHit;
                  if (!isVisible) return null;

                  return (
                    <Line
                      points={activeSafeZonePoints as number[]}
                      closed={true}
                      stroke={SAFE_ZONE_STROKE}
                      strokeWidth={2.5}
                      dash={pref ? [] : [10, 5]}
                      listening={false}
                      shadowEnabled={true}
                      shadowColor={SAFE_ZONE_GLOW}
                      shadowBlur={15}
                      shadowOpacity={0.6}
                      opacity={safeZoneHit ? 1 : 0}
                    />
                  );
                }

                // Else draw rectangular inset inside preview rect if present, else inside template rect
                const gx = pref ? Number(pref.x || 0) : Number((graniteTemplate?.x) || 0);
                const gy = pref ? Number(pref.y || 0) : Number((graniteTemplate?.y) || 0);
                const gw = pref ? Number(pref.width || 0) : Number((graniteTemplate?.width) || 0);
                const gh = pref ? Number(pref.height || 0) : Number((graniteTemplate?.height) || 0);
                const leftX = gx + inset;
                const topY = gy + inset;
                const width = Math.max(0, gw - inset * 2);
                const height = Math.max(0, gh - inset * 2);
                if (!(isFinite(leftX) && isFinite(topY) && isFinite(width) && isFinite(height))) return null;
                if (width <= 0 || height <= 0) return null;

                const isVisible = safeZoneHit;
                if (!isVisible) return null;

                return (
                  <Rect
                    x={leftX}
                    y={topY}
                    width={width}
                    height={height}
                    stroke={SAFE_ZONE_STROKE}
                    strokeWidth={2.5}
                    dash={pref ? [] : [10, 5]}
                    listening={false}
                    shadowEnabled={true}
                    shadowColor={SAFE_ZONE_GLOW}
                    shadowBlur={15}
                    shadowOpacity={0.6}
                    opacity={safeZoneHit ? 1 : 0}
                  />
                );
              })()
            ) : null}

            {/* Sample Guide Lines (User Toggle) */}
            {/* Sample Guide Lines (User Toggle) - Relative to Template if Present */}
            {/* Hidden when hideDesignerUI is true */}
            {!hideDesignerUI && sampleGuideLines.visible && (() => {
              const cp = compositeProps as any;
              const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0, width: DESIGN_WIDTH, height: DESIGN_HEIGHT });

              const ox = ref.x || 0;
              const oy = ref.y || 0;
              const w = ref.width || DESIGN_WIDTH;
              const h = ref.height || DESIGN_HEIGHT;

              const fallbackPpi = pixelsPerInch > 0 ? pixelsPerInch : 96;

              // Derive per-axis PPI directly from rendered pixel dimensions and DB stone size.
              // This guarantees H2 = physicalH lands EXACTLY on the stone bottom pixel edge,
              // and V2 = physicalW lands EXACTLY on the stone right pixel edge — regardless
              // of Math.floor rounding in compositeProps layout.
              const gcSizeGL = (graniteConfig as any)?.granite_details?.size as number[] | undefined;
              const physW = (Array.isArray(gcSizeGL) && gcSizeGL.length >= 1 && Number(gcSizeGL[0]) > 0) ? Number(gcSizeGL[0]) : 0;
              const physH = (Array.isArray(gcSizeGL) && gcSizeGL.length >= 2 && Number(gcSizeGL[1]) > 0) ? Number(gcSizeGL[1]) : 0;

              // ppiX: pixels per inch on the X axis (from rendered width)
              const ppiX = (physW > 0 && w > 0) ? w / physW : fallbackPpi;
              // ppiY: pixels per inch on the Y axis (from rendered height)
              const ppiY = (physH > 0 && h > 0) ? h / physH : fallbackPpi;

              // Absolute positions — use axis-specific PPI so lines land on pixel-exact edges
              const y1 = oy + sampleGuideLines.h1 * ppiY;
              const y2 = oy + sampleGuideLines.h2 * ppiY;
              const x1 = ox + sampleGuideLines.v1 * ppiX;
              const x2 = ox + sampleGuideLines.v2 * ppiX;

              return (
                <Group>
                  {/* Modern Green Selection Outline (Matching attached style) */}
                  <Rect
                    x={x1}
                    y={y1}
                    width={x2 - x1}
                    height={y2 - y1}
                    stroke="#5d722c"
                    strokeWidth={2}
                    fill="rgba(93, 114, 44, 0.05)"
                    listening={false}
                  />
                  {/* Left Grip Point */}
                  <Circle
                    x={x1}
                    y={y1 + (y2 - y1) / 2}
                    radius={5}
                    fill="#5d722c"
                    stroke="white"
                    strokeWidth={1.5}
                    listening={false}
                  />
                  {/* Right Grip Point */}
                  <Circle
                    x={x2}
                    y={y1 + (y2 - y1) / 2}
                    radius={5}
                    fill="#5d722c"
                    stroke="white"
                    strokeWidth={1.5}
                    listening={false}
                  />
                </Group>
              );
            })()}

            {/* Artwork Placement Slots */}
            {showPlacementOutlines && (() => {
              const cp = compositeProps as any;
              const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0, width: DESIGN_WIDTH, height: DESIGN_HEIGHT });
              const ox = ref.x || 0;
              const oy = ref.y || 0;
              const tw = ref.width || DESIGN_WIDTH;
              const th = ref.height || DESIGN_HEIGHT;
              const ppi = pixelsPerInch > 0 ? pixelsPerInch : 96;

              const templateSize = graniteConfig?.granite_details?.size as number[] | undefined;
              const templateInchesW = templateSize ? Number(templateSize[0]) : (tw / ppi);

              // Slots are now coming strictly from the database
              const displaySlots = [...placementSlots];

              const hasPorcelainPhoto = currentDesign.elements.some((el: any) =>
                el.properties?.role === 'porcelain' &&
                el.properties?.src &&
                !el.properties?.placeholder
              );

              // Only show outlines for the currently active placement (the artwork you clicked).
              // Special is a paired mode: show BOTH Special + Porcelain together.
              const activeSlotType = String(activePlacementSlot?.slot_type || '');
              const activeIsCorner = activeSlotType === 'corner_left' || activeSlotType === 'corner_right';
              const activeIsSpecialPair = activeSlotType === 'special' || activeSlotType === 'porcelain';

              // If nothing is active, don't show any outlines (prevents clutter).
              if (!activeSlotType) return null;

              // Filter out porcelain slot if photo already uploaded, unless the active mode is Special/Porcelain
              const allSlots = displaySlots.filter(s => {
                if (s.slot_type === 'porcelain') return activeIsSpecialPair || !hasPorcelainPhoto;
                return true;
              });

              // Pick the best-matching slot for each type to avoid "wrong outline"
              // when multiple DB rows exist for the same slot_type.
              const activePlacementCode = String(
                (activePlacementSlot as any)?.placement_code ??
                (activePlacementSlot as any)?.placementCode ??
                ''
              );

              const pickSlot = (slotType: string): any | null => {
                const candidates = allSlots.filter((s: any) => String(s?.slot_type || '') === slotType);
                if (candidates.length === 0) return null;
                // Prefer matching placement_code when available
                if (activePlacementCode) {
                  const exact = candidates.find((s: any) => String(s?.placement_code || '') === activePlacementCode);
                  if (exact) return exact;
                }
                return candidates[0];
              };

              const slotsToShow = (() => {
                if (activeIsCorner) {
                  return [pickSlot('corner_left'), pickSlot('corner_right')].filter(Boolean);
                }
                if (activeIsSpecialPair) {
                  return [pickSlot('special'), pickSlot('porcelain')].filter(Boolean);
                }
                return [pickSlot(activeSlotType)].filter(Boolean);
              })();

              return slotsToShow.map((slot, idx) => {
                const sx = ox + Number(slot.v1) * ppi;
                const sy = oy + Number(slot.h1) * ppi;
                const sw = (Number(slot.v2) - Number(slot.v1)) * ppi;
                const sh = (Number(slot.h2) - Number(slot.h1)) * ppi;

                if (!(isFinite(sx) && isFinite(sy) && isFinite(sw) && isFinite(sh))) return null;

                const isCorner = slot.slot_type === 'corner_left' || slot.slot_type === 'corner_right';
                const activeIsCorner = activePlacementSlot?.slot_type === 'corner_left' || activePlacementSlot?.slot_type === 'corner_right';

                // If it's a corner and a corner is active, treat BOTH as active for styling
                const isActive = (isCorner && activeIsCorner) || (activePlacementSlot?.slot_type === slot.slot_type);

                return (
                  <Group key={`slot-${slot.id}-${idx}`}>
                    <Rect
                      x={sx}
                      y={sy}
                      width={sw}
                      height={sh}
                      stroke={isActive ? "#5d722c" : "#94a3b8"}
                      strokeWidth={isActive ? 2.5 : 1.5}
                      dash={isActive ? [] : [6, 3]}
                      opacity={isActive ? 0.8 : 0.4}
                      fill={isActive ? "rgba(93, 114, 44, 0.08)" : "rgba(148, 163, 184, 0.05)"}
                      onClick={() => {
                        setActivePlacementSlot(slot);
                      }}
                      onTap={() => {
                        setActivePlacementSlot(slot);
                      }}
                      cursor="pointer"
                    />
                    <Text
                      x={sx}
                      y={sy - 15}
                      text={slot.slot_type ? slot.slot_type.replace('_', ' ').toUpperCase() : ''}
                      fontSize={11}
                      fill={isActive ? "#5d722c" : "#64748b"}
                      fontStyle="bold"
                      listening={false}
                      visible={Boolean(slot.slot_type)}
                    />
                  </Group>
                );
              });
            })()}

            {measureOverlays.map((m, idx) => (
              <Group key={`measure-${idx}`} listening={false}>
                <Line
                  points={[m.x1, m.y1, m.x2, m.y2]}
                  stroke="#f59e0b"
                  strokeWidth={2}
                  listening={false}
                  shadowEnabled={false}
                />
                {/* Midpoint label background */}
                <Rect
                  x={(m.x1 + m.x2) / 2 - 24}
                  y={(m.y1 + m.y2) / 2 - 10}
                  width={48}
                  height={20}
                  fill="rgba(0,0,0,0.6)"
                  cornerRadius={4}
                  listening={false}
                />
                <Text
                  x={(m.x1 + m.x2) / 2 - 22}
                  y={(m.y1 + m.y2) / 2 - 8}
                  text={m.label || ''}
                  fontSize={12}
                  fill="#ffffff"
                  listening={false}
                  visible={Boolean(m.label)}
                />
              </Group>
            ))}
          </Layer>
        </Stage>
      </div>

      {/* Area select overlay (Alt + drag) */}
      {areaRect.active && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(areaRect.x, areaRect.x + areaRect.w) * scale,
            top: Math.min(areaRect.y, areaRect.y + areaRect.h) * scale,
            width: Math.abs(areaRect.w) * scale,
            height: Math.abs(areaRect.h) * scale,
            border: '2px dashed rgba(93, 114, 44, 0.8)',
            background: 'rgba(93, 114, 44, 0.1)',
            pointerEvents: 'none',
            zIndex: 20
          }}
        />
      )}

      {/* Persistent area selection overlay (draggable + resizable) */}
      {persistentAreaSelection?.active && (() => {
        const sel = persistentAreaSelection;
        const handleDragMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
          e.preventDefault();
          e.stopPropagation();
          const startX = e.clientX;
          const startY = e.clientY;
          const startLeft = sel.x;
          const startTop = sel.y;
          const onMove = (ev: MouseEvent) => {
            const dx = (ev.clientX - startX) / (scale || 1);
            const dy = (ev.clientY - startY) / (scale || 1);
            let cx = startLeft + dx + sel.w / 2;
            let cy = startTop + dy + sel.h / 2;
            const clamped = clampCenterToLayout(cx, cy, sel.w, sel.h);
            const isPushed = Math.abs(clamped.x - cx) > 1 || Math.abs(clamped.y - cy) > 1;
            setSafeZoneHit(isPushed);
            // No clamping applied to position, just visual feedback
            setPersistentAreaSelection(p => p ? { ...p, x: startLeft + dx, y: startTop + dy } : p);
          };
          const onUp = () => {
            setSafeZoneHit(false);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
          };
          window.addEventListener('mousemove', onMove);
          window.addEventListener('mouseup', onUp);
        };
        const makeResize = (edge: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw') => (e: React.MouseEvent<HTMLDivElement>) => {
          e.preventDefault();
          e.stopPropagation();
          const startX = e.clientX;
          const startY = e.clientY;
          const start = { x: sel.x, y: sel.y, w: sel.w, h: sel.h };
          const onMove = (ev: MouseEvent) => {
            const dx = (ev.clientX - startX) / (scale || 1);
            const dy = (ev.clientY - startY) / (scale || 1);
            let nx = start.x, ny = start.y, nw = start.w, nh = start.h;
            if (edge.includes('e')) nw = Math.max(10, start.w + dx);
            if (edge.includes('s')) nh = Math.max(10, start.h + dy);
            if (edge.includes('w')) { nx = start.x + dx; nw = Math.max(10, start.w - dx); }
            if (edge.includes('n')) { ny = start.y + dy; nh = Math.max(10, start.h - dy); }
            // Snap the selection rect to layout edges and centerlines
            if (layoutGuides) {
              const lg = layoutGuides;
              const centers = [
                { val: nx, axis: 'x' },
                { val: nx + nw / 2, axis: 'x' },
                { val: nx + nw, axis: 'x' },
                { val: ny, axis: 'y' },
                { val: ny + nh / 2, axis: 'y' },
                { val: ny + nh, axis: 'y' },
              ];
              const vLines = [lg.left, lg.right];
              const hLines = [lg.top, lg.bottom];
              const tol = 0; // Disable snapping for free movement
              centers.forEach(c => {
                if (c.axis === 'x') {
                  for (const v of vLines) {
                    if (Math.abs(c.val - v) <= tol) {
                      const delta = v - c.val;
                      if (edge.includes('w')) nx += delta;
                      else if (edge.includes('e')) nw += delta;
                      else nx += delta; // move whole rect when dragging center via east/west-free edges
                      break;
                    }
                  }
                } else {
                  for (const h of hLines) {
                    if (Math.abs(c.val - h) <= tol) {
                      const delta = h - c.val;
                      if (edge.includes('n')) ny += delta;
                      else if (edge.includes('s')) nh += delta;
                      else ny += delta;
                      break;
                    }
                  }
                }
              });
            }
            // Clamp inside layout bounds
            const c = clampCenterToLayout(nx + nw / 2, ny + nh / 2, nw, nh);
            setPersistentAreaSelection(p => p ? { ...p, x: c.x - nw / 2, y: c.y - nh / 2, w: nw, h: nh } : p);
          };
          const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
          };
          window.addEventListener('mousemove', onMove);
          window.addEventListener('mouseup', onUp);
        };
        return (
          <div
            style={{
              position: 'absolute',
              left: sel.x * scale,
              top: sel.y * scale,
              width: sel.w * scale,
              height: sel.h * scale,
              border: '2px solid rgba(34, 197, 94, 0.9)',
              background: 'rgba(34, 197, 94, 0.1)',
              cursor: 'move',
              zIndex: 20
            }}
            onMouseDown={handleDragMouseDown}
          >
            {/* Resize handles */}
            {([
              { edge: 'nw', style: { left: -6, top: -6, cursor: 'nwse-resize' } },
              { edge: 'ne', style: { right: -6, top: -6, cursor: 'nesw-resize' } },
              { edge: 'sw', style: { left: -6, bottom: -6, cursor: 'nesw-resize' } },
              { edge: 'se', style: { right: -6, bottom: -6, cursor: 'nwse-resize' } },
              { edge: 'n', style: { left: '50%', top: -6, transform: 'translateX(-50%)', cursor: 'ns-resize' } },
              { edge: 's', style: { left: '50%', bottom: -6, transform: 'translateX(-50%)', cursor: 'ns-resize' } },
              { edge: 'w', style: { top: '50%', left: -6, transform: 'translateY(-50%)', cursor: 'ew-resize' } },
              { edge: 'e', style: { top: '50%', right: -6, transform: 'translateY(-50%)', cursor: 'ew-resize' } },
            ] as any[]).map((h, idx) => (
              <div
                key={idx}
                onMouseDown={makeResize(h.edge)}
                style={{ position: 'absolute', width: 12, height: 12, background: '#88847d', borderRadius: 2, ...h.style }}
              />
            ))}
          </div>
        );
      })()}

      {/* Custom canvas for granite image - DISABLED TO PREVENT SHADOW ISSUES */}
      {false && graniteImageProps && (
        <canvas
          ref={graniteCanvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: computeStageWidth(),
            height: computeStageHeight(),
            pointerEvents: 'none',
            zIndex: 1,
            filter: 'none !important',
            boxShadow: 'none !important',
            WebkitFilter: 'none !important',
            WebkitBoxShadow: 'none !important',
            imageRendering: 'pixelated',
            outline: 'none',
            border: 'none',
            appearance: 'none',
            WebkitAppearance: 'none'
          }}
        />
      )}

      {showRuler && (
        <React.Fragment>
          <canvas ref={rulerTopRef} style={{ position: 'absolute', left: 0, top: 0, height: 24, width: '100%', pointerEvents: 'none', zIndex: 10 }} />
          <canvas ref={rulerLeftRef} style={{ position: 'absolute', left: 0, top: 0, width: 24, height: '100%', pointerEvents: 'none', zIndex: 10 }} />
        </React.Fragment>
      )}

      {false && editingTextId && textEditor && textEditorPos && (
        <div
          ref={textEditorRef}
          style={{ position: 'fixed', left: (textEditorPos as any).left, top: (textEditorPos as any).top, zIndex: 4000, width: 380, background: '#ffffff', borderRadius: 10, boxShadow: '0 16px 40px rgba(2,6,23,0.25)', border: '1px solid #dbe2ea', overflow: 'hidden', userSelect: 'none' }}
        >
          <div
            style={{ fontWeight: 800, padding: '12px 14px', background: '#0f172a', color: '#ffffff', borderBottom: '1px solid #0b1220', cursor: 'move', letterSpacing: 0.2 }}
            onMouseDown={(e) => {
              const startLeft = (textEditorPos as any).left;
              const startTop = (textEditorPos as any).top;
              const startX = e.clientX;
              const startY = e.clientY;
              dragStateRef.current = { active: true, dx: startX - startLeft, dy: startY - startTop };
              const onMove = (ev: MouseEvent) => {
                if (!dragStateRef.current?.active) return;
                const left = Math.max(0, Math.min(window.innerWidth - 360, ev.clientX - (dragStateRef.current?.dx || 0)));
                const top = Math.max(0, Math.min(window.innerHeight - 60, ev.clientY - (dragStateRef.current?.dy || 0)));
                setTextEditorPos({ left, top });
              };
              const onUp = () => {
                dragStateRef.current = null;
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
              };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            }}
          >
            Text Style
          </div>
          <div style={{ padding: 16, background: '#f8fafc' }}>
            <div style={{ display: 'grid', gap: 12 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Font Family</span>
                <select
                  value={(textEditor as any).fontFamily || 'MLCCondensedRomanSKS'}
                  onChange={(e) => setTextEditor(te => te ? { ...te, fontFamily: e.target.value } : te)}
                  style={{ padding: '10px 12px', border: '2px solid #cbd5e1', borderRadius: 8, background: '#ffffff', color: '#0f172a' }}
                >
                  <option>MLCModifiedRomanSKS</option>
                  <option>MLCCondensedRomanSKS</option>
                  <option>Vermarco</option>
                  <option>BagindaScript</option>
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Size (inches)</span>
                <input type="number" step={0.125} min={0.125} value={(textEditor as any).fontSize}
                  onChange={(e) => setTextEditor(te => te ? { ...te, fontSize: Math.max(0.125, Number(e.target.value) || 0.125) } : te)}
                  style={{ padding: '10px 12px', border: '2px solid #cbd5e1', borderRadius: 8, background: '#ffffff', color: '#0f172a' }} />
              </label>

              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Character Spacing</span>
                <input type="number" step="0.01" min={-2} max={2} value={(textEditor as any).letterSpacing}
                  onChange={(e) => setTextEditor(te => te ? { ...te, letterSpacing: Math.max(-2, Math.min(2, Number(e.target.value) || 0)) } : te)}
                  style={{ padding: '10px 12px', border: '2px solid #cbd5e1', borderRadius: 8, background: '#ffffff', color: '#0f172a' }} />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <button
                onClick={() => {
                  if (!editingTextId) return;
                  if (window.confirm('Are you sure you want to delete this text?')) {
                    deleteText(editingTextId);
                  }
                }}
                style={{
                  padding: '10px 14px',
                  border: '1px solid #dc2626',
                  borderRadius: 8,
                  background: '#ffffff',
                  color: '#dc2626',
                  fontWeight: 600,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.borderColor = '#b91c1c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = '#dc2626';
                }}
              >
                ðŸ—‘ï¸ Delete
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setEditingTextId(null); setTextEditor(null); setTextEditorPos(null); }}
                  style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#ffffff', color: '#0f172a' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!editingTextId || !textEditor) return;
                    updateDesign(prev => {
                      const next = prev.elements.map(el => {
                        if (el.id !== editingTextId) return el;
                        return {
                          ...el,
                          properties: {
                            ...el.properties,
                            fontFamily: textEditor.fontFamily,
                            fontSize: textEditor.fontSize,
                            lineHeight: textEditor.lineHeight,
                            letterSpacing: textEditor.letterSpacing,
                            isManualFont: true,
                            isManualSize: true
                          }
                        } as any;
                      });
                      return { ...prev, elements: next };
                    });
                    setEditingTextId(null);
                    setTextEditor(null);
                    setTextEditorPos(null);
                  }}
                  style={{ padding: '10px 16px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#ffffff', fontWeight: 700 }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      {
        false && selectedArtwork && (
          <React.Fragment>
            {(() => {
              // Try to compute the artwork's on-screen bounding box using Konva so we handle transforms/rotation
              const sa = selectedArtwork as any;
              if (!sa) return null;
              let artLeft = sa.x;
              let artTop = sa.y;
              let artWidth = sa.width;
              let artHeight = sa.height;
              try {
                const stage = stageRef.current?.getStage?.();
                const node = stage?.findOne ? stage.findOne(`#${sa.id}`) : null;
                if (node && typeof node.getClientRect === 'function') {
                  const r = node.getClientRect();
                  if (r && typeof r.x === 'number') {
                    artLeft = r.x;
                    artTop = r.y;
                    artWidth = r.width;
                    artHeight = r.height;
                  }
                }
              } catch (err) { }

              const deleteSize = 30;
              const gearSize = 32;
              const deleteLeft = artLeft + artWidth - deleteSize / 2;
              const deleteTop = artTop - deleteSize / 2;
              const gearLeft = 0;
              const gearTop = 0;

              return (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      left: deleteLeft,
                      top: deleteTop,
                      width: `${deleteSize}px`,
                      height: `${deleteSize}px`,
                      backgroundColor: '#ff4444',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      border: '2px solid white'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Batch the update to prevent flickering
                      requestAnimationFrame(() => {
                        updateDesign(prev => ({
                          ...prev,
                          elements: prev.elements.filter(element => element.id !== sa.id)
                        }));
                        setSelectedArtworkId(null);
                      });
                    }}
                    title="Remove"
                  >
                    <span style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>Ã—</span>
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      left: gearLeft,
                      top: gearTop,
                      width: `${gearSize}px`,
                      height: `${gearSize}px`,
                      backgroundColor: '#2c3e50',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      zIndex: 1000,
                      boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
                      border: '3px solid #ffffff',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setContextMenu({
                        visible: true,
                        x: gearLeft,
                        y: gearTop + gearSize + 8,
                        artworkId: sa.id
                      });
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#34495e';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#2c3e50';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title=""
                  >
                    <span style={{ display: 'none' }}>x</span>
                  </div>
                </>
              );
            })()}
          </React.Fragment>
        )
      }

      {
        contextMenu.visible && (() => {
          const found = currentDesign.elements.find(el => el && el.id === contextMenu.artworkId);
          const props = found ? (found as any).properties : null;
          const role = props ? String(props.role || props.kind || '').toLowerCase() : '';
          const isVase = role === 'vase';
          const slotType: string = props?.slotType || props?.placement || '';
          const hasSlot = Boolean(slotType);

          const itemStyle: React.CSSProperties = {
            padding: '10px 16px', cursor: 'pointer', display: 'flex',
            alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 500,
            color: '#1e293b', whiteSpace: 'nowrap' as const, transition: 'background 0.15s'
          };
          const maxLeft = (containerRef.current?.offsetWidth || 800) - 185;
          const maxTop = (containerRef.current?.offsetHeight || 600) - 200;

          return (
            <div
              style={{
                position: 'absolute',
                left: Math.min(contextMenu.x, maxLeft),
                top: Math.min(contextMenu.y, maxTop),
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
                zIndex: 1001,
                minWidth: 175,
                padding: '4px 0',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Replace - only for slotted artworks */}
              {!isVase && hasSlot && (
                <div style={itemStyle} onClick={replaceArtwork}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  Replace Artwork
                </div>
              )}
              {!isVase && role !== 'porcelain' && (
                <>
                  <div style={itemStyle} onClick={mirrorArtwork}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    Mirror
                  </div>
                  <div style={itemStyle} onClick={flipArtwork}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    Flip
                  </div>
                </>
              )}
              <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
              <div style={{ ...itemStyle, color: '#dc2626' }}
                onClick={() => { deleteArtwork(); hideContextMenu(); }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                Delete
              </div>
            </div>
          );
        })()
      }

      {
        textContextMenu.visible && (
          <div
            style={{
              position: 'absolute',
              left: textContextMenu.x,
              top: textContextMenu.y,
              backgroundColor: '#ffffff',
              border: '2px solid #2c3e50',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              zIndex: 1001,
              minWidth: '160px',
              padding: '6px 0',
              backdropFilter: 'blur(10px)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#2c3e50',
                transition: 'all 0.2s ease'
              }}
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this text?')) {
                  deleteText(textContextMenu.textId);
                }
                hideTextContextMenu();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fef2f2';
                e.currentTarget.style.color = '#dc2626';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#2c3e50';
              }}
            >
              ðŸ—‘ï¸ Delete Text
            </div>
          </div>
        )
      }

      {
        showSafeZoneWarning && (
          <div style={{ position: 'absolute', left: 5, top: 5, zIndex: 1100 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
                position: 'relative',
                overflow: 'hidden',
                maxWidth: 420
              }}
            >
              {/* Subtle indigo accent bar */}
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: '#4f46e5' }} />
              {/* Message */}
              <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>
                Please keep your design within the safe zone.
              </div>
              {/* Close */}
              <button
                onClick={() => setSafeWarnVisible(false)}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(15,23,42,0.04)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                style={{
                  marginLeft: 6,
                  border: '1px solid #e5e7eb',
                  background: 'transparent',
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  fontSize: 16,
                  lineHeight: 1,
                  color: '#334155',
                  cursor: 'pointer'
                }}
                aria-label="Close warning"
                title="Close"
              >
                x
              </button>
            </div>
          </div>
        )
      }

      {/* Inline Text Editor Overlay */}
      {
        editingTextId && textEditorPos && textEditor && (() => {
          const editingElement = currentDesign.elements.find(el => el.id === editingTextId);
          if (!editingElement) return null;

          const isAdditionalText = String(editingTextId || '').toLowerCase().startsWith('additionaltext');
          const baseEditingId = (editingTextId || '').replace(/-back$/, '');
          const fieldNameLc = String((editingElement as any)?.properties?.fieldName || '').toLowerCase();
          // Name fields are always uppercase; only verse / additionalText allow mixed case.
          // ESQ fixed fields have arbitrary IDs, so use fieldName when present.
          const isWritingField =
            baseEditingId === 'verse' ||
            isAdditionalText ||
            (designMode === 'ESQ' && (fieldNameLc === 'verse' || fieldNameLc.startsWith('verse') || fieldNameLc.includes('verse')));
          const isNameWithCasingField =
            baseEditingId === 'lastName' ||
            baseEditingId === 'fullName' ||
            fieldNameLc === 'lastname' ||
            fieldNameLc === 'fullname';
          const shouldAllowMixedCase = isWritingField || isNameWithCasingField;

          return (
            <InlineTextEditor
              element={editingElement}
              position={textEditorPos}
              styleDef={textEditor}
              scale={scale || 1}
              graniteColorId={selectedGraniteMeta?.colorId}
              sharedColors={sharedColors}
              shouldAllowMixedCase={shouldAllowMixedCase}
              isNameWithCasingField={isNameWithCasingField}
              onSave={(newContent) => {
                updateDesign(prev => {
                  const idx = prev.elements.findIndex(x => x.id === editingTextId);
                  if (idx === -1) return prev;
                  const curr = prev.elements[idx];
                  const finalContent = isNameWithCasingField
                    ? adjustLastNameCasing(newContent)
                    : (shouldAllowMixedCase ? newContent : newContent.toUpperCase());
                  const updatedElement = { ...curr, properties: { ...curr.properties, content: finalContent } } as any;

                  // After inline edit, force fixed ESQ text back into its own slot bounds.
                  if (updatedElement?.type === 'text' && updatedElement?.properties?.isFixedPosition) {
                    const bounds = updatedElement.properties?.esqBounds;
                    const ppi = Number(pixelsPerInch) || 0;
                    const cp = compositeProps as any;
                    const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { x: 0, y: 0 });
                    if (bounds && ppi && graniteTemplate && ref) {
                      const { left, right, insetX } = computeEsqBoundsPixelFrame({
                        bounds,
                        compositeProps,
                        graniteTemplate,
                        graniteConfig: graniteConfig || {},
                        pixelsPerInch: ppi,
                      });
                      if (Number.isFinite(left) && Number.isFinite(right) && right > left) {
                        const laneWidth = Math.max(1, (right - left) - insetX * 2);
                        updatedElement.width = Math.min(Math.max(1, Number(updatedElement.width || 1)), laneWidth);
                        updatedElement.boxWidth = updatedElement.width;
                      }
                    }
                    const fixed = clampFixedEsqElementToOwnBounds(updatedElement);
                    // Avoid subpixel churn that snaps the canvas after inline edit
                    if (Math.abs(fixed.x - Number(updatedElement.x || 0)) > 0.25) {
                      updatedElement.x = fixed.x;
                    }
                    if (Math.abs(fixed.y - Number(updatedElement.y || 0)) > 0.25) {
                      updatedElement.y = fixed.y;
                    }
                  }

                  const newElements = prev.elements.map((el2, i) => (i === idx ? updatedElement : el2));
                  return { ...prev, elements: newElements };
                });
              }}
              onClose={() => {
                setEditingTextId(null);
                setTextEditor(null);
                setTextEditorPos(null);
              }}
            />
          );
        })()
      }
      {esqDatePicker && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(15,23,42,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5200,
            padding: '20px'
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '320px',
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
              padding: '12px',
              display: 'grid',
              gap: '12px'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#f8fafc',
                color: '#0f172a',
                fontWeight: 600
              }}
            >
              {formatEsqDateForDisplay(esqDatePicker.value) || 'MM DD, YYYY'}
            </div>
            <div style={{ userSelect: 'none' }}>
              <PremiumDatePicker
                month={esqDatePickerParts.month}
                day={esqDatePickerParts.day}
                year={esqDatePickerParts.year}
                onChange={(month, day, year) => {
                  const monthIdx = MONTHS.indexOf(String(month || '').toUpperCase());
                  const dayNum = Number(day);
                  const yearNum = Number(year);
                  if (monthIdx < 0 || !Number.isFinite(dayNum) || !Number.isFinite(yearNum)) return;
                  const mm = String(monthIdx + 1).padStart(2, '0');
                  const dd = String(dayNum).padStart(2, '0');
                  const yyyy = String(yearNum);
                  setEsqDatePicker(prev => (prev ? { ...prev, value: `${yyyy}-${mm}-${dd}` } : prev));
                }}
                maxDate={new Date()}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
              <button
                onClick={() => {
                  const todayValue = getTodayDateInputValue();
                  applyEsqDateToElement(esqDatePicker.elementId, todayValue);
                  setEsqDatePicker(null);
                }}
                style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#f8fafc', color: '#334155', fontWeight: 600, cursor: 'pointer' }}
              >
                Today
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setEsqDatePicker(null)}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#ffffff', color: '#334155', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    applyEsqDateToElement(esqDatePicker.elementId, esqDatePicker.value);
                    setEsqDatePicker(null);
                  }}
                  style={{ padding: '8px 14px', border: 'none', borderRadius: '8px', background: '#5D722C', color: '#ffffff', fontWeight: 700, cursor: 'pointer' }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Sample Guide Guidelines Controls */}
      {showGuidelineSettings && (
        <div style={{ position: 'absolute', top: 15, right: 15, zIndex: 3500, background: 'white', padding: '12px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid #e2e8f0', minWidth: 220 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 600, color: '#334155', cursor: 'pointer', marginBottom: 4 }}>
            <input
              type="checkbox"
              checked={sampleGuideLines.visible}
              onChange={(e) => setSampleGuideLines(prev => ({ ...prev, visible: e.target.checked }))}
              style={{ accentColor: '#4f46e5', cursor: 'pointer' }}
            />
            Sample Guide Lines
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 600, color: '#334155', cursor: 'pointer', marginBottom: 4 }}>
            <input
              type="checkbox"
              checked={showPlacementOutlines}
              onChange={(e) => setShowPlacementOutlines(e.target.checked)}
              style={{ accentColor: '#4f46e5', cursor: 'pointer' }}
            />
            Show Placement Outlines
          </label>
          {sampleGuideLines.visible && (() => {
            // Dynamic ranges based on template (converted to inches)
            const cp = compositeProps as any;
            // Fallback to graniteTemplate if compositeProps not globally available or scoped
            const ref = (cp && cp.preview) ? cp.preview : (graniteTemplate || { width: DESIGN_WIDTH, height: DESIGN_HEIGHT });

            const maxWidth = ref.width || DESIGN_WIDTH;
            const maxHeight = ref.height || DESIGN_HEIGHT;

            const ppi = pixelsPerInch > 0 ? pixelsPerInch : 96;

            // Use exact stone dimensions (inches) when available so the slider max is
            // always a clean whole-inch value (e.g. exactly 24" not 23.625").
            // Pixel-derived values drift because compositeProps uses Math.floor layout.
            //
            // ESQ does not always have graniteConfig; in that case, fall back to parsing
            // the modal name (e.g. "24x12") which we store on the template element.
            const gcSize = (graniteConfig as any)?.granite_details?.size as number[] | undefined;
            let logicalWIn = (Array.isArray(gcSize) && gcSize.length >= 1 && Number(gcSize[0]) > 0) ? Number(gcSize[0]) : 0;
            let logicalHIn = (Array.isArray(gcSize) && gcSize.length >= 2 && Number(gcSize[1]) > 0) ? Number(gcSize[1]) : 0;
            if (!(logicalWIn > 0 && logicalHIn > 0) && designMode === 'ESQ') {
              try {
                const tpl = (currentDesign.elements || []).find((el: any) => String(el?.properties?.role || el?.properties?.kind || '').toLowerCase() === 'template');
                const modalName = String(tpl?.properties?.esqModalName || '');
                const match = modalName.match(/(\d+)\s*[xX]\s*(\d+)/);
                if (match) {
                  logicalWIn = Number(match[1]) || 0;
                  logicalHIn = Number(match[2]) || 0;
                }
              } catch { }
            }
            const maxHInches = (logicalHIn > 0) ? logicalHIn : (maxHeight / ppi);
            const maxWInches = (logicalWIn > 0) ? logicalWIn : (maxWidth / ppi);

            // Step size for buttons (1/8th inch)
            const step = 0.125;

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* H1 */}
                <div style={{ display: 'grid', gridTemplateColumns: '24px 24px 1fr 24px 34px', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444' }}>H1</span>
                  <button
                    onClick={() => setSampleGuideLines(prev => ({ ...prev, h1: Math.max(0, prev.h1 - step) }))}
                    style={{ width: 24, height: 24, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1, color: '#475569' }}
                  >-</button>
                  <input type="range" min={0} max={maxHInches} step={step} value={sampleGuideLines.h1}
                    onChange={(e) => setSampleGuideLines(prev => ({ ...prev, h1: Number(e.target.value) }))}
                    style={{ width: '100%', accentColor: '#ef4444', cursor: 'pointer', height: 4 }} />
                  <button
                    onClick={() => setSampleGuideLines(prev => ({ ...prev, h1: Math.min(maxHInches, prev.h1 + step) }))}
                    style={{ width: 24, height: 24, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1, color: '#475569' }}
                  >+</button>
                  <span style={{ fontSize: 10, color: '#64748b', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{sampleGuideLines.h1.toFixed(3)}</span>
                </div>
                {/* H2 */}
                <div style={{ display: 'grid', gridTemplateColumns: '24px 24px 1fr 24px 34px', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444' }}>H2</span>
                  <button
                    onClick={() => setSampleGuideLines(prev => ({ ...prev, h2: Math.max(0, prev.h2 - step) }))}
                    style={{ width: 24, height: 24, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1, color: '#475569' }}
                  >-</button>
                  <input type="range" min={0} max={maxHInches} step={step} value={sampleGuideLines.h2}
                    onChange={(e) => setSampleGuideLines(prev => ({ ...prev, h2: Number(e.target.value) }))}
                    style={{ width: '100%', accentColor: '#ef4444', cursor: 'pointer', height: 4 }} />
                  <button
                    onClick={() => setSampleGuideLines(prev => ({ ...prev, h2: Math.min(maxHInches, prev.h2 + step) }))}
                    style={{ width: 24, height: 24, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1, color: '#475569' }}
                  >+</button>
                  <span style={{ fontSize: 10, color: '#64748b', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{sampleGuideLines.h2.toFixed(3)}</span>
                </div>
                <div style={{ height: 1, background: '#f1f5f9', margin: '2px 0' }} />
                {/* V1 */}
                <div style={{ display: 'grid', gridTemplateColumns: '24px 24px 1fr 24px 34px', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6' }}>V1</span>
                  <button
                    onClick={() => setSampleGuideLines(prev => ({ ...prev, v1: Math.max(0, prev.v1 - step) }))}
                    style={{ width: 24, height: 24, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1, color: '#475569' }}
                  >-</button>
                  <input type="range" min={0} max={maxWInches} step={step} value={sampleGuideLines.v1}
                    onChange={(e) => setSampleGuideLines(prev => ({ ...prev, v1: Number(e.target.value) }))}
                    style={{ width: '100%', accentColor: '#3b82f6', cursor: 'pointer', height: 4 }} />
                  <button
                    onClick={() => setSampleGuideLines(prev => ({ ...prev, v1: Math.min(maxWInches, prev.v1 + step) }))}
                    style={{ width: 24, height: 24, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1, color: '#475569' }}
                  >+</button>
                  <span style={{ fontSize: 10, color: '#64748b', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{sampleGuideLines.v1.toFixed(3)}</span>
                </div>
                {/* V2 */}
                <div style={{ display: 'grid', gridTemplateColumns: '24px 24px 1fr 24px 34px', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6' }}>V2</span>
                  <button
                    onClick={() => setSampleGuideLines(prev => ({ ...prev, v2: Math.max(0, prev.v2 - step) }))}
                    style={{ width: 24, height: 24, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1, color: '#475569' }}
                  >-</button>
                  <input type="range" min={0} max={maxWInches} step={step} value={sampleGuideLines.v2}
                    onChange={(e) => setSampleGuideLines(prev => ({ ...prev, v2: Number(e.target.value) }))}
                    style={{ width: '100%', accentColor: '#3b82f6', cursor: 'pointer', height: 4 }} />
                  <button
                    onClick={() => setSampleGuideLines(prev => ({ ...prev, v2: Math.min(maxWInches, prev.v2 + step) }))}
                    style={{ width: 24, height: 24, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4, cursor: 'pointer', fontSize: 14, lineHeight: 1, color: '#475569' }}
                  >+</button>
                  <span style={{ fontSize: 10, color: '#64748b', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{sampleGuideLines.v2.toFixed(3)}</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Admin Position Upload Button */}
      {isAdminEmail && designMode !== 'ESQ' && (
        <button
          onClick={() => {
            if (!selectedArtworkId) {
              setSelectedArtCode('');
              setSelectedPlacementCode('');
              setIsEnteringNewPlacement(false);
              setSelectedPosType('');
              setSelectedInnerArtTitle('');
            }
            setShowPositionUploadModal(true);
          }}
          style={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            zIndex: 4000,
            padding: '10px 20px',
            background: '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>Update Position</span>
        </button>
      )}

      {/* Admin ESQ Position Update Button */}
      {isAdminEmail && designMode === 'ESQ' && (
        <button
          onClick={() => {
            const activeFixedElement = currentDesign.elements.find(
              el => el.properties?.isFixedPosition && !!el.properties?.fieldName
            );
            if (activeFixedElement?.properties?.fieldName) {
              setSelectedEsqField(String(activeFixedElement.properties.fieldName));
            } else {
              setSelectedEsqField('FirstName');
            }
            setShowEsqPositionModal(true);
          }}
          style={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            zIndex: 4000,
            padding: '10px 20px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>Update ESQ Position</span>
        </button>
      )}

      {/* Admin Position Upload Modal */}
      {showPositionUploadModal && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 5000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Update Placement Slot</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Position Type</label>
              <select
                id="pos-type"
                value={selectedPosType}
                onChange={(e) => setSelectedPosType(e.target.value)}
                style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              >
                <option value="" disabled>Select Position</option>
                <option value="left">Left</option>
                <option value="top">Top</option>
                <option value="corner">Corner</option>
                <option value="special">Special</option>
                <option value="porcelain">Porcelain</option>
                <option value="inner_artwork">Inner Artwork</option>
              </select>
            </div>

            {selectedPosType === 'corner' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Corner Side</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="corner-side"
                      value="corner_left"
                      checked={selectedCornerSide === 'corner_left'}
                      onChange={(e) => setSelectedCornerSide(e.target.value)}
                    /> Left
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="corner-side"
                      value="corner_right"
                      checked={selectedCornerSide === 'corner_right'}
                      onChange={(e) => setSelectedCornerSide(e.target.value)}
                    /> Right
                  </label>
                </div>
              </div>
            )}

            {(selectedPosType === 'special' || selectedPosType === 'porcelain' || selectedPosType === 'inner_artwork') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Art Code</label>
                  <select
                    id="art-code"
                    value={selectedArtCode}
                    onChange={(e) => handleArtCodeChange(e.target.value)}
                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', width: '100%' }}
                  >
                    <option value="">Select Art Code</option>
                    {artCodeOptions.map(code => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                </div>
                {selectedPosType === 'inner_artwork' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Inner Art Title</label>
                    <select
                      value={selectedInnerArtTitle}
                      onChange={(e) => setSelectedInnerArtTitle(e.target.value)}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', width: '100%' }}
                    >
                      <option value="">Select Inner Art</option>
                      {innerArtTitleOptions.map(title => (
                        <option key={title} value={title}>{title}</option>
                      ))}
                    </select>
                  </div>
                )}
                {selectedPosType !== 'inner_artwork' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Placement Code</label>
                    {!isEnteringNewPlacement ? (
                      <select
                        id="placement-code-select"
                        value={selectedPlacementCode}
                        onChange={(e) => {
                          if (e.target.value === 'NEW') {
                            setIsEnteringNewPlacement(true);
                            setSelectedPlacementCode('');
                          } else {
                            handlePlacementCodeChange(e.target.value);
                          }
                        }}
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', width: '100%' }}
                      >
                        <option value="">Select Placement Code</option>
                        {placementCodeOptions.map(code => (
                          <option key={code} value={code}>{code}</option>
                        ))}
                        <option value="NEW" style={{ fontWeight: 'bold' }}>+ Enter New Code</option>
                      </select>
                    ) : (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input
                          id="placement-code-input"
                          type="text"
                          value={selectedPlacementCode}
                          onChange={(e) => setSelectedPlacementCode(e.target.value)}
                          placeholder="Enter New Placement Code"
                          style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', flex: 1 }}
                          autoFocus
                        />
                        <button
                          onClick={() => setIsEnteringNewPlacement(false)}
                          style={{ padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}
                        >
                          Pick
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ fontSize: '12px', color: '#6b7280', background: '#f9fafb', padding: '10px', borderRadius: '6px', border: '1px solid #f3f4f6' }}>
              <strong>Guidelines:</strong> H1:{sampleGuideLines.h1.toFixed(3)}, H2:{sampleGuideLines.h2.toFixed(3)}, V1:{sampleGuideLines.v1.toFixed(3)}, V2:{sampleGuideLines.v2.toFixed(3)}
              <br />
              <strong>Selected:</strong> {selectedElement ? 'Yes' : 'No'}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button
                onClick={() => setShowPositionUploadModal(false)}
                style={{ flex: 1, padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                disabled={positionUploadBusy}
                onClick={async () => {
                  let finalType = selectedPosType;
                  if (finalType === 'corner') {
                    finalType = selectedCornerSide;
                  }

                  if (!finalType || finalType === "") {
                    alert('Please select a position type');
                    return;
                  }

                  const isSpecial = finalType === 'special' || finalType === 'porcelain' || finalType === 'inner_artwork' || finalType.startsWith('special') || finalType.startsWith('porcelain');
                  const artCode = selectedArtCode;
                  const placementCode = selectedPlacementCode;

                  if (isSpecial && !artCode) {
                    alert('Art code is required');
                    return;
                  }

                  if (isSpecial && finalType !== 'inner_artwork' && !placementCode) {
                    alert('Placement code is required for Special/Porcelain');
                    return;
                  }

                  const modelId = currentDesign.selectedModelId;
                  const sizeId = currentDesign.selectedSizeId;

                  if (!modelId || !sizeId) {
                    alert('Model ID and Size ID are missing');
                    return;
                  }

                  // Find the selected element for coordinates if any, otherwise use default or guides
                  const selEl = currentDesign.elements.find(el => el.id === selectedElement);

                  const data = {
                    type: finalType,
                    model_id: modelId,
                    size_id: sizeId,
                    art_code: artCode,
                    placement_code: placementCode,
                    x: selEl?.x ?? 0,
                    y: selEl?.y ?? 0,
                    width: selEl?.width ?? 0,
                    height: selEl?.height ?? 0,
                    v1: sampleGuideLines.v1,
                    v2: sampleGuideLines.v2,
                    h1: sampleGuideLines.h1,
                    h2: sampleGuideLines.h2,
                    inner_art_title: selectedInnerArtTitle,
                  };

                  setPositionUploadBusy(true);
                  try {
                    const res = await fetch(buildApiUrl('/api/designs/update-position'), {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...authHeaders()
                      },
                      body: JSON.stringify(data)
                    });
                    const result = await res.json();
                    if (res.ok && result.ok) {
                      alert('Position updated successfully');
                      setShowPositionUploadModal(false);
                      // Update local placement list if we entered a new code
                      if (!placementCodeOptions.includes(placementCode)) {
                        setPlacementCodeOptions(prev => [...prev, placementCode].sort());
                      }
                    } else {
                      alert('Error: ' + (result.error || 'Failed to update position'));
                    }
                  } catch (e) {
                    alert('Request failed');
                  } finally {
                    setPositionUploadBusy(false);
                  }
                }}
                style={{ flex: 1, padding: '10px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', opacity: positionUploadBusy ? 0.7 : 1 }}
              >
                {positionUploadBusy ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Admin ESQ Position Upload Modal */}
      {showEsqPositionModal && (() => {
        const esqFieldOptions = ['FirstName', 'LastName', 'DOB', 'DOD', 'Verse', 'Verse2', 'Verse3', 'Artwork', 'Separator'];
        return (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Update ESQ Fixed Positions</h3>

              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                Select one field to save. Modal and color are auto-filled from the selected ESQ design.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Fixed Field</label>
                <select
                  value={selectedEsqField}
                  onChange={(e) => setSelectedEsqField(e.target.value)}
                  style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  {esqFieldOptions.map((field) => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
              </div>

              {/* Artwork image upload — only shown when Artwork field is selected */}
              {/* {selectedEsqField === 'Artwork' && (() => {
              const designTemplateEl = currentDesign.elements.find(el => el.properties?.role === 'template_design');
              const tplId = designTemplateEl?.properties?.templateId;
              return tplId ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#065f46' }}>Upload Artwork Image for this Template</label>
                  <input
                    type="file"
                    accept="image/*"
                    id="esq-artwork-upload"
                    style={{ fontSize: '13px' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('artwork_image', file);
                      try {
                        const res = await fetch(buildApiUrl(`/api/esq/templates/${tplId}/artwork`), {
                          method: 'POST',
                          body: formData
                        });
                        const result = await res.json();
                        if (res.ok && result.artwork_image_path) {
                          alert('Artwork image uploaded! It will appear when this template is selected next time.');
                        } else {
                          alert('Upload failed: ' + (result.error || 'Unknown error'));
                        }
                      } catch (err) {
                        alert('Upload error. Check console.');
                        console.error(err);
                      }
                    }}
                  />
                  <p style={{ margin: 0, fontSize: '11px', color: '#6b7280' }}>This will set the artwork image shown in the Artwork position slot for this template.</p>
                </div>
              ) : <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>Select a template first to upload artwork.</p>;
            })()} */}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                <button
                  onClick={() => setShowEsqPositionModal(false)}
                  style={{ padding: '8px 16px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const templateEl = currentDesign.elements.find(el => el.properties?.role === 'template');
                      const designTemplateEl = currentDesign.elements.find(el => el.properties?.role === 'template_design');
                      const selectedFieldEl = currentDesign.elements.find(
                        el => el.properties?.isFixedPosition && el.properties?.fieldName === selectedEsqField
                      );
                      const modalId = templateEl?.properties?.esqModalId || designTemplateEl?.properties?.modalId || selectedFieldEl?.properties?.modalId;
                      const tplId = selectedFieldEl?.properties?.templateId || designTemplateEl?.properties?.templateId;
                      const colorId = selectedGraniteMeta?.colorId || designTemplateEl?.properties?.colorId || selectedFieldEl?.properties?.colorId;

                      if (!modalId || !colorId || !tplId) {
                        alert('Cannot find modal/template/color context. Ensure you selected a modal and template first.');
                        return;
                      }

                      const fields: any = {};
                      currentDesign.elements.forEach(el => {
                        if (el.properties?.isFixedPosition) {
                          const fieldName = el.properties?.fieldName;
                          if (fieldName && fieldName === selectedEsqField) {
                            fields[fieldName] = {
                              x: el.x,
                              y: el.y,
                              width: el.width,
                              height: el.height,
                              font_size: el.properties?.fontSize || 30,
                              alignment: el.properties?.align || 'center'
                            };
                          }
                        }
                      });

                      const res = await fetch(buildApiUrl('/api/esq/update-positions'), {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          ...authHeaders()
                        },
                        body: JSON.stringify({
                          modal_id: modalId,
                          template_id: tplId,
                          color_id: colorId,
                          fields,
                          h1: sampleGuideLines.h1,
                          h2: sampleGuideLines.h2,
                          v1: sampleGuideLines.v1,
                          v2: sampleGuideLines.v2
                        })
                      });

                      if (res.ok) {
                        alert('ESQ positions updated successfully');
                        setShowEsqPositionModal(false);
                      } else {
                        alert('Failed to update ESQ positions');
                      }
                    } catch (e) {
                      console.error('Update failed:', e);
                      alert('Update error');
                    }
                  }}
                  style={{ padding: '8px 24px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Save Fixed Positions
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </Root >
  );
};

// Extracted component to manage inline text editing and hooks properly
const InlineTextEditor: React.FC<{
  element: DesignElement;
  position: { left: number; top: number };
  styleDef: {
    fontFamily: string;
    fontSize: number;
    lineHeight: number;
    letterSpacing: number;
    slot?: { widthPx: number; heightPx: number };
  };
  scale: number;
  graniteColorId?: number | string;
  sharedColors: any[];
  shouldAllowMixedCase?: boolean;
  isNameWithCasingField?: boolean;
  onSave: (val: string) => void;
  onClose: () => void;
}> = ({ element, position, styleDef, scale, graniteColorId, sharedColors, shouldAllowMixedCase, isNameWithCasingField, onSave, onClose }) => {
  const currentContent = element.properties?.content || '';
  const alignment = element.properties?.align || 'center';
  const boxWidth = (element as any).boxWidth || element.width || 800;
  const slot = styleDef.slot;
  const characterLimit = Number((element as any)?.properties?.characterLimit || 0) || undefined;

  const formatTextContent = useCallback((val: string): string => {
    if (isNameWithCasingField) return adjustLastNameCasing(val);
    return shouldAllowMixedCase ? val : val.toUpperCase();
  }, [isNameWithCasingField, shouldAllowMixedCase]);

  const displayColor = React.useMemo(() => {
    const props = element.properties || {};
    if (props.frost) return '#e5e7eb';
    if (props.polish) return '#ffffff';

    const rawColor = props.color || '#000000';
    const activeColor = String(rawColor).toUpperCase();

    const activeColorObj = sharedColors.find((c: any) => Number(c.id) === Number(graniteColorId));
    const isPJB = activeColorObj?.code === 'PJB' || String(activeColorObj?.name).toUpperCase().includes('BLACK');
    const isGrey = activeColorObj?.code === 'MG' || activeColorObj?.code === 'Grey' || String(activeColorObj?.name).toUpperCase().includes('GRAY') || String(activeColorObj?.name).toUpperCase().includes('GREY');

    // Black granite (PJB) -> Flip black text to white
    if (isPJB && (activeColor === '#000000' || activeColor === '#000')) {
      return '#FFFFFF';
    }

    // Grey granite (MG) -> Flip white text to black
    if (isGrey && (activeColor === '#FFFFFF' || activeColor === '#FFF')) {
      return '#000000';
    }

    return rawColor;
  }, [element.properties, graniteColorId, sharedColors]);

  const textRef = useRef(currentContent);
  const isSavingRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const slotEditorFontPx = slot
    ? Math.max(1, Math.min(styleDef.fontSize * 0.95, slot.heightPx * 0.78))
    : styleDef.fontSize;
  const slotEditorLineHeightPx = slot
    ? Math.max(1, slot.heightPx - 2)
    : undefined;

  // Sync ref with content
  textRef.current = currentContent;

  // Initial focus and sizing (mount only — slot geometry is fixed for this edit session)
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      // ESQ slot: fixed box must match Konva — never replace height with scrollHeight (causes snap)
      if (!styleDef.slot) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
      el.focus();
      el.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on open
  }, []);

  useEffect(() => {
    return () => {
      // Ensure we save on unmount (clicking outside)
      if (!isSavingRef.current && textRef.current !== element.properties?.content) {
        onSave(formatTextContent(textRef.current));
      }
    };
  }, [element.properties?.content, formatTextContent, onSave]);

  return (
    <textarea
      ref={textareaRef}
      defaultValue={formatTextContent(currentContent)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onBlur={(e) => {
        isSavingRef.current = true;
        const raw = (slot && !shouldAllowMixedCase) ? String(e.target.value || '').replace(/\r?\n+/g, ' ') : String(e.target.value || '');
        const newContent = clampTextByCharacterLimit(raw, characterLimit);
        const formatted = formatTextContent(newContent);
        if (formatted !== currentContent) {
          onSave(formatted);
        }
        onClose();
      }}
      onInput={(e) => {
        const rawVal = (slot && !shouldAllowMixedCase)
          ? String(e.currentTarget.value || '').replace(/\r?\n+/g, ' ')
          : String(e.currentTarget.value || '');
        const val = clampTextByCharacterLimit(rawVal, characterLimit);
        const formatted = formatTextContent(val);
        textRef.current = formatted;
        onSave(formatted);
        if (!styleDef.slot) {
          const target = e.currentTarget;
          target.style.height = 'auto';
          target.style.height = `${target.scrollHeight}px`;
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          isSavingRef.current = true; // Prevent unmount save on cancel
          onClose();
        } else if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      style={{
        position: 'fixed',
        left: position.left,
        top: position.top,
        width: slot ? `${slot.widthPx}px` : `${boxWidth * (scale || 1)}px`,
        height: slot ? `${slot.heightPx}px` : undefined,
        fontFamily: styleDef.fontFamily ? `"${styleDef.fontFamily}", "Monotype Corsiva", "Pamega Script", Vermarco, cursive, sans-serif` : 'inherit',
        // Fit editor text to slot height with a tiny safety margin (prevents top/bottom clipping).
        fontSize: `${slotEditorFontPx}px`,
        lineHeight: slot ? `${slotEditorLineHeightPx}px` : styleDef.lineHeight,
        letterSpacing: `${styleDef.letterSpacing}px`,
        textAlign: alignment as any,
        textTransform: shouldAllowMixedCase ? 'none' : 'uppercase',
        padding: slot ? 0 : '0.2em 0',
        margin: '0',
        transform: slot ? 'translateY(-0.5px)' : 'translateY(-0.2em)',
        border: 'none',
        background: 'transparent',
        outline: 'none',
        resize: 'none',
        overflowX: 'hidden',
        overflowY: 'hidden',
        whiteSpace: slot ? 'nowrap' : 'pre-wrap',
        wordWrap: slot ? 'normal' : 'break-word',
        color: displayColor,
        zIndex: 10000,
        pointerEvents: 'auto',
        minHeight: slot ? `${slot.heightPx}px` : '1em',
        boxSizing: 'border-box',
        cursor: 'text',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
      maxLength={characterLimit}
    />
  );
};

export default Designer;